import React, { useState, useEffect, useMemo } from 'react';
import { collection, addDoc, onSnapshot, query, where, orderBy, doc, deleteDoc, setDoc, updateDoc, getDocs } from 'firebase/firestore';
import { db, storage, auth } from '../firebaseConfig';
import { sendPasswordResetEmail, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useExerciseLibrary } from '../hooks/useExerciseLibrary';
import { useTranslation, LanguageSwitch } from '../context/LanguageContext';
import { validatePassword } from '../utils/validation';
import WorkoutList from './WorkoutList';
import PdfPlanImporter from './PdfImporter';
import { MediaLightboxModal } from './MediaLightboxModal';
import { ExerciseFormBuilder } from './ExerciseFormBuilder';
import { InteractiveWorkoutBuilder } from './InteractiveWorkoutBuilder';
import { InteractivePlanBuilder } from './InteractivePlanBuilder';
import { sendNewPlanAssignedNotification, sendPasswordResetNotification } from '../utils/emailService';

/* ==========================================
   HELPER: HIGH-VISIBILITY COLOR-CODED MUSCLE BADGES
   ========================================== */
/* ==========================================
   HELPER: HIGH-VISIBILITY COLOR-CODED MUSCLE BADGES
   ========================================== */
const getMuscleBadge = (bodyPart, t, lang = 'pl') => {
  const clean = (bodyPart || 'General').toLowerCase().trim();
  let styles = "bg-slate-800 text-slate-300 border-slate-700";
  let label = bodyPart;

  const isPL = lang === 'pl' || (t && t('common.polish', 'Polski') === 'Polski');

  if (clean.includes('chest') || clean.includes('klatka')) {
    styles = "bg-rose-500/20 text-rose-300 border-rose-500/50 shadow-[0_0_10px_rgba(244,63,94,0.2)]";
    label = isPL ? 'Klatka piersiowa' : 'Chest';
  } else if (clean.includes('back') || clean.includes('plecy') || clean.includes('grzbiet')) {
    styles = "bg-sky-500/20 text-sky-300 border-sky-500/50 shadow-[0_0_10px_rgba(14,165,233,0.2)]";
    label = isPL ? 'Plecy i Grzbiet' : 'Back';
  } else if (clean.includes('shoulder') || clean.includes('barki') || clean.includes('naramien')) {
    styles = "bg-indigo-500/20 text-indigo-300 border-indigo-500/50 shadow-[0_0_10px_rgba(99,102,241,0.2)]";
    label = isPL ? 'Barki' : 'Shoulders';
  } else if (clean.includes('upper legs') || clean.includes('uda') || clean.includes('pośladk')) {
    styles = "bg-emerald-500/20 text-emerald-300 border-emerald-500/50 shadow-[0_0_10px_rgba(16,185,129,0.2)]";
    label = isPL ? 'Uda i Pośladki' : 'Upper Legs / Glutes';
  } else if (clean.includes('lower legs') || clean.includes('łydk')) {
    styles = "bg-emerald-500/20 text-emerald-300 border-emerald-500/50";
    label = isPL ? 'Łydki' : 'Lower Legs';
  } else if (clean === 'legs' || clean.includes('nogi')) {
    styles = "bg-emerald-500/20 text-emerald-300 border-emerald-500/50 shadow-[0_0_10px_rgba(16,185,129,0.2)]";
    label = isPL ? 'Nogi' : 'Legs';
  } else if (clean.includes('waist') || clean.includes('core') || clean.includes('brzuch') || clean.includes('pas')) {
    styles = "bg-teal-500/20 text-teal-300 border-teal-500/50 shadow-[0_0_10px_rgba(20,184,166,0.2)]";
    label = isPL ? 'Brzuch / Pas' : 'Waist / Core';
  } else if (clean.includes('upper arms') || clean.includes('ramiona')) {
    styles = "bg-amber-500/20 text-amber-300 border-amber-500/50";
    label = isPL ? 'Ramiona' : 'Upper Arms';
  } else if (clean.includes('lower arms') || clean.includes('przedramiona')) {
    styles = "bg-amber-500/20 text-amber-300 border-amber-500/50";
    label = isPL ? 'Przedramiona' : 'Lower Arms';
  } else if (clean.includes('biceps')) {
    styles = "bg-amber-500/20 text-amber-300 border-amber-500/50 shadow-[0_0_10px_rgba(245,158,11,0.2)]";
    label = 'Biceps';
  } else if (clean.includes('triceps')) {
    styles = "bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/50 shadow-[0_0_10px_rgba(217,70,239,0.2)]";
    label = 'Triceps';
  } else if (clean.includes('cardio') || clean.includes('kardio')) {
    styles = "bg-rose-500/20 text-rose-300 border-rose-500/50";
    label = isPL ? 'Kardio' : 'Cardio';
  } else if (clean.includes('neck') || clean.includes('szyja')) {
    styles = "bg-slate-700 text-slate-200 border-slate-600";
    label = isPL ? 'Szyja' : 'Neck';
  }

  return (
    <span className={`px-3 py-1 rounded-lg border text-[11px] font-extrabold tracking-wider uppercase inline-flex items-center justify-center shrink-0 ${styles}`}>
      {label}
    </span>
  );
};

/* ==========================================
   HELPER: DYNAMIC TERM TRANSLATOR (EN -> PL)
   ========================================== */
const translateTerm = (term, lang = 'pl') => {
  if (!term || typeof term !== 'string') return term || '-';
  if (lang !== 'pl') return term;

  const text = term.trim();
  const lower = text.toLowerCase();

  if (text.includes(',')) {
    return text.split(',').map(s => translateTerm(s, lang)).join(', ');
  }

  const dictionary = {
    'upper legs': 'Uda i Pośladki',
    'upper_legs': 'Uda i Pośladki',
    'lower legs': 'Łydki',
    'lower_legs': 'Łydki',
    'upper arms': 'Ramiona',
    'upper_arms': 'Ramiona',
    'lower arms': 'Przedramiona',
    'lower_arms': 'Przedramiona',
    'chest': 'Klatka piersiowa',
    'back': 'Plecy i Grzbiet',
    'shoulders': 'Barki',
    'waist': 'Brzuch / Pas',
    'waist / core': 'Brzuch / Pas',
    'cardio': 'Kardio',
    'neck': 'Szyja',
    'legs': 'Nogi',
    'body weight': 'Ciężar ciała',
    'bodyweight': 'Ciężar ciała',
    'dumbbell': 'Hantle',
    'dumbbells': 'Hantle',
    'barbell': 'Sztanga',
    'cable': 'Wyciąg',
    'leverage machine': 'Maszyna',
    'machine': 'Maszyna',
    'band': 'Guma oporowa',
    'smith machine': 'Suwnica Smitha',
    'kettlebell': 'Kettlebell',
    'kettlebells': 'Kettlebell',
    'weighted': 'Z dociążeniem',
    'stability ball': 'Piłka gimnastyczna',
    'ez barbell': 'Sztanga łamana',
    'ez_barbell': 'Sztanga łamana',
    'other': 'Inny',
    'quads': 'Czworogłowy uda',
    'quadriceps': 'Czworogłowy uda',
    'hamstrings': 'Kulszowo-goleniowe',
    'glutes': 'Pośladki',
    'gluteus maximus': 'Pośladkowy większy',
    'pectoralis major': 'Piersiowy większy',
    'latissimus dorsi': 'Najszerszy grzbietu',
    'deltoids': 'Naramienne (barki)',
    'deltoid': 'Naramienny',
    'biceps': 'Biceps (dwugłowy ramienia)',
    'biceps brachii': 'Biceps (dwugłowy ramienia)',
    'triceps': 'Triceps (trójgłowy ramienia)',
    'triceps brachii': 'Triceps (trójgłowy ramienia)',
    'abs': 'Prosty brzucha',
    'rectus abdominis': 'Prosty brzucha',
    'core': 'Mięśnie głębokie (core)',
    'calves': 'Brzuchaty łydki',
    'gastrocnemius': 'Brzuchaty łydki',
    'trapezius': 'Czworoboczny (kaptury)',
    'rhomboids': 'Równoległoboczne',
    'erector spinae': 'Prostownik grzbietu',
    'lower back': 'Dolny grzbiet',
    'upper back': 'Górny grzbiet',
    'obliques': 'Skośne brzucha',
    'forearms': 'Przedramiona',
    'upper chest': 'Góra klatki piersiowej',
    'lower chest': 'Dół klatki piersiowej',
    'long head': 'Głowa długa',
    'short head': 'Głowa krótka',
    'anterior deltoid': 'Przednia część naramiennego',
    'rear deltoid': 'Tylna część naramiennego',
    'posterior deltoid': 'Tylna część naramiennego',
    'lateral deltoid': 'Boczna część naramiennego',
    'brachialis': 'Ramienny',
    'hip flexors': 'Zginacze bioder'
  };

  if (dictionary[lower]) return dictionary[lower];

  for (const [key, translated] of Object.entries(dictionary)) {
    if (lower.includes(key)) {
      return lower.replace(key, translated);
    }
  }

  return text;
};

/* ==========================================
   SUB-RENDER 1: LIVE TRACKER & ATTENDANCE CALENDAR
   ========================================== */
const CoachAttendanceCalendar = ({ traineeId, traineeName }) => {
  const { lang } = useTranslation();
  const [workouts, setWorkouts] = useState([]);
  const [calendarDate, setCalendarDate] = useState(() => new Date());
  const [selectedDayWorkouts, setSelectedDayWorkouts] = useState(null);

  useEffect(() => {
    if (!traineeId) return;
    let unsubscribe;
    try {
      const q = query(
        collection(db, 'workouts'),
        where('userId', '==', traineeId),
        orderBy('createdAt', 'desc')
      );
      unsubscribe = onSnapshot(q, (snapshot) => {
        const docs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setWorkouts(docs);
      }, (err) => console.warn("Coach calendar listener notice:", err.message));
    } catch (e) {}
    return () => { if (unsubscribe) unsubscribe(); };
  }, [traineeId]);

  const calendarYear = calendarDate.getFullYear();
  const calendarMonth = calendarDate.getMonth();

  const monthNamesPL = ["Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec", "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"];
  const monthNamesEN = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const currentMonthName = lang === 'pl' ? monthNamesPL[calendarMonth] : monthNamesEN[calendarMonth];

  const firstDayOfMonth = new Date(calendarYear, calendarMonth, 1);
  const lastDayOfMonth = new Date(calendarYear, calendarMonth + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();

  let startOffset = firstDayOfMonth.getDay() - 1;
  if (startOffset < 0) startOffset = 6;

  const todayStr = new Date().toISOString().split('T')[0];

  const workoutsByDateMap = useMemo(() => {
    const map = {};
    (workouts || []).forEach(w => {
      if (!w.date) return;
      if (!map[w.date]) map[w.date] = [];
      map[w.date].push(w);
    });
    return map;
  }, [workouts]);

  const handlePrevMonth = () => {
    setCalendarDate(new Date(calendarYear, calendarMonth - 1, 1));
    setSelectedDayWorkouts(null);
  };

  const handleNextMonth = () => {
    setCalendarDate(new Date(calendarYear, calendarMonth + 1, 1));
    setSelectedDayWorkouts(null);
  };

  const handleCurrentMonth = () => {
    setCalendarDate(new Date());
    setSelectedDayWorkouts(null);
  };

  return (
    <div className="bg-slate-900/80 border border-slate-800 rounded-3xl p-6 md:p-8 space-y-6 shadow-xl mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center justify-center text-xl shrink-0 shadow">
            <span className="material-symbols-outlined text-2xl">calendar_month</span>
          </div>
          <div>
            <h3 className="text-lg md:text-xl font-black text-white m-0 tracking-tight leading-tight">
              {lang === 'pl' ? `Kalendarz Aktywności: ${traineeName}` : `Activity Calendar: ${traineeName}`}
            </h3>
            <p className="text-xs text-slate-400 m-0 font-medium">
              {lang === 'pl' ? "Dni z zalogowanymi treningami zawodnika są wyróżnione na zielono." : "Days with logged workout sessions are highlighted in green."}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button 
            type="button"
            onClick={handlePrevMonth}
            className="w-9 h-9 bg-slate-950 hover:bg-slate-800 border border-slate-800 text-slate-300 rounded-xl transition flex items-center justify-center cursor-pointer shadow-sm active:scale-95"
            title={lang === 'pl' ? "Poprzedni miesiąc" : "Previous month"}
          >
            <span className="material-symbols-outlined text-base">chevron_left</span>
          </button>
          <span className="text-sm font-black text-slate-100 min-w-[130px] text-center px-2">
            {currentMonthName} {calendarYear}
          </span>
          <button 
            type="button"
            onClick={handleNextMonth}
            className="w-9 h-9 bg-slate-950 hover:bg-slate-800 border border-slate-800 text-slate-300 rounded-xl transition flex items-center justify-center cursor-pointer shadow-sm active:scale-95"
            title={lang === 'pl' ? "Następny miesiąc" : "Next month"}
          >
            <span className="material-symbols-outlined text-base">chevron_right</span>
          </button>
          <button 
            type="button"
            onClick={handleCurrentMonth}
            className="px-3.5 h-9 bg-indigo-500/15 hover:bg-indigo-500/30 text-indigo-300 hover:text-white border border-indigo-500/40 text-xs font-black rounded-xl transition flex items-center gap-1.5 cursor-pointer active:scale-95 shadow-sm ml-1"
            title={lang === 'pl' ? "Przejdź do obecnego miesiąca" : "Go to current month"}
          >
            <span className="material-symbols-outlined text-xs text-indigo-400">today</span>
            <span>{lang === 'pl' ? "Dziś" : "Today"}</span>
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <div className="grid grid-cols-7 text-center font-black text-xs text-slate-400 uppercase tracking-wider pb-2 border-b border-slate-800/60">
          <div>{lang === 'pl' ? "Pon" : "Mon"}</div>
          <div>{lang === 'pl' ? "Wt" : "Tue"}</div>
          <div>{lang === 'pl' ? "Śr" : "Wed"}</div>
          <div>{lang === 'pl' ? "Czw" : "Thu"}</div>
          <div>{lang === 'pl' ? "Pt" : "Fri"}</div>
          <div className="text-indigo-400">{lang === 'pl' ? "Sob" : "Sat"}</div>
          <div className="text-indigo-400">{lang === 'pl' ? "Ndz" : "Sun"}</div>
        </div>

        <div className="grid grid-cols-7 gap-1.5 pt-1">
          {Array.from({ length: startOffset }).map((_, idx) => (
            <div key={`offset_${idx}`} className="h-14 sm:h-16 rounded-xl bg-slate-950/20 border border-transparent" />
          ))}

          {Array.from({ length: daysInMonth }).map((_, idx) => {
            const dayNum = idx + 1;
            const dayStr = String(dayNum).padStart(2, '0');
            const monthStr = String(calendarMonth + 1).padStart(2, '0');
            const dateKey = `${calendarYear}-${monthStr}-${dayStr}`;

            const dayWorkouts = workoutsByDateMap[dateKey] || [];
            const hasWorkout = dayWorkouts.length > 0;
            const isToday = dateKey === todayStr;
            const isSelected = selectedDayWorkouts && selectedDayWorkouts.dateKey === dateKey;

            return (
              <div
                key={dateKey}
                onClick={() => {
                  if (hasWorkout) {
                    setSelectedDayWorkouts({ dateKey, dayNum, workouts: dayWorkouts });
                  }
                }}
                className={`h-14 sm:h-16 rounded-xl p-1.5 flex flex-col justify-between transition-all duration-150 relative ${
                  hasWorkout ? 'cursor-pointer' : 'cursor-default'
                } ${
                  hasWorkout 
                    ? (isSelected 
                        ? 'bg-emerald-500/30 border-2 border-emerald-400 text-white shadow-[0_0_15px_rgba(16,185,129,0.4)] scale-105 z-10' 
                        : (isToday
                            ? 'bg-emerald-500/20 border-2 border-indigo-500 text-emerald-300 shadow-[0_0_12px_rgba(99,102,241,0.35)]'
                            : 'bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 hover:border-emerald-400 shadow-md'))
                    : (isToday
                        ? 'bg-indigo-500/15 border-2 border-indigo-500 text-indigo-300 shadow-[0_0_12px_rgba(99,102,241,0.25)]'
                        : 'bg-slate-950/60 border border-slate-800/80 text-slate-400 hover:border-slate-700')
                }`}
              >
                <div className="flex items-center justify-between w-full">
                  <span className={`text-xs font-black ${hasWorkout ? 'text-emerald-300' : (isToday ? 'text-indigo-400 font-black' : 'text-slate-400')}`}>
                    {dayNum}
                  </span>
                  {isToday && (
                    <span className="text-[8px] font-black uppercase px-1 rounded bg-indigo-500/30 text-indigo-300 border border-indigo-500/40">
                      {lang === 'pl' ? 'Dziś' : 'Today'}
                    </span>
                  )}
                </div>

                {hasWorkout && (
                  <div className="flex items-center justify-between mt-auto">
                    <span className="material-symbols-outlined text-sm sm:text-base text-emerald-400 animate-pulse">
                      fitness_center
                    </span>
                    <span className="text-[9px] font-black bg-emerald-950/80 border border-emerald-500/50 text-emerald-300 px-1.5 py-0.5 rounded-md">
                      {dayWorkouts.length > 1 ? `${dayWorkouts.length}×` : (lang === 'pl' ? 'Wpis' : 'Logged')}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {selectedDayWorkouts && (
          <div className="bg-slate-950 border border-emerald-500/40 rounded-2xl p-4 sm:p-5 mt-4 space-y-3 shadow-xl animate-fade-in">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <strong className="text-xs sm:text-sm font-extrabold text-emerald-300 flex items-center gap-2">
                <span className="material-symbols-outlined text-base">event_available</span>
                <span>{lang === 'pl' ? `Sesje w dniu: ${selectedDayWorkouts.dateKey}` : `Logged workouts on: ${selectedDayWorkouts.dateKey}`}</span>
              </strong>
              <button
                type="button"
                onClick={() => setSelectedDayWorkouts(null)}
                className="text-slate-400 hover:text-white text-xs font-bold bg-slate-900 border border-slate-800 px-2.5 py-1 rounded-lg transition cursor-pointer"
              >
                ✕ {lang === 'pl' ? 'Zamknij' : 'Close'}
              </button>
            </div>

            <div className="space-y-3">
              {selectedDayWorkouts.workouts.map((wk, wIdx) => (
                <div key={wIdx} className="bg-slate-900/90 border border-slate-800 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-inner">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-sm text-emerald-400">check_circle</span>
                      <strong className="text-slate-100 font-extrabold text-sm">
                        {wk.routineTitle || wk.planTitle || wk.title || (lang === 'pl' ? "Trening Własny" : "Custom Session")}
                      </strong>
                    </div>
                    <p className="text-xs text-slate-400 m-0 font-medium flex items-center gap-2">
                      <span>{lang === 'pl' ? `Liczba ćwiczeń: ${(wk.exercises || []).length} • RPE: ${wk.rpe || "8"}/10` : `Exercises: ${(wk.exercises || []).length} • RPE: ${wk.rpe || "8"}/10`}</span>
                      {wk.caloriesBurned > 0 && (
                        <span className="text-amber-300 font-extrabold flex items-center gap-0.5">
                          <span className="material-symbols-outlined text-xs text-amber-400">local_fire_department</span>
                          <span>{wk.caloriesBurned} kcal</span>
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    

</div>
  );
};

/* ==========================================
   COACH TRAINEE SUMMARY REPORT MODAL
   ========================================== */
const CoachTraineeReportModal = ({ trainee, onClose }) => {
  const { lang } = useTranslation();
  const [workouts, setWorkouts] = useState([]);
  const [timeRange, setTimeRange] = useState('this_month'); // 'this_month' | '30_days' | '90_days' | 'all' | 'custom'
  
  const today = new Date();
  const firstDayThisMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  const todayStr = today.toISOString().split('T')[0];

  const [customStartDate, setCustomStartDate] = useState(firstDayThisMonth);
  const [customEndDate, setCustomEndDate] = useState(todayStr);

  const [selectedProgressionExercise, setSelectedProgressionExercise] = useState("");

  useEffect(() => {
    if (!trainee?.id) return;
    let unsubscribe;
    try {
      const q = query(
        collection(db, 'workouts'),
        where('userId', '==', trainee.id),
        orderBy('createdAt', 'desc')
      );
      unsubscribe = onSnapshot(q, (snapshot) => {
        const docs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setWorkouts(docs);
      }, (err) => console.warn("Report modal listener notice:", err.message));
    } catch (e) {}
    return () => { if (unsubscribe) unsubscribe(); };
  }, [trainee]);

  // Filter workouts by selected time range
  const filteredWorkouts = useMemo(() => {
    if (!workouts || workouts.length === 0) return [];
    const now = new Date();
    
    return workouts.filter(w => {
      if (!w.date) return false;
      const wDate = new Date(w.date);

      if (timeRange === 'this_month') {
        return wDate.getFullYear() === now.getFullYear() && wDate.getMonth() === now.getMonth();
      } else if (timeRange === '30_days') {
        const cutoff = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        return wDate >= cutoff;
      } else if (timeRange === '90_days') {
        const cutoff = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        return wDate >= cutoff;
      } else if (timeRange === 'custom') {
        const start = customStartDate ? new Date(customStartDate) : new Date(0);
        const end = customEndDate ? new Date(customEndDate + 'T23:59:59') : new Date();
        return wDate >= start && wDate <= end;
      }
      return true; // 'all'
    });
  }, [workouts, timeRange, customStartDate, customEndDate]);

  // Key stats
  const totalSessions = filteredWorkouts.length;
  const totalCalories = filteredWorkouts.reduce((acc, w) => acc + (Number(w.caloriesBurned) || 0), 0);
  const workoutsWithCalories = filteredWorkouts.filter(w => Number(w.caloriesBurned) > 0).length;
  const avgCalories = workoutsWithCalories > 0 ? Math.round(totalCalories / workoutsWithCalories) : 0;
  const maxCalories = filteredWorkouts.reduce((max, w) => Math.max(max, Number(w.caloriesBurned) || 0), 0);
  
  const avgRpe = filteredWorkouts.length > 0 
    ? (filteredWorkouts.reduce((acc, w) => acc + (parseFloat(w.rpe) || 8), 0) / filteredWorkouts.length).toFixed(1)
    : "0.0";

  // Unique exercises logged in filtered workouts
  const uniqueExercises = useMemo(() => {
    const set = new Set();
    filteredWorkouts.forEach(w => {
      (w.exercises || []).forEach(e => {
        if (e.name) set.add(e.name.trim());
      });
    });
    return Array.from(set).sort();
  }, [filteredWorkouts]);

  useEffect(() => {
    if (uniqueExercises.length > 0 && !selectedProgressionExercise) {
      setSelectedProgressionExercise(uniqueExercises[0]);
    }
  }, [uniqueExercises, selectedProgressionExercise]);

  // Progression history for selected exercise
  const exerciseProgressionHistory = useMemo(() => {
    if (!selectedProgressionExercise) return [];
    const list = [];
    filteredWorkouts.forEach(w => {
      (w.exercises || []).forEach(e => {
        if ((e.name || "").trim().toLowerCase() === selectedProgressionExercise.trim().toLowerCase()) {
          (e.sets || []).forEach(s => {
            if (s.weight && s.reps) {
              list.push({
                date: w.date,
                rpe: w.rpe || 8,
                weight: parseFloat(s.weight),
                reps: parseInt(s.reps),
                setNumber: s.setNumber
              });
            }
          });
        }
      });
    });
    return list;
  }, [filteredWorkouts, selectedProgressionExercise]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-md overflow-y-auto">
      <div className="bg-slate-900 border border-slate-700 w-full max-w-5xl rounded-3xl p-6 sm:p-8 shadow-2xl space-y-6 my-8 max-h-[90vh] overflow-y-auto">
        
        {/* Header & Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800 pb-5">
          <div>
            <div className="flex items-center gap-2">
              <img src="/gymghost_logo.jpg" alt="GymGhost Logo" className="w-8 h-8 rounded-lg object-cover border border-emerald-500/40" />
              <h2 className="text-xl sm:text-2xl font-black text-white m-0 tracking-tight">
                {lang === 'pl' ? `Raport Podsumowujący: ${trainee.name || trainee.displayName}` : `Summary Report: ${trainee.name || trainee.displayName}`}
              </h2>
            </div>
            <span className="text-xs text-slate-400 font-medium block mt-1">
              {trainee.email} • {lang === 'pl' ? "Wygenerowano w GymGhost" : "Generated in GymGhost"} ({todayStr})
            </span>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 text-xs font-extrabold px-4 py-2.5 rounded-xl transition cursor-pointer active:scale-95 shadow-sm"
            >
              ✕ {lang === 'pl' ? "Zamknij" : "Close"}
            </button>
          </div>
        </div>

        {/* Time Range Filter Controls */}
        <div className="bg-slate-950/80 p-4 rounded-2xl border border-slate-800 space-y-3 shadow-inner">
          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">
            {lang === 'pl' ? "Zakres Czasowy Raportu:" : "Report Timeframe:"}
          </label>
          <div className="flex flex-wrap items-center gap-2">
            {[
              { id: 'this_month', label: lang === 'pl' ? "Ten Miesiąc" : "This Month" },
              { id: '30_days', label: lang === 'pl' ? "Ostatnie 30 Dni" : "Last 30 Days" },
              { id: '90_days', label: lang === 'pl' ? "Ostatnie 90 Dni" : "Last 90 Days" },
              { id: 'all', label: lang === 'pl' ? "Cały Okres" : "All Time" },
              { id: 'custom', label: lang === 'pl' ? "Własny Zakres" : "Custom Range" }
            ].map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setTimeRange(tab.id)}
                className={`px-3.5 py-2 rounded-xl text-xs font-extrabold transition cursor-pointer ${
                  timeRange === tab.id
                    ? 'bg-sky-500 text-slate-950 font-black shadow-md shadow-sky-500/20'
                    : 'bg-slate-900 text-slate-400 hover:text-white border border-slate-800'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {timeRange === 'custom' && (
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-slate-400">{lang === 'pl' ? "Od:" : "From:"}</span>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="bg-slate-900 border border-slate-700 text-white font-bold text-xs rounded-xl px-3 py-1.5 outline-none"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-slate-400">{lang === 'pl' ? "Do:" : "To:"}</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="bg-slate-900 border border-slate-700 text-white font-bold text-xs rounded-xl px-3 py-1.5 outline-none"
                />
              </div>
            </div>
          )}
        </div>

        {/* 1. Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 shadow-inner">
            <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest block">
              {lang === 'pl' ? "Treningi na Siłowni" : "Gym Sessions Logged"}
            </span>
            <div className="text-2xl sm:text-3xl font-black text-white mt-1">
              {totalSessions} <span className="text-xs text-emerald-400 font-bold">{lang === 'pl' ? "sesji" : "workouts"}</span>
            </div>
          </div>

          <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 shadow-inner">
            <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest block">
              {lang === 'pl' ? "Łącznie Spalone Kalorie" : "Total Calories Burned"}
            </span>
            <div className="text-2xl sm:text-3xl font-black text-amber-400 mt-1">
              {totalCalories.toLocaleString()} <span className="text-xs text-amber-300 font-bold">kcal</span>
            </div>
          </div>

          <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 shadow-inner">
            <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest block">
              {lang === 'pl' ? "Średnio / Trening" : "Average per Workout"}
            </span>
            <div className="text-2xl sm:text-3xl font-black text-sky-400 mt-1">
              {avgCalories} <span className="text-xs text-slate-400 font-bold">kcal</span>
            </div>
          </div>

          <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 shadow-inner">
            <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest block">
              {lang === 'pl' ? "Średnie RPE Sesji" : "Average Session RPE"}
            </span>
            <div className="text-2xl sm:text-3xl font-black text-indigo-300 mt-1">
              {avgRpe} <span className="text-xs text-slate-500 font-bold">/ 10</span>
            </div>
          </div>
        </div>

        {/* 2. List of Completed Trainings */}
        <div className="bg-slate-950/70 border border-slate-800 rounded-2xl p-5 space-y-3 shadow-inner">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <h3 className="text-sm font-black text-white m-0 flex items-center gap-2 uppercase tracking-wider">
              <span className="material-symbols-outlined text-base text-emerald-400">fitness_center</span>
              <span>{lang === 'pl' ? "Wykaz Zrealizowanych Treningów" : "Trainings Completed Breakdown"}</span>
            </h3>
            <span className="text-xs text-slate-400 font-bold">{filteredWorkouts.length} {lang === 'pl' ? "wpisów" : "entries"}</span>
          </div>

          {filteredWorkouts.length === 0 ? (
            <div className="p-6 text-center text-slate-500 text-xs italic">
              {lang === 'pl' ? "Brak zarejestrowanych treningów w tym przedziale czasowym." : "No workouts logged in this timeframe."}
            </div>
          ) : (
            <div className="space-y-2.5 max-h-56 overflow-y-auto pr-1">
              {filteredWorkouts.map((wk, idx) => (
                <div key={idx} className="bg-slate-900 p-3.5 rounded-xl border border-slate-800 flex items-center justify-between gap-3 text-xs">
                  <div>
                    <strong className="text-white font-extrabold block text-sm">{wk.routineTitle || wk.planTitle || (lang === 'pl' ? "Trening Własny" : "Custom Session")}</strong>
                    <span className="text-slate-400 text-[11px] font-medium">{wk.date} • RPE: {wk.rpe}/10</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-slate-300 font-semibold">{lang === 'pl' ? `Ćwiczeń: ${(wk.exercises || []).length}` : `Exercises: ${(wk.exercises || []).length}`}</span>
                    {wk.caloriesBurned > 0 && (
                      <span className="text-amber-300 font-black bg-amber-500/10 border border-amber-500/30 px-2.5 py-1 rounded-lg flex items-center gap-1">
                        <span className="material-symbols-outlined text-xs text-amber-400">local_fire_department</span>
                        <span>{wk.caloriesBurned} kcal</span>
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 3. Calories Burned Progress Chart */}
        {totalCalories > 0 && (
          <div className="bg-slate-950/70 border border-slate-800 rounded-2xl p-5 space-y-3 shadow-inner">
            <h3 className="text-sm font-black text-white m-0 flex items-center gap-2 uppercase tracking-wider">
              <span className="material-symbols-outlined text-base text-amber-400">bar_chart</span>
              <span>{lang === 'pl' ? "Wykres Spalonych Kalorii (Wydatek Energetyczny)" : "Calories Burned Chart"}</span>
            </h3>

            <div className="space-y-2.5 max-h-52 overflow-y-auto pr-1">
              {filteredWorkouts.filter(w => Number(w.caloriesBurned) > 0).map((wk, idx) => {
                const cal = Number(wk.caloriesBurned) || 0;
                const maxVal = maxCalories || 1;
                const pct = Math.min(100, Math.max(10, Math.round((cal / maxVal) * 100)));
                return (
                  <div key={idx} className="bg-slate-900 p-3 rounded-xl border border-slate-800 flex items-center justify-between gap-3">
                    <span className="text-xs font-bold text-slate-300 w-24 shrink-0 truncate">{wk.date}</span>
                    <div className="w-full bg-slate-950 h-3 rounded-full overflow-hidden p-0.5 border border-slate-800 flex-1">
                      <div className="h-full rounded-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-500" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs font-black text-amber-400 w-20 text-right shrink-0">{cal} kcal</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 4. Exercise Progression Analysis */}
        <div className="bg-slate-950/70 border border-slate-800 rounded-2xl p-5 space-y-4 shadow-inner">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-800 pb-3">
            <h3 className="text-sm font-black text-white m-0 flex items-center gap-2 uppercase tracking-wider">
              <span className="material-symbols-outlined text-base text-sky-400">trending_up</span>
              <span>{lang === 'pl' ? "Progresja Ciężarów w Czasie" : "Exercise Strength Progression"}</span>
            </h3>

            <div className="w-full sm:w-64">
              <select
                value={selectedProgressionExercise}
                onChange={(e) => setSelectedProgressionExercise(e.target.value)}
                className="w-full bg-slate-900 border border-slate-750 text-sky-300 font-bold text-xs rounded-xl px-3 py-2 outline-none cursor-pointer"
              >
                {uniqueExercises.length === 0 ? (
                  <option value="">{lang === 'pl' ? "-- Brak ćwiczeń --" : "-- No exercises --"}</option>
                ) : (
                  uniqueExercises.map((name, idx) => (
                    <option key={idx} value={name}>{name}</option>
                  ))
                )}
              </select>
            </div>
          </div>

          {exerciseProgressionHistory.length === 0 ? (
            <div className="p-6 text-center text-slate-500 text-xs italic">
              {lang === 'pl' ? "Brak zarejestrowanych ciężarów dla tego ćwiczenia w wybranym okresie." : "No logged weight records for this exercise in timeframe."}
            </div>
          ) : (
            <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
              {exerciseProgressionHistory.map((row, rIdx) => (
                <div key={rIdx} className="bg-slate-900 p-3 rounded-xl border border-slate-800 flex items-center justify-between gap-3 text-xs">
                  <div className="flex items-center gap-3">
                    <span className="text-slate-400 font-bold">{row.date}</span>
                    <span className="text-emerald-400 font-black text-sm">{row.weight} kg × {row.reps} powt.</span>
                  </div>
                  <span className="text-slate-400 font-medium">Seria #{row.setNumber} • RPE: {row.rpe}/10</span>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    

</div>
  );
};

const RenderLiveTracker = ({ clients, selectedTrainee, setSelectedTrainee, traineeSearchQuery, setTraineeSearchQuery }) => {
  const { t, lang } = useTranslation();
  const [showReportModal, setShowReportModal] = useState(false);

  const filteredTrainees = clients.filter(c => {
    const term = (traineeSearchQuery || "").toLowerCase();
    return (c.name || c.displayName || "").toLowerCase().includes(term) ||
           (c.email || "").toLowerCase().includes(term);
  });

  return (
    <div className="w-full max-w-7xl mx-auto space-y-6">
      {/* Trainee Report Modal */}
      {showReportModal && selectedTrainee && (
        <CoachTraineeReportModal
          trainee={selectedTrainee}
          onClose={() => setShowReportModal(false)}
        />
      )}

      <div className="border-b border-slate-700 pb-4 flex justify-between items-end flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="material-symbols-outlined text-3xl text-indigo-400">bolt</span>
            <h2 className="text-2xl md:text-3xl font-black text-white m-0 tracking-tight leading-none">{t('liveTracker.title')}</h2>
          </div>
          <p className="text-slate-400 text-sm md:text-base mt-1.5 m-0 font-medium">{t('liveTracker.subtitle')}</p>
        </div>
      </div>

      <div className="bg-slate-800/90 p-6 rounded-2xl border border-slate-700 shadow-xl space-y-5">
        <div className="flex justify-between items-center flex-wrap gap-4 border-b border-slate-700/80 pb-4">
          <h4 className="text-xs md:text-sm font-black uppercase tracking-wider text-slate-300 m-0 flex items-center gap-2">
            <span className="material-symbols-outlined text-base">group</span>
            <span>{t('liveTracker.selectorLabel')} ({filteredTrainees.length} {t('common.active')})</span>
          </h4>
          <div className="w-full sm:w-auto flex-1 max-w-md">
            <input
              type="text"
              placeholder={t('liveTracker.searchPlaceholder')}
              value={traineeSearchQuery}
              onChange={(e) => setTraineeSearchQuery(e.target.value)}
              className="w-full bg-slate-900 border border-slate-600 text-white font-semibold text-sm rounded-xl px-4 py-2.5 outline-none focus:border-sky-400 placeholder-slate-500 shadow-inner"
            />
          </div>
        </div>

        {filteredTrainees.length === 0 ? (
          <div className="p-8 text-center bg-slate-900/50 rounded-xl border border-dashed border-slate-700 text-slate-400 font-semibold">
            {lang === 'pl' ? `Brak aktywnych zawodników u tego trenera w Firestore.` : `No active athletes registered under this coach in Firestore.`}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[360px] overflow-y-auto p-1">
            {filteredTrainees.map((trainee) => {
              const isSelected = selectedTrainee?.id === trainee.id;
              return (
                <button
                  key={trainee.id}
                  onClick={() => setSelectedTrainee(trainee)}
                  className={`p-5 rounded-xl border text-left transition-all flex items-center justify-between gap-3.5 shadow-lg cursor-pointer ${
                    isSelected 
                      ? 'bg-gradient-to-br from-sky-600/30 to-sky-500/10 border-sky-400 text-white font-extrabold shadow-sky-500/10' 
                      : 'bg-slate-900/90 border-slate-700 hover:border-slate-500 text-slate-300 hover:text-white'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {(trainee.email || "").toLowerCase().includes('kinga') || (trainee.name || trainee.displayName || "").toLowerCase().includes('kinga') || (trainee.name || trainee.displayName || "").toLowerCase().includes('testowa') ? (
                      <img 
                        src="/kinga_avatar.jpg" 
                        alt={trainee.displayName || trainee.name} 
                        className="w-11 h-11 rounded-full object-cover border-2 border-fuchsia-400 shadow-md shadow-fuchsia-500/30 shrink-0 transition hover:scale-105" 
                      />
                    ) : (trainee.email || "").toLowerCase().includes('monika') || (trainee.name || trainee.displayName || "").toLowerCase().includes('monika') ? (
                      <img 
                        src="/monika_avatar.jpg?v=1" 
                        alt={trainee.displayName || trainee.name} 
                        className="w-11 h-11 rounded-full object-cover border-2 border-pink-400 shadow-md shadow-pink-500/30 shrink-0 transition hover:scale-105" 
                      />
                    ) : (trainee.email || "").toLowerCase().includes('cuker') || (trainee.email || "").toLowerCase().includes('mateusz') || trainee.avatarUrl ? (
                      <img 
                        src={(trainee.avatarUrl || "/mateusz_avatar.png").replace("/mateusz_avatar.png", "/mateusz_avatar.png?v=2")} 
                        alt={trainee.displayName || trainee.name} 
                        className="w-11 h-11 rounded-full object-cover border-2 border-sky-400 shadow-md shrink-0 transition hover:scale-105" 
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-slate-950 border border-slate-800 flex items-center justify-center text-lg shrink-0 shadow-inner">
                        <span className="material-symbols-outlined text-slate-400">person</span>
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <span className="block text-base md:text-lg font-extrabold text-white whitespace-normal break-words leading-snug">
                        {trainee.name || trainee.displayName}
                      </span>
                      <span className="block text-xs text-slate-400 font-medium mt-1 whitespace-normal break-all">
                        {trainee.email}
                      </span>
                    </div>
                  </div>
                  {isSelected ? (
                    <span className="w-3.5 h-3.5 rounded-full bg-sky-400 animate-pulse shadow-md shadow-sky-400/50 shrink-0"></span>
                  ) : (
                    <span className="material-symbols-outlined text-base text-slate-500 shrink-0">arrow_forward_ios</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {selectedTrainee ? (
        <div className="space-y-6">
          {/* Top Trainee Header Bar with Report Generation Button */}
          <div className="bg-slate-900/90 border border-slate-800 p-4 sm:p-5 rounded-2xl flex items-center justify-between flex-wrap gap-4 shadow-xl">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center text-indigo-400 text-xl font-bold shrink-0 shadow">
                <span className="material-symbols-outlined text-2xl">person</span>
              </div>
              <div>
                <strong className="text-base sm:text-lg font-black text-white block leading-tight">
                  {selectedTrainee.name || selectedTrainee.displayName}
                </strong>
                <span className="text-xs text-slate-400 font-medium">{selectedTrainee.email}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowReportModal(true)}
              className="bg-gradient-to-r from-indigo-600 to-sky-600 hover:from-indigo-500 hover:to-sky-500 text-white border border-indigo-400/40 text-xs sm:text-sm font-black px-4 py-2.5 rounded-xl transition cursor-pointer shadow-lg shadow-indigo-500/20 flex items-center gap-2 active:scale-95 shrink-0"
            >
              <span className="material-symbols-outlined text-base">assessment</span>
              <span>{lang === 'pl' ? "Generuj Raport Podsumowujący" : "Generate Summary Report"}</span>
            </button>
          </div>

          <CoachAttendanceCalendar 
            traineeId={selectedTrainee.id} 
            traineeName={selectedTrainee.name || selectedTrainee.displayName || selectedTrainee.email} 
          />
          <div className="bg-slate-800/60 p-6 md:p-8 rounded-xl border border-slate-700/80 shadow-2xl">
            <div className="flex items-center gap-3 mb-2">
              <span className="material-symbols-outlined text-2xl text-indigo-400">analytics</span>
              <h3 className="text-xl font-extrabold text-white m-0">{t('liveTracker.liveDiaryTitle')} <span className="text-sky-400">{selectedTrainee.name || selectedTrainee.displayName}</span></h3>
            </div>
            <WorkoutList userId={selectedTrainee.id} role="trainer" />
          </div>
        </div>
      ) : (
        <div className="p-16 text-center text-slate-400 bg-slate-800/30 rounded-xl border border-dashed border-slate-700 font-medium">
          {t('liveTracker.emptySelection')}
        </div>
      )}
    

</div>
  );
};

/* ==========================================
   SUB-RENDER 2: CREATE & ASSIGN (TAB 'assign')
   ========================================== */
const RenderCreateAndAssign = ({
  trainerId = '',
  trainerEmail = '',
  routineTitle, setRoutineTitle,
  routineDate, setRoutineDate,
  assignedClientId, setAssignedClientId,
  routineTag = 'FBW', setRoutineTag = () => {},
  customRoutineTag = '', setCustomRoutineTag = () => {},
  clients, exercises,
  routineExercises, setRoutineExercises,
  setSelectedExercises,
  handleAddExerciseToRoutine,
  handleSaveRoutine,
  createdRoutines,
  isCreatingPlan, setIsCreatingPlan,
  planTitle, setPlanTitle,
  planAssignedTrainee, setPlanAssignedTrainee,
  planNotes, setPlanNotes,
  planDays, setPlanDays,
  handleSavePlan
}) => {
  const { t, lang } = useTranslation();
  const [exerciseSearch, setExerciseSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [originFilter, setOriginFilter] = useState('ALL'); // 'ALL' | 'CUSTOM' | 'GLOBAL'

  const filteredCatalog = (exercises || []).filter(ex => {
    const isGlobal = ex.isGlobal || (ex.trainerId || "").toLowerCase() === 'global' || (ex.trainerId || "").toLowerCase() === 'all';
    if (originFilter === 'CUSTOM' && isGlobal) return false;
    if (originFilter === 'GLOBAL' && !isGlobal) return false;

    const searchLower = (exerciseSearch || "").toLowerCase().trim();
    if (searchLower) {
      const exName = (ex.name || "").toLowerCase();
      const exNamePL = (ex.namePL || ex.name_pl || "").toLowerCase();
      const exNameEN = (ex.nameEN || ex.name_en || "").toLowerCase();
      const exBody = (ex.bodyPart || "").toLowerCase();
      const exTarget = (ex.targetMuscle || "").toLowerCase();
      const matchesSearch = exName.includes(searchLower) || exNamePL.includes(searchLower) || exNameEN.includes(searchLower) || exBody.includes(searchLower) || exTarget.includes(searchLower);
      if (!matchesSearch) return false;
    }
    if (selectedCategory !== 'ALL') {
      const catLower = selectedCategory.toLowerCase().trim();
      const bodyLower = (ex.bodyPart || ex.targetMuscle || "").toLowerCase().trim();
      if (catLower === 'biceps' || catLower === 'triceps') {
        return bodyLower === catLower || bodyLower === 'arms';
      }
      return bodyLower === catLower;
    }
    return true;
  });

  return (
    <div className="w-full max-w-7xl mx-auto space-y-8">
      <div className="border-b border-slate-700 pb-4">
        <div className="flex items-center gap-3 mb-2">
          <span className="material-symbols-outlined text-3xl text-indigo-400">fitness_center</span>
          <h2 className="text-2xl md:text-3xl font-black text-white m-0 tracking-tight leading-none">{t('createAssign.title')}</h2>
        </div>
        <p className="text-slate-400 text-sm md:text-base mt-1.5 m-0 font-medium">{t('createAssign.subtitle')}</p>
      </div>

      <div className="flex gap-4 mb-6 border-b border-slate-800 pb-4 flex-wrap">
        <button
          type="button"
          onClick={() => setIsCreatingPlan(false)}
          className={`px-4 py-2.5 rounded-xl text-xs sm:text-sm font-extrabold transition cursor-pointer shadow flex items-center gap-2 ${
            !isCreatingPlan ? 'bg-indigo-600 text-white shadow-indigo-500/30 font-black' : 'text-slate-400 hover:text-slate-200 bg-slate-900/80 border border-slate-800'
          }`}
        >
          <span className="material-symbols-outlined text-sm">fitness_center</span>
          <span>{lang === 'pl' ? "Szablon Pojedynczej Sesji" : "Single Routine Template"}</span>
        </button>
        <button
          type="button"
          onClick={() => setIsCreatingPlan(true)}
          className={`px-4 py-2.5 rounded-xl text-xs sm:text-sm font-extrabold transition cursor-pointer shadow flex items-center gap-2 ${
            isCreatingPlan ? 'bg-indigo-600 text-white shadow-indigo-500/30 animate-pulse font-black' : 'text-slate-400 hover:text-slate-200 bg-slate-900/80 border border-slate-800'
          }`}
        >
          <span className="material-symbols-outlined text-sm">folder_special</span>
          <span>{lang === 'pl' ? "Wielodniowy Plan Treningowy (Cykl)" : "Multi-Day Training Plan"}</span>
        </button>
      </div>

      {isCreatingPlan ? (
        <InteractivePlanBuilder
          initialTitle={planTitle}
          initialNotes={planNotes}
          initialDays={planDays}
          availableRoutines={createdRoutines}
          activeTrainees={clients}
          isGlobalOnly={false}
          lang={lang}
          onSave={async (planData) => {
            const targetClientObj = planData.assignedTrainee;
            const targetId = planData.assignmentMode === 'ALL_TRAINEES' ? 'ALL' : (targetClientObj ? (targetClientObj.id || targetClientObj.email) : 'TEMPLATE');
            const isAllTrainees = targetId === 'ALL';
            const cleanCoachId = (trainerEmail || trainerId || "wieslawp@gmail.com").toLowerCase().trim();
            const planId = 'plan_' + Date.now();

            const payload = {
              id: planId,
              docId: planId,
              trainerId: cleanCoachId,
              trainerEmail: cleanCoachId,
              assignedTo: isAllTrainees ? "ALL" : (targetClientObj?.email || targetId).toLowerCase().trim(),
              assignedClientId: isAllTrainees ? "ALL" : (targetClientObj?.id || targetId).toLowerCase().trim(),
              assignedToName: isAllTrainees ? (lang === 'pl' ? "Wszyscy moi zawodnicy" : "All My Trainees") : (targetClientObj?.name || targetClientObj?.displayName || targetClientObj?.email || "Template Only"),
              title: planData.title,
              notes: planData.notes,
              durationWeeks: planData.durationWeeks,
              includeDeload: planData.includeDeload,
              isGlobal: false,
              routines: planData.days,
              createdAt: Date.now()
            };

            await setDoc(doc(db, "plans", planId), payload).catch(err => console.warn("Offline note:", err.message));

            if (targetClientObj && targetClientObj.email && targetClientObj.email !== 'ALL' && targetClientObj.email.includes('@')) {
              sendNewPlanAssignedNotification(targetClientObj.email, targetClientObj.name || targetClientObj.displayName, planData.title, userProfile?.displayName || userProfile?.name || trainerEmail || "Twój Trener")
                .catch(e => console.warn("Email notice error:", e));
            }

            alert(lang === 'pl' ? `🎉 Zapisano i wdrożono cykl treningowy "${planData.title}"!` : `🎉 Successfully created and deployed plan "${planData.title}"!`);

            setPlanTitle("");
            setPlanNotes("");
            setIsCreatingPlan(false);
          }}
          onCancel={() => setIsCreatingPlan(false)}
        />
      ) : (
        <InteractiveWorkoutBuilder
          initialTitle={routineTitle}
          initialTag={routineTag}
          initialExercises={routineExercises}
          isGlobalOnly={false}
          allExercises={exercises}
          customExercises={exercises.filter(e => e.isCustom || (e.trainerId || "").toLowerCase() === (trainerEmail || trainerId || "trainer_wieslaw").toLowerCase().trim())}
          assignedClientId={assignedClientId}
          setAssignedClientId={setAssignedClientId}
          activeTrainees={clients}
          lang={lang}
          onSave={async (routineData) => {
            const finalTitle = routineData.title;
            const finalTag = routineData.tag;
            const targetClient = routineData.assignedClientId || assignedClientId || 'ALL';
            const cleanCoachId = (trainerEmail || trainerId || "wieslawp@gmail.com").toLowerCase().trim();
            const isGlobalRoutine = targetClient === 'GLOBAL';
            const rtId = isGlobalRoutine ? ('rt_global_' + Date.now()) : ('routine_' + Date.now());

            const newRoutine = {
              id: rtId,
              docId: rtId,
              trainerId: isGlobalRoutine ? "global" : cleanCoachId,
              trainerEmail: cleanCoachId,
              creatorCoachEmail: cleanCoachId,
              assignedClientId: isGlobalRoutine ? "GLOBAL" : (targetClient || 'ALL'),
              assignedTo: isGlobalRoutine ? "GLOBAL" : (targetClient || 'ALL'),
              creationDate: routineDate || new Date().toISOString().split('T')[0],
              title: finalTitle,
              tag: finalTag,
              isGlobal: isGlobalRoutine,
              exercises: routineData.exercises,
              createdAt: Date.now()
            };

            await setDoc(doc(db, 'routines', rtId), newRoutine, { merge: true }).catch(err => console.warn("Offline note:", err.message));
            setCreatedRoutines(prev => [newRoutine, ...prev]);
            window.dispatchEvent(new CustomEvent('routine_created', { detail: newRoutine }));

            const targetName = targetClient === 'ALL' 
              ? (lang === 'pl' ? "Wszyscy moi zawodnicy" : "All My Trainees") 
              : (targetClient === 'GLOBAL' ? (lang === 'pl' ? "Biblioteka Globalna" : "Global Library") : (clients.find(c => c.id === targetClient)?.name || "selected trainee"));

            alert(lang === 'pl' ? `🎯 Szablon treningu "${finalTitle}" [#${finalTag}] został zapisany!\n\nPrzypisano do: ${targetName}` : `🎯 Routine template "${finalTitle}" [#${finalTag}] saved successfully!\n\nTarget recipient: ${targetName}`);

            setRoutineTitle('');
            setRoutineExercises([]);
            setSelectedExercises([]);
            setAssignedClientId('ALL');
          }}
        />
      )}
    

</div>
  );
};

/* ==========================================
   SUB-RENDER 3: MASTER CATALOG (TAB 'catalog')
   ========================================== */
const RenderMasterCatalog = ({
  exercises = [],
  customExerciseNamePL, setCustomExerciseNamePL,
  customExerciseNameEN, setCustomExerciseNameEN,
  customExerciseHintsPL, setCustomExerciseHintsPL,
  customExerciseHintsEN, setCustomExerciseHintsEN,
  customExerciseImageFile, setCustomExerciseImageFile,
  customExerciseImageUrlInput, setCustomExerciseImageUrlInput,
  customExerciseYoutubeUrl, setCustomExerciseYoutubeUrl,
  isUploadingCoachImage,
  customExerciseBodyPart, setCustomExerciseBodyPart,
  customExerciseEquipment = 'body weight', setCustomExerciseEquipment = () => {},
  customExerciseSubPart = '', setCustomExerciseSubPart = () => {},
  customExerciseTarget = '', setCustomExerciseTarget = () => {},
  customExerciseMuscleGroup = '', setCustomExerciseMuscleGroup = () => {},
  customExerciseSecondaryMuscles = '', setCustomExerciseSecondaryMuscles = () => {},
  customExerciseInstructionsPL = '', setCustomExerciseInstructionsPL = () => {},
  customExerciseInstructionsEN = '', setCustomExerciseInstructionsEN = () => {},
  customExerciseMediaId = '', setCustomExerciseMediaId = () => {},
  customExerciseAttribution = 'Gym visual', setCustomExerciseAttribution = () => {},
  handleCreateExercise,
  editingExerciseId, setEditingExerciseId,
  editingName, setEditingName,
  editingNamePL, setEditingNamePL,
  editingNameEN, setEditingNameEN,
  editingHintsPL, setEditingHintsPL,
  editingHintsEN, setEditingHintsEN,
  editingBodyPart, setEditingBodyPart,
  editingEquipment = 'body weight', setEditingEquipment = () => {},
  editingSubPart = '', setEditingSubPart = () => {},
  editingTarget = '', setEditingTarget = () => {},
  editingMuscleGroup = '', setEditingMuscleGroup = () => {},
  editingSecondaryMuscles = '', setEditingSecondaryMuscles = () => {},
  editingInstructionsPL = '', setEditingInstructionsPL = () => {},
  editingInstructionsEN = '', setEditingInstructionsEN = () => {},
  editingMediaId = '', setEditingMediaId = () => {},
  editingAttribution = 'Gym visual', setEditingAttribution = () => {},
  editingImageFile, setEditingImageFile,
  editingImageUrlInput, setEditingImageUrlInput,
  editingYoutubeUrl, setEditingYoutubeUrl,
  isUploadingEditImage,
  handleUpdateExercise,
  handleDeleteExercise,
  handleAddExerciseToRoutine,
  routineExercises = [], setRoutineExercises = () => {},
  selectedExercises = [], setSelectedExercises = () => {},
  setActiveTab = () => {},
  setEnlargedImage = () => {}
}) => {
  const { t, lang } = useTranslation();
  const [searchFilter, setSearchFilter] = useState('');
  const [bodyPartFilter, setBodyPartFilter] = useState('ALL');
  const [equipmentFilter, setEquipmentFilter] = useState('ALL');
  const [originFilter, setOriginFilter] = useState('ALL'); // 'ALL' | 'GLOBAL' | 'INDIVIDUAL'
  const [createLangTab, setCreateLangTab] = useState('pl'); // 'pl' | 'en'
  const [editLangTab, setEditLangTab] = useState('pl'); // 'pl' | 'en'
  const [viewingExerciseDetails, setViewingExerciseDetails] = useState(null);

  const filteredExercises = (exercises || []).filter(ex => {
    const isGlobalEx = ex.isGlobal || (ex.trainerId || "").toLowerCase() === 'global' || (ex.trainerId || "").toLowerCase() === 'all';
    if (originFilter === 'GLOBAL' && !isGlobalEx) return false;
    if (originFilter === 'INDIVIDUAL' && isGlobalEx) return false;

    if (equipmentFilter !== 'ALL') {
      const exEq = (ex.equipment || "").toLowerCase().trim();
      const filterEq = equipmentFilter.toLowerCase().trim();
      if (exEq !== filterEq) return false;
    }

    const matchesSearch = (ex.name || "").toLowerCase().includes(searchFilter.toLowerCase()) ||
                          (ex.namePL || "").toLowerCase().includes(searchFilter.toLowerCase()) ||
                          (ex.nameEN || "").toLowerCase().includes(searchFilter.toLowerCase()) ||
                          (ex.equipment || "").toLowerCase().includes(searchFilter.toLowerCase()) ||
                          (ex.bodyPart || ex.category || "").toLowerCase().includes(searchFilter.toLowerCase());
    if (!matchesSearch) return false;
    if (bodyPartFilter !== 'ALL') {
      const exBody = (ex.bodyPart || ex.category || "").toLowerCase();
      const targetBody = bodyPartFilter.toLowerCase();
      if (!exBody.includes(targetBody)) return false;
    }
    return true;
  });

  const handleToggleSelectExercise = (ex) => {
    if (routineExercises.some(re => re.id === ex.id)) {
      setRoutineExercises(prev => prev.filter(item => item.id !== ex.id));
      setSelectedExercises(prev => prev.filter(item => item.id !== ex.id));
    } else {
      setSelectedExercises(prev =>
        prev.some(e => e.id === ex.id) ? prev.filter(item => item.id !== ex.id) : [...prev, ex]
      );
    }
  };

  const handleSelectAll = () => {
    const allChecked = filteredExercises.every(ex => selectedExercises.some(e => e.id === ex.id) || routineExercises.some(re => re.id === ex.id));
    if (allChecked && filteredExercises.length > 0) {
      const filteredIds = filteredExercises.map(ex => ex.id);
      setSelectedExercises(prev => prev.filter(e => !filteredIds.includes(e.id)));
      setRoutineExercises(prev => prev.filter(re => !filteredIds.includes(re.id)));
    } else {
      const newItems = filteredExercises.filter(ex => !selectedExercises.some(e => e.id === ex.id) && !routineExercises.some(re => re.id === ex.id));
      setSelectedExercises(prev => [...prev, ...newItems]);
    }
  };

  const handleBatchAddToRoutine = () => {
    if (selectedExercises.length === 0) return;
    const newItems = selectedExercises.filter(ex => !routineExercises.some(re => re.id === ex.id));
    setRoutineExercises(prev => [...prev, ...newItems]);
    setSelectedExercises([]);
    setActiveTab('assign');
  };

  const handleBatchDelete = () => {
    if (selectedExercises.length === 0) return;
    if (window.confirm(lang === 'pl' ? `Czy na pewno usunąć zaznaczone (${selectedExercises.length}) ćwiczeń z katalogu?` : `Are you sure you want to remove selected (${selectedExercises.length}) exercises?`)) {
      selectedExercises.forEach(ex => {
        handleDeleteExercise(ex.id, ex.name, true);
      });
      setSelectedExercises([]);
      alert(lang === 'pl' ? '🗑️ Wybrane ćwiczenia zostały usunięte.' : '🗑️ Selected exercises deleted.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Modern Two-Column Exercise Builder Form */}
      <ExerciseFormBuilder 
        onSubmit={handleCreateExercise}
        isUploading={isUploadingCoachImage}
        lang={lang}
        isGlobal={false}
      />

      {/* UNIFIED & EMOJI-FREE EXERCISE LIBRARY CONTROL BAR */}
      <div className="bg-slate-900/60 border border-slate-800/90 rounded-2xl p-5 shadow-xl space-y-4">
        {/* Row 1: Title, Active Counter Badge & Select All Action */}
        <div className="flex justify-between items-center flex-wrap gap-4 border-b border-slate-800/80 pb-4">
          <div className="flex items-center gap-3">
            <h3 className="text-base md:text-lg font-black text-white uppercase tracking-wider m-0 flex items-center gap-2">
              <span className="material-symbols-outlined text-indigo-400 text-xl">folder_special</span>
              <span>{lang === 'pl' ? 'BIBLIOTEKA ĆWICZEŃ' : 'EXERCISE LIBRARY'}</span>
            </h3>
            <span className="bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 text-xs font-black px-3 py-1 rounded-full shadow-sm">
              {filteredExercises.length} {lang === 'pl' ? 'AKTYWNYCH' : 'ACTIVE'}
            </span>
          </div>

          <button
            type="button"
            onClick={handleSelectAll}
            className="bg-slate-950 hover:bg-slate-800 text-slate-200 border border-slate-800 hover:border-indigo-500/60 px-4 py-2 rounded-xl text-xs font-extrabold transition cursor-pointer shadow-md flex items-center gap-2 active:scale-95"
          >
            <span className="material-symbols-outlined text-sm text-indigo-400">check_box</span>
            <span>
              {filteredExercises.every(ex => selectedExercises.some(e => e.id === ex.id) || routineExercises.some(re => re.id === ex.id)) && filteredExercises.length > 0
                ? (lang === 'pl' ? 'Odznacz Wszystkie' : 'Deselect All')
                : (lang === 'pl' ? 'Zaznacz Wszystkie' : 'Select All Listed')}
            </span>
          </button>
        </div>

        {/* Row 2: Aligned 4-Column Search & Filters Panel */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
          {/* Column 1: Search Box */}
          <div className="md:col-span-4 relative">
            <span className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm pointer-events-none">
              search
            </span>
            <input 
              type="text" 
              placeholder={lang === 'pl' ? 'Szukaj ćwiczenia po nazwie, partii lub sprzęcie...' : 'Search movement by name, muscle, equipment...'} 
              value={searchFilter} 
              onChange={e => setSearchFilter(e.target.value)} 
              className="w-full bg-slate-950 border border-slate-800 text-white placeholder-slate-500 font-semibold text-xs rounded-xl pl-9 pr-3.5 py-3 outline-none focus:border-indigo-500 shadow-inner transition"
            />
          </div>

          {/* Column 2: Origin Filter (Global vs Custom) */}
          <div className="md:col-span-3 relative">
            <span className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-indigo-400 text-sm pointer-events-none">
              public
            </span>
            <select
              value={originFilter}
              onChange={e => setOriginFilter(e.target.value)}
              className="w-full bg-slate-950 border border-slate-800 text-indigo-300 font-extrabold text-xs rounded-xl pl-9 pr-3.5 py-3 outline-none focus:border-indigo-500 cursor-pointer shadow-inner transition appearance-none"
            >
              <option value="ALL">{lang === 'pl' ? 'Wszystkie Źródła' : 'All Sources'}</option>
              <option value="GLOBAL">{lang === 'pl' ? 'Tylko Standardowe' : 'Standard Only'}</option>
              <option value="INDIVIDUAL">{lang === 'pl' ? 'Tylko Autorskie' : 'Custom Only'}</option>
            </select>
          </div>

          {/* Column 3: Muscle Group Filter */}
          <div className="md:col-span-3 relative">
            <span className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">
              filter_alt
            </span>
            <select 
              value={bodyPartFilter} 
              onChange={e => setBodyPartFilter(e.target.value)} 
              className="w-full bg-slate-950 border border-slate-800 text-slate-200 font-extrabold text-xs rounded-xl pl-9 pr-3.5 py-3 outline-none focus:border-indigo-500 cursor-pointer shadow-inner transition appearance-none"
            >
              <option value="ALL">{lang === 'pl' ? 'Wszystkie Partie' : 'All Muscle Groups'}</option>
              <option value="chest">{lang === 'pl' ? 'Klatka piersiowa' : 'Chest'}</option>
              <option value="back">{lang === 'pl' ? 'Plecy' : 'Back'}</option>
              <option value="shoulders">{lang === 'pl' ? 'Barki' : 'Shoulders'}</option>
              <option value="upper arms">{lang === 'pl' ? 'Ramiona' : 'Upper Arms'}</option>
              <option value="lower arms">{lang === 'pl' ? 'Przedramiona' : 'Lower Arms'}</option>
              <option value="upper legs">{lang === 'pl' ? 'Uda i Pośladki' : 'Upper Legs / Glutes'}</option>
              <option value="lower legs">{lang === 'pl' ? 'Łydki' : 'Lower Legs'}</option>
              <option value="waist">{lang === 'pl' ? 'Brzuch / Pas' : 'Waist / Core'}</option>
              <option value="cardio">{lang === 'pl' ? 'Kardio' : 'Cardio'}</option>
              <option value="neck">{lang === 'pl' ? 'Szyja' : 'Neck'}</option>
            </select>
          </div>

          {/* Column 4: Equipment Filter */}
          <div className="md:col-span-2 relative">
            <span className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-indigo-400 text-sm pointer-events-none">
              fitness_center
            </span>
            <select 
              value={equipmentFilter} 
              onChange={e => setEquipmentFilter(e.target.value)} 
              className="w-full bg-slate-950 border border-slate-800 text-slate-200 font-extrabold text-xs rounded-xl pl-9 pr-3.5 py-3 outline-none focus:border-indigo-500 cursor-pointer shadow-inner transition appearance-none"
            >
              <option value="ALL">{lang === 'pl' ? 'Wszystkie Sprzęty' : 'All Equipment'}</option>
              <option value="body weight">{lang === 'pl' ? 'Ciężar ciała' : 'Body Weight'}</option>
              <option value="dumbbell">{lang === 'pl' ? 'Hantle' : 'Dumbbell'}</option>
              <option value="cable">{lang === 'pl' ? 'Wyciąg' : 'Cable'}</option>
              <option value="barbell">{lang === 'pl' ? 'Sztanga' : 'Barbell'}</option>
              <option value="leverage machine">{lang === 'pl' ? 'Maszyna' : 'Leverage Machine'}</option>
              <option value="band">{lang === 'pl' ? 'Guma oporowa' : 'Band'}</option>
              <option value="smith machine">{lang === 'pl' ? 'Suwnica Smitha' : 'Smith Machine'}</option>
              <option value="kettlebell">Kettlebell</option>
              <option value="weighted">{lang === 'pl' ? 'Z dociążeniem' : 'Weighted'}</option>
              <option value="stability ball">{lang === 'pl' ? 'Piłka gimnastyczna' : 'Stability Ball'}</option>
              <option value="ez barbell">{lang === 'pl' ? 'Sztanga łamana' : 'EZ Barbell'}</option>
              <option value="other">{lang === 'pl' ? 'Inny' : 'Other'}</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6 w-full mt-6">
        {filteredExercises.map((ex) => {
          const isChecked = selectedExercises.some(e => e.id === ex.id) || routineExercises.some(re => re.id === ex.id);
          const isEditing = editingExerciseId === ex.id;
          const isGlobal = ex.isGlobal || (ex.trainerId || "").toLowerCase() === 'global' || (ex.trainerId || "").toLowerCase() === 'all';

          if (isEditing) {
            return (
              <div key={ex.id} className="col-span-full my-4">
                <ExerciseFormBuilder 
                  initialData={ex}
                  isGlobal={isGlobal}
                  isEdit={true}
                  onSubmit={async (formData) => handleUpdateExercise(ex.id, ex, formData)}
                  onCancel={() => setEditingExerciseId(null)}
                  isUploading={isUploadingEditImage}
                  lang={lang}
                />
              </div>
            );
          }

          if (editingExerciseId === ex.id) {
            return (
              <div key={ex.id} className="col-span-full my-4">
                <ExerciseFormBuilder 
                  initialData={ex}
                  isGlobal={isGlobal}
                  isEdit={true}
                  onSubmit={async (formData) => handleUpdateExercise(ex.id, ex, formData)}
                  onCancel={() => setEditingExerciseId(null)}
                  isUploading={isUploadingEditImage}
                  lang={lang}
                />
              </div>
            );
          }

          return (
            <div 
              key={ex.id} 
              onClick={() => setViewingExerciseDetails(ex)}
              className={`flex flex-col justify-between p-5 rounded-2xl border transition-all duration-200 cursor-pointer select-none min-h-[180px] ${
                isChecked 
                  ? 'border-indigo-500 bg-indigo-500/10 shadow-[0_0_20px_rgba(99,102,241,0.2)] text-white' 
                  : 'border-slate-800 hover:border-slate-700 bg-slate-900/50 hover:bg-slate-900/80 shadow-lg'
              }`}
            >
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2 w-full border-b border-slate-800/60 pb-3 mb-1">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleSelectExercise(ex);
                      }}
                      title={lang === 'pl' ? "Zaznacz do nowej rutyny" : "Select for new routine"}
                      className={`w-5 h-5 rounded-md border flex items-center justify-center transition-all shrink-0 cursor-pointer ${
                        isChecked ? 'bg-indigo-600 border-indigo-400 text-white' : 'border-slate-700 bg-slate-950 hover:border-indigo-500'
                      }`}
                    >
                      {isChecked && <span className="text-xs font-extrabold">✓</span>}
                    </div>
                    <div className="min-w-0 truncate">
                      {getMuscleBadge(ex.bodyPart || ex.targetMuscle, t, lang)}
                    </div>
                  </div>

                  <div className="shrink-0" title={isGlobal ? (lang === 'pl' ? "Standardowe (Globalne)" : "Standard (Global)") : (lang === 'pl' ? "Indywidualne (Autorskie)" : "Custom (Individual)")}>
                    {isGlobal ? (
                      <span className="w-7 h-7 rounded-lg bg-rose-500/15 border border-rose-500/30 flex items-center justify-center text-rose-300 shadow-sm">
                        <span className="material-symbols-outlined text-sm">public</span>
                      </span>
                    ) : (
                      <span className="w-7 h-7 rounded-lg bg-purple-500/15 border border-purple-500/30 flex items-center justify-center text-purple-300 shadow-sm">
                        <span className="material-symbols-outlined text-sm">person</span>
                      </span>
                    )}
                  </div>
                </div>

                {(() => {
                  const mediaUrl = ex.imageUrl || ex.gif_url || ex.image || "";
                  const youtubeLink = ex.youtubeUrl || ex.videoUrl || "";
                  if (!mediaUrl && !youtubeLink) return null;

                  return (
                    <div className="my-1.5 flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      {mediaUrl && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            const imgList = Array.isArray(ex.images) && ex.images.length > 0
                              ? ex.images
                              : [mediaUrl, ex.gif_url, ex.image, ex.imageUrl2].filter(Boolean);

                            const uniqueImgs = Array.from(new Set(imgList));

                            setEnlargedImage({ 
                              url: mediaUrl,
                              imageUrl2: ex.imageUrl2,
                              images: uniqueImgs,
                              title: lang === 'pl' ? (ex.namePL || ex.name) : (ex.nameEN || ex.name) 
                            });
                          }}
                          className="inline-flex items-center gap-2 bg-slate-950 hover:bg-slate-800 text-indigo-300 hover:text-white border border-indigo-500/30 hover:border-indigo-500/60 p-1.5 pr-3 rounded-xl transition shadow-md cursor-pointer group active:scale-95"
                          title={lang === 'pl' ? "Kliknij, aby powiększyć zdjęcie/GIF" : "Click to enlarge picture/GIF"}
                        >
                          <div className="w-8 h-8 rounded-lg overflow-hidden border border-indigo-500/40 bg-slate-900 shrink-0 flex items-center justify-center relative">
                            <span className="material-symbols-outlined text-xs text-indigo-400">image</span>
                            <img 
                              src={mediaUrl} 
                              alt={ex.name} 
                              className="absolute inset-0 w-full h-full object-contain p-0.5 bg-slate-950/80 group-hover:scale-110 transition duration-200" 
                              onError={(ev) => { ev.target.style.display = 'none'; }}
                            />
                          </div>
                          <span className="text-[11px] font-extrabold flex items-center gap-1.5">
                            <span className="material-symbols-outlined text-xs text-indigo-400 group-hover:scale-110 transition">visibility</span>
                            <span>{lang === 'pl' ? "Pokaż Zdjęcie / GIF" : "View Media"}</span>
                          </span>
                        </button>
                      )}

                    {(ex.youtubeUrl || ex.videoUrl) && (
                      <a
                        href={ex.youtubeUrl || ex.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1.5 bg-red-950/80 hover:bg-red-900/90 text-red-300 hover:text-white border border-red-500/40 px-3 py-2 rounded-xl text-[11px] font-extrabold transition shadow-md active:scale-95 cursor-pointer"
                        title="Watch YouTube Video Instruction"
                      >
                        <span className="material-symbols-outlined text-sm text-red-400">play_circle</span>
                        <span>YouTube</span>
                      </a>
                    )}
                    </div>
                  );
                })()}

                <div>
                  <h4 className="text-base font-extrabold text-slate-100 tracking-wide leading-snug break-words m-0">
                    {lang === 'pl' ? (ex.namePL || ex.name) : (ex.nameEN || ex.name)}
                  </h4>
                  {(ex.hintsPL || ex.hintsEN || ex.hints) && (
                    <p className="text-xs text-slate-400 italic mt-1.5 m-0 leading-relaxed font-medium line-clamp-2">
                      {lang === 'pl' ? (ex.hintsPL || ex.hints || ex.notes) : (ex.hintsEN || ex.hints || ex.notes)}
                    </p>
                  )}
                </div>
              </div>

              {/* Action Bar for ALL Exercises with Perfectly Aligned 3-Button Grid */}
              <div 
                className="grid grid-cols-3 gap-1.5 w-full mt-4 pt-3 border-t border-slate-800/80 items-center"
                onClick={(e) => e.stopPropagation()} 
              >
                <button 
                  type="button"
                  onClick={() => setViewingExerciseDetails(ex)}
                  className="w-full py-2 px-1 rounded-xl text-[11px] font-black text-indigo-300 hover:text-white transition bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/25 cursor-pointer flex items-center justify-center gap-1 shadow-sm active:scale-95 text-center"
                  title={lang === 'pl' ? "Zobacz Pełne Szczegóły Ćwiczenia" : "View Full Movement Details"}
                >
                  <span className="material-symbols-outlined text-xs">info</span>
                  <span>{lang === 'pl' ? 'Szczegóły' : 'Details'}</span>
                </button>

                {!isGlobal ? (
                  <>
                    <button 
                      type="button"
                      onClick={() => setEditingExerciseId(ex.id)}
                      className="w-full py-2 px-1 rounded-xl text-[11px] font-black text-sky-400 hover:text-sky-300 transition bg-sky-500/10 hover:bg-sky-500/20 border border-sky-500/20 cursor-pointer flex items-center justify-center gap-1 shadow-sm active:scale-95 text-center"
                    >
                      <span className="material-symbols-outlined text-xs">edit</span>
                      <span>{t('common.edit')}</span>
                    </button>
                    <button 
                      type="button"
                      onClick={() => handleDeleteExercise(ex.id, ex.name)}
                      className="w-full py-2 px-1 rounded-xl text-[11px] font-black text-rose-400 hover:text-rose-300 transition bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/20 cursor-pointer flex items-center justify-center gap-1 shadow-sm active:scale-95 text-center"
                    >
                      <span className="material-symbols-outlined text-xs">delete</span>
                      <span>{lang === 'pl' ? 'Usuń' : 'Delete'}</span>
                    </button>
                  </>
                ) : (
                  <div className="col-span-2" />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* EXERCISE DETAILS MODAL OVERLAY */}
      {viewingExerciseDetails && (
        <div 
          className="fixed inset-0 z-[99999] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 transition-all duration-200"
          onClick={() => setViewingExerciseDetails(null)}
        >
          <div 
            className="relative max-w-2xl max-h-[92vh] bg-slate-900 border border-slate-700/90 rounded-2xl overflow-y-auto shadow-2xl p-5 md:p-6 flex flex-col gap-5 w-full z-[100000] scrollbar-thin scrollbar-thumb-slate-700"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-start justify-between border-b border-slate-800 pb-4">
              <div className="space-y-1 min-w-0 pr-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full border ${
                    (viewingExerciseDetails.isGlobal || viewingExerciseDetails.trainerId === 'global' || viewingExerciseDetails.trainerId === 'all')
                      ? 'bg-rose-500/15 border-rose-500/30 text-rose-300'
                      : 'bg-purple-500/15 border-purple-500/30 text-purple-300'
                  }`}>
                    {(viewingExerciseDetails.isGlobal || viewingExerciseDetails.trainerId === 'global' || viewingExerciseDetails.trainerId === 'all')
                      ? (lang === 'pl' ? 'Standardowe (Globalne)' : 'Standard (Global)')
                      : (lang === 'pl' ? 'Autorskie (Indywidualne)' : 'Custom (Individual)')}
                  </span>
                  {(viewingExerciseDetails.bodyPart || viewingExerciseDetails.category) && (
                    <span className="bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 text-[10px] font-black px-2.5 py-0.5 rounded-full uppercase">
                      {translateTerm(viewingExerciseDetails.bodyPart || viewingExerciseDetails.category, lang)}
                    </span>
                  )}
                </div>
                <h3 className="text-lg md:text-xl font-black text-white m-0 tracking-wide">
                  {lang === 'pl' ? (viewingExerciseDetails.namePL || viewingExerciseDetails.name) : (viewingExerciseDetails.nameEN || viewingExerciseDetails.name)}
                </h3>
                {(viewingExerciseDetails.namePL && viewingExerciseDetails.nameEN && viewingExerciseDetails.namePL !== viewingExerciseDetails.nameEN) && (
                  <p className="text-xs text-slate-400 font-medium m-0">
                    {lang === 'pl' ? `EN: ${viewingExerciseDetails.nameEN}` : `PL: ${viewingExerciseDetails.namePL}`}
                  </p>
                )}
              </div>

              <button
                type="button"
                onClick={() => setViewingExerciseDetails(null)}
                className="w-9 h-9 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white flex items-center justify-center cursor-pointer transition border border-slate-700 shrink-0 active:scale-95 shadow"
              >
                <span className="material-symbols-outlined text-lg">close</span>
              </button>
            </div>

            {/* Media Preview Hero */}
            {(() => {
              const mediaUrl = viewingExerciseDetails.imageUrl || viewingExerciseDetails.gif_url || viewingExerciseDetails.image || "";
              const youtubeLink = viewingExerciseDetails.youtubeUrl || viewingExerciseDetails.videoUrl || "";
              if (!mediaUrl && !youtubeLink) return null;

              return (
                <div className="bg-slate-950 p-3 rounded-xl border border-slate-800 flex flex-col items-center gap-3">
                  {mediaUrl && (
                    <div 
                      className="relative w-full max-h-[300px] flex items-center justify-center bg-black/60 rounded-lg overflow-hidden border border-slate-800/80 cursor-pointer group"
                      onClick={() => {
                        const imgList = Array.isArray(viewingExerciseDetails.images) && viewingExerciseDetails.images.length > 0
                          ? viewingExerciseDetails.images
                          : [mediaUrl, viewingExerciseDetails.gif_url, viewingExerciseDetails.image, viewingExerciseDetails.imageUrl2].filter(Boolean);
                        setEnlargedImage({
                          url: mediaUrl,
                          imageUrl2: viewingExerciseDetails.imageUrl2,
                          images: Array.from(new Set(imgList)),
                          title: lang === 'pl' ? (viewingExerciseDetails.namePL || viewingExerciseDetails.name) : (viewingExerciseDetails.nameEN || viewingExerciseDetails.name)
                        });
                      }}
                    >
                      <img 
                        src={mediaUrl} 
                        alt={viewingExerciseDetails.name} 
                        className="max-w-full max-h-[280px] object-contain transition duration-200 group-hover:scale-105" 
                      />
                      <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-1.5 text-xs font-black text-white">
                        <span className="material-symbols-outlined text-sm">fullscreen</span>
                        <span>{lang === 'pl' ? 'Powiększ Media (GIF / Zdjęcie)' : 'Enlarge Media (GIF / Image)'}</span>
                      </div>
                    </div>
                  )}

                  {youtubeLink && (
                    <a
                      href={youtubeLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center justify-center gap-2 bg-red-950/80 hover:bg-red-900 text-red-300 hover:text-white border border-red-500/40 py-2.5 px-4 rounded-xl text-xs font-black transition cursor-pointer active:scale-95 shadow"
                    >
                      <span className="material-symbols-outlined text-sm text-red-400">play_circle</span>
                      <span>{lang === 'pl' ? 'Otwórz Wideo Instruktażowe YouTube' : 'Watch YouTube Video Instruction'}</span>
                      <span className="material-symbols-outlined text-xs">open_in_new</span>
                    </a>
                  )}
                </div>
              );
            })()}

            {/* Grid 1: Basic Specifications */}
            <div className="bg-slate-950/70 p-4 rounded-xl border border-slate-800/80 space-y-3">
              <h4 className="text-xs font-black text-indigo-400 uppercase tracking-wider m-0 border-b border-slate-800/60 pb-1.5 flex items-center gap-1.5">
                <span className="material-symbols-outlined text-sm">badge</span>
                <span>{lang === 'pl' ? 'Specyfikacja Ćwiczenia' : 'Exercise Specifications'}</span>
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div>
                  <span className="block text-[10px] text-slate-400 font-bold uppercase mb-0.5">{lang === 'pl' ? 'Partia Ciała' : 'Body Part'}</span>
                  <span className="text-xs font-black text-white capitalize">
                    {translateTerm(viewingExerciseDetails.bodyPart || viewingExerciseDetails.category, lang)}
                  </span>
                </div>

                <div>
                  <span className="block text-[10px] text-slate-400 font-bold uppercase mb-0.5">{lang === 'pl' ? 'Sprzęt' : 'Equipment'}</span>
                  <span className="text-xs font-black text-slate-200 capitalize">
                    {translateTerm(viewingExerciseDetails.equipment, lang)}
                  </span>
                </div>

                <div>
                  <span className="block text-[10px] text-slate-400 font-bold uppercase mb-0.5">{lang === 'pl' ? 'Podpartia Ciała (Detal)' : 'Sub Part (Detail)'}</span>
                  <span className="text-xs font-bold text-slate-300 capitalize">
                    {translateTerm(viewingExerciseDetails.subPart || viewingExerciseDetails.sub_part, lang)}
                  </span>
                </div>
              </div>
            </div>

            {/* Grid 2: Anatomy & Muscle Engagement */}
            <div className="bg-slate-950/70 p-4 rounded-xl border border-slate-800/80 space-y-3">
              <h4 className="text-xs font-black text-indigo-400 uppercase tracking-wider m-0 border-b border-slate-800/60 pb-1.5 flex items-center gap-1.5">
                <span className="material-symbols-outlined text-sm">fitness_center</span>
                <span>{lang === 'pl' ? 'Anatomia i Zaangażowane Mięśnie' : 'Anatomy & Muscle Engagement'}</span>
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <span className="block text-[10px] text-slate-400 font-bold uppercase mb-0.5">{lang === 'pl' ? 'Główny Mięsień Docelowy' : 'Primary Target Muscle'}</span>
                  <span className="text-xs font-black text-emerald-400 capitalize">
                    {translateTerm(viewingExerciseDetails.target || viewingExerciseDetails.targetMuscle, lang)}
                  </span>
                </div>

                <div>
                  <span className="block text-[10px] text-slate-400 font-bold uppercase mb-0.5">{lang === 'pl' ? 'Grupa Synergistyczna' : 'Synergist Group'}</span>
                  <span className="text-xs font-bold text-slate-200 capitalize">
                    {translateTerm(viewingExerciseDetails.muscle_group || viewingExerciseDetails.muscleGroup, lang)}
                  </span>
                </div>

                <div>
                  <span className="block text-[10px] text-slate-400 font-bold uppercase mb-0.5">{lang === 'pl' ? 'Mięśnie Wspomagające' : 'Secondary Muscles'}</span>
                  <span className="text-xs font-semibold text-slate-300 capitalize">
                    {translateTerm(
                      Array.isArray(viewingExerciseDetails.secondary_muscles) 
                        ? viewingExerciseDetails.secondary_muscles.join(', ') 
                        : (viewingExerciseDetails.secondary_muscles || viewingExerciseDetails.secondaryMuscles),
                      lang
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* Section 3: Execution Instructions & Hints */}
            {(viewingExerciseDetails.hintsPL || viewingExerciseDetails.hintsEN || viewingExerciseDetails.hints || viewingExerciseDetails.instructions) && (
              <div className="bg-slate-950/70 p-4 rounded-xl border border-slate-800/80 space-y-2">
                <h4 className="text-xs font-black text-indigo-400 uppercase tracking-wider m-0 border-b border-slate-800/60 pb-1.5 flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-sm">description</span>
                  <span>{lang === 'pl' ? 'Instrukcja Wykonania i Wskazówki' : 'Execution Instructions & Hints'}</span>
                </h4>
                <p className="text-xs text-slate-200 font-medium leading-relaxed m-0 whitespace-pre-line">
                  {lang === 'pl' 
                    ? (viewingExerciseDetails.hintsPL || (typeof viewingExerciseDetails.instructions === 'object' ? viewingExerciseDetails.instructions?.pl : viewingExerciseDetails.instructions) || viewingExerciseDetails.hints || viewingExerciseDetails.notes) 
                    : (viewingExerciseDetails.hintsEN || (typeof viewingExerciseDetails.instructions === 'object' ? viewingExerciseDetails.instructions?.en : viewingExerciseDetails.instructions) || viewingExerciseDetails.hints || viewingExerciseDetails.notes)}
                </p>
              </div>
            )}

            {/* Footer Controls */}
            <div className="flex items-center justify-between gap-3 pt-3 border-t border-slate-800">
              <button
                type="button"
                onClick={() => {
                  handleAddExerciseToRoutine(viewingExerciseDetails);
                }}
                className={`px-4 py-2.5 rounded-xl text-xs font-black cursor-pointer transition shadow flex items-center gap-1.5 active:scale-95 ${
                  (selectedExercises.some(e => e.id === viewingExerciseDetails.id) || routineExercises.some(re => re.id === viewingExerciseDetails.id))
                    ? 'bg-amber-500 hover:bg-amber-400 text-slate-950'
                    : 'bg-indigo-600 hover:bg-indigo-500 text-white'
                }`}
              >
                <span className="material-symbols-outlined text-sm">
                  {(selectedExercises.some(e => e.id === viewingExerciseDetails.id) || routineExercises.some(re => re.id === viewingExerciseDetails.id)) ? 'check_box' : 'add'}
                </span>
                <span>
                  {(selectedExercises.some(e => e.id === viewingExerciseDetails.id) || routineExercises.some(re => re.id === viewingExerciseDetails.id))
                    ? (lang === 'pl' ? 'Zaznaczone (Kliknij, aby odznaczyć)' : 'Selected (Click to deselect)')
                    : (lang === 'pl' ? 'Zaznacz dla Nowej Rutyny' : 'Select for New Routine')}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setViewingExerciseDetails(null)}
                className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-5 py-2.5 rounded-xl text-xs font-bold cursor-pointer transition"
              >
                {lang === 'pl' ? 'Zamknij' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    

</div>
  );
};

const RenderClientRoster = ({ 
  clients, 
  archivedTrainees,
  selectedRosterTrainee, setSelectedRosterTrainee,
  isArchiveListExpanded, setIsArchiveListExpanded,
  handleArchiveTrainee,
  handleDisconnectTrainee,
  handleChangeTraineePassword,
  handleRestoreTrainee,
  createdRoutines, 
  handleDeleteRoutine, 
  handleReassignRoutine,
  handleUpdateRoutine,
  createdPlans,
  handleDeletePlan,
  handleUpdatePlan,
  exercises = [],
  branding = { color: "#6366f1" },
  trainerId = "",
  trainerEmail = "",
  isMarek = false,
  setActiveTab,
  setSelectedTrainee
}) => {
  const { t, lang } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('ALL');

  // Editing state for Multi-Day Training Plans
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [editPlanTitle, setEditPlanTitle] = useState("");
  const [editPlanNotes, setEditPlanNotes] = useState("");
  const [editPlanRoutines, setEditPlanRoutines] = useState([]); // Dynamic array for editing days/routines
  const [editPlanAssignedTo, setEditPlanAssignedTo] = useState("");
  const [editPlanAssignedToName, setEditPlanAssignedToName] = useState("");

  const routines = createdRoutines || [];

  const handleStartEditPlan = (plan) => {
    setEditingPlanId(plan.id);
    setEditPlanTitle(plan.title || "");
    setEditPlanNotes(plan.notes || "");
    setEditPlanRoutines(plan.routines || [{ dayNumber: 1, dayLabel: lang === 'pl' ? "Dzień 1" : "Day 1", routineId: "", routineTitle: "" }]);
    
    // Load current assignment data
    const currAssign = (plan.assignedTo || "").toString();
    setEditPlanAssignedTo(currAssign.toLowerCase() === 'global' ? 'ALL' : currAssign);
    setEditPlanAssignedToName(plan.assignedToName || "");
  };

  const handleSaveEditPlan = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!editingPlanId || !editPlanTitle || !editPlanAssignedTo || editPlanRoutines.some(d => !d.routineId)) {
      alert(lang === 'pl' ? "Proszę wpisać nazwę planu, przypisać podopiecznego i przypisać szablony do każdego dnia." : "Please provide plan name, assign a trainee, and select routine templates for each day.");
      return;
    }

    try {
      if (handleUpdatePlan) {
        handleUpdatePlan(editingPlanId, editPlanTitle.trim(), editPlanNotes.trim(), editPlanRoutines, editPlanAssignedTo, editPlanAssignedToName);
      } else {
        const cleanAssign = editPlanAssignedTo.toLowerCase().trim();
        const planRef = doc(db, "plans", editingPlanId);
        await updateDoc(planRef, {
          title: editPlanTitle,
          notes: editPlanNotes,
          assignedTo: cleanAssign === 'all' ? 'global' : cleanAssign,
          assignedClientId: cleanAssign === 'all' ? 'global' : cleanAssign,
          assignedToName: editPlanAssignedToName,
          routines: editPlanRoutines,
          trainerId: isMarek ? "marekt@gmail.com" : (userProfile?.id || trainerId || "marekt@gmail.com")
        });
        alert(lang === 'pl' ? "Zmiany w planie oraz przypisanie podopiecznego zostały zapisane!" : "Plan modifications and trainee assignment saved successfully!");
      }
      setEditingPlanId(null);
    } catch (error) {
      console.error("Failed to save updated plan:", error);
      alert(lang === 'pl' ? "Wystąpił błąd podczas zapisywania zmian w planie." : "An error occurred while saving plan changes.");
    }
  };

  // Editing & Reordering state for Routines
  const [editingRoutineId, setEditingRoutineId] = useState(null);
  const [editingRoutineTitle, setEditingRoutineTitle] = useState("");
  const [editingRoutineExercises, setEditingRoutineExercises] = useState([]);
  const [editingRoutineBodyPartFilter, setEditingRoutineBodyPartFilter] = useState('ALL');
  const [editingRoutineSourceFilter, setEditingRoutineSourceFilter] = useState('ALL'); // 'ALL' | 'GLOBAL' | 'TRAINER'
  const [editingRoutineSearchQuery, setEditingRoutineSearchQuery] = useState('');
  const [selectedRoutineFilter, setSelectedRoutineFilter] = useState(null);
  const [selectedTagFilter, setSelectedTagFilter] = useState('ALL');
  const [editingRoutineTag, setEditingRoutineTag] = useState('FBW');
  const [editingRoutineCustomTag, setEditingRoutineCustomTag] = useState('');

  const handleMoveExerciseUp = (index) => {
    if (index <= 0) return;
    const newArr = [...editingRoutineExercises];
    const temp = newArr[index - 1];
    newArr[index - 1] = newArr[index];
    newArr[index] = temp;
    setEditingRoutineExercises(newArr);
  };

  const handleMoveExerciseDown = (index) => {
    if (index >= editingRoutineExercises.length - 1) return;
    const newArr = [...editingRoutineExercises];
    const temp = newArr[index + 1];
    newArr[index + 1] = newArr[index];
    newArr[index] = temp;
    setEditingRoutineExercises(newArr);
  };

  const handleRemoveExerciseFromRoutineEdit = (index) => {
    setEditingRoutineExercises(prev => prev.filter((_, idx) => idx !== index));
  };

  const uniqueActiveTrainees = (clients || []).filter((trainee, index, self) => 
    self.findIndex(t => (t.email || t.id)?.toLowerCase().trim() === (trainee.email || trainee.id)?.toLowerCase().trim()) === index
  );

  const matchTrainee = (assignedVal, traineeObj) => {
    if (!assignedVal || !traineeObj) return false;
    const val = assignedVal.toString().toLowerCase().trim();
    const tId = (traineeObj.id || "").toString().toLowerCase().trim();
    const tEmail = (traineeObj.email || "").toString().toLowerCase().trim();
    return val === tId || (tEmail !== "" && val === tEmail);
  };

  const checkIsGlobal = (obj) => {
    if (!obj) return false;
    const val = (obj.assignedTo !== undefined && obj.assignedTo !== null && obj.assignedTo !== "" ? obj.assignedTo : (obj.assignedClientId || "")).toString().trim().toLowerCase();
    const tId = (obj.trainerId || "").toString().trim().toLowerCase();
    return val === 'global' || tId === 'global' || obj.isGlobal === true;
  };

  const filteredRoutines = (createdRoutines || []).filter(rt => {
    // If routine filter is active, filter strictly to show only the selected routine!
    if (selectedRoutineFilter) {
      const targetId = typeof selectedRoutineFilter === 'object' ? selectedRoutineFilter.routineId : selectedRoutineFilter;
      const targetTitle = typeof selectedRoutineFilter === 'object' ? selectedRoutineFilter.routineTitle : selectedRoutineFilter;
      const isMatch = (targetId && (rt.id === targetId || rt.docId === targetId)) ||
                      (targetTitle && (rt.title === targetTitle || rt.title?.trim() === targetTitle?.trim()));
      if (!isMatch) return false;
    }

    // Filter by Tag (#)
    if (selectedTagFilter && selectedTagFilter !== 'ALL') {
      const rtTag = (rt.tag || "").toLowerCase().trim();
      if (rtTag !== selectedTagFilter.toLowerCase().trim()) return false;
    }

    // Filter by Trainee / Recipient filter from dropdown
    if (filterType === 'ALL_TRAINEES') {
      const assigned = (rt.assignedClientId || rt.assignedTo || "").toString().toUpperCase();
      if (assigned !== 'ALL' && assigned !== 'ALL_TRAINEES') return false;
    } else if (filterType !== 'ALL') {
      // Specific Trainee selected by ID or email
      const targetTrainee = uniqueActiveTrainees.find(c => c.id === filterType || (c.email && c.email.toLowerCase() === filterType.toLowerCase()));
      if (targetTrainee && !matchTrainee(rt.assignedClientId || rt.assignedTo, targetTrainee)) {
        return false;
      }
    }

    const isGlobal = checkIsGlobal(rt);
    
    if (selectedRosterTrainee && !isGlobal && !matchTrainee(rt.assignedClientId || rt.assignedTo, selectedRosterTrainee)) {
      return false;
    }

    const traineeObj = isGlobal ? null : uniqueActiveTrainees.find(c => matchTrainee(rt.assignedClientId || rt.assignedTo, c));
    const matchesSearch = (rt.title || "").toLowerCase().includes(searchQuery.toLowerCase()) || 
                          (rt.tag || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (traineeObj && (traineeObj.name || "").toLowerCase().includes(searchQuery.toLowerCase())) ||
                          (isGlobal && "global all trainees wszyscy".includes(searchQuery.toLowerCase()));
    if (!matchesSearch) return false;
    return true;
  });

  const handleClearFilter = () => {
    setSelectedRosterTrainee(null);
    setSelectedRoutineFilter(null);
    setSelectedTagFilter('ALL');
  };

  // Filter plans to display:
  const filteredPlans = (createdPlans || []).filter(plan => {
    // If no trainee is selected, display ALL of the coach's plans + global templates
    if (!selectedRosterTrainee) return true;
    
    // If a trainee is selected, filter strictly by their assigned email/ID or global status
    return matchTrainee(plan.assignedTo || plan.assignedClientId, selectedRosterTrainee) || checkIsGlobal(plan);
  });

  // Calculate dynamic counts directly from state arrays
  const totalTraineesCount = uniqueActiveTrainees.length; // Active roster size
  const plansList = createdPlans || [];
  const routinesList = createdRoutines || [];

  // Plans metrics:
  const dedicatedPlansCount = plansList.filter(p => !checkIsGlobal(p)).length;
  const globalPlansCount = plansList.filter(p => checkIsGlobal(p)).length;

  // Routines (Szablony) metrics:
  const dedicatedRoutinesCount = routinesList.filter(r => !checkIsGlobal(r)).length;
  const globalRoutinesCount = routinesList.filter(r => checkIsGlobal(r)).length;

  return (
    <div className="w-full max-w-7xl mx-auto space-y-8">
      <div className="border-b border-slate-700 pb-4 flex flex-wrap gap-4 justify-between items-end">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="material-symbols-outlined text-3xl text-indigo-400">group</span>
            <h2 className="text-2xl font-black text-white tracking-tight leading-none">
              {t('admin.headers.title', t('clientRoster.title', 'Lista Podopiecznych i Katalog Programów'))}
            </h2>
          </div>
          <p className="text-slate-400 text-sm md:text-base mt-1.5 m-0 font-medium">{t('admin.headers.subtitle', t('clientRoster.subtitle', 'Monitoruj, wyszukuj i dynamicznie przepisuj aktywne plany treningowe swoim podopiecznym.'))}</p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {archivedTrainees.length > 0 && (
            <button
              onClick={() => setIsArchiveListExpanded(!isArchiveListExpanded)}
              className="bg-slate-800 hover:bg-slate-700 text-amber-300 border border-amber-500/40 font-extrabold px-4 py-2.5 rounded-xl text-xs transition cursor-pointer flex items-center gap-2 shadow-sm"
            >
              <span className="material-symbols-outlined text-base">archive</span>
              <span>{t('common.archived')} ({archivedTrainees.length})</span>
            </button>
          )}
        </div>
      </div>

      {isArchiveListExpanded && archivedTrainees.length > 0 && (
        <div className="bg-slate-900/90 border border-amber-500/40 p-6 rounded-2xl shadow-xl space-y-4 animate-fade-in">
          <div className="flex justify-between items-center border-b border-slate-800 pb-3">
            <h4 className="text-sm font-black text-amber-300 uppercase tracking-wider m-0 flex items-center gap-2">
              <span className="material-symbols-outlined">archive</span> {t('clientRoster.archivedHeader')} ({archivedTrainees.length})
            </h4>
            <span className="text-xs text-slate-400 font-medium">{lang === 'pl' ? "Logi treningowe pozostają bezpiecznym archiwum." : "Workout logs remain stored safely."}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {archivedTrainees.map(arch => (
              <div key={arch.id} className="bg-slate-950 p-4 rounded-xl border border-slate-800 flex items-center justify-between gap-3 shadow-inner">
                <div className="min-w-0 flex-1">
                  <strong className="text-slate-300 font-extrabold block truncate text-base">{arch.name || arch.displayName}</strong>
                  <span className="text-xs text-amber-400/80 block mt-0.5 truncate font-semibold">Status: {t('common.archived')}</span>
                </div>
                <button
                  onClick={() => handleRestoreTrainee(arch)}
                  className="bg-slate-800 hover:bg-emerald-600 text-emerald-400 hover:text-white border border-slate-700 hover:border-emerald-500 px-3 py-1.5 rounded-lg text-xs font-black transition cursor-pointer shrink-0 shadow-sm flex items-center gap-1"
                  title="Restore to Active Roster"
                >
                  <span className="material-symbols-outlined text-xs">restore</span>
                  <span>{t('clientRoster.restoreBtn')}</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div className="flex justify-between items-center flex-wrap gap-2">
          <h3 className="text-xs font-black uppercase tracking-wider text-slate-400 m-0 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse inline-block" />
            <span>{t('clientRoster.metricActive')} ({uniqueActiveTrainees.length})</span> — <span className="text-emerald-400 font-semibold">{t('admin.headers.filterPrompt', lang === 'pl' ? 'Kliknij kartę zawodnika, aby filtrować przypisane mu programy!' : 'Click on a trainee card to filter assigned programs!')}</span>
          </h3>
          {selectedRosterTrainee && (
            <button
              type="button"
              onClick={handleClearFilter}
              className="text-xs font-bold text-red-400 hover:text-red-300 flex items-center gap-1.5 transition cursor-pointer bg-transparent border-0 p-0"
            >
              <span className="material-symbols-outlined text-xs">close</span>
              <span>{t('admin.buttons.showAll', lang === 'pl' ? 'Pokaż wszystkich zawodników' : 'Show all trainees')}</span>
            </button>
          )}
        </div>
        
        {uniqueActiveTrainees.length === 0 ? (
          <div className="p-12 text-center bg-slate-900/40 border border-dashed border-slate-800 rounded-2xl text-slate-500 font-semibold text-xs italic">
            {lang === 'pl' ? "Brak aktywnych podopiecznych przypisanych w bazie danych Firestore. Skontaktuj się z Super Adminem w celu dodania podopiecznych!" : "No active athletes currently assigned to your roster in Firestore. Super Admin provisions and pairs your athletes!"}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
            {uniqueActiveTrainees.map(client => {
              const isSelected = selectedRosterTrainee && (selectedRosterTrainee.id === client.id || selectedRosterTrainee.email === client.email);
              const isFemale = client.gender === 'female';

              return (
                <div 
                  key={client.id}
                  style={isSelected ? { borderColor: branding.color, boxShadow: `0 0 15px ${branding.color}20` } : {}}
                  className={`border rounded-3xl p-5 transition-all duration-200 flex flex-col justify-between min-h-[190px] relative shadow-lg ${
                    isSelected 
                      ? 'border-indigo-500 ring-1 ring-indigo-500/30 bg-gradient-to-br from-indigo-950/50 via-slate-900 to-slate-900' 
                      : 'bg-slate-900/80 border-slate-700/80 hover:border-slate-500 hover:bg-slate-900 text-slate-200'
                  }`}
                >
                  
                  {/* TOP LAYER: PROFILE HEADER */}
                  <div className="flex items-start gap-4 cursor-pointer" onClick={() => setSelectedRosterTrainee(isSelected ? null : client)}>
                    {/* Avatar with Custom Brand Ring on Active Selection */}
                    <div 
                      style={isSelected ? { borderColor: branding.color } : {}}
                      className={`w-14 h-14 rounded-full border-2 overflow-hidden shrink-0 shadow-md ${
                        isSelected ? 'border-indigo-500' : (isFemale ? 'border-pink-500/50' : 'border-sky-500/50')
                      }`}
                    >
                      {(client.email || "").toLowerCase().includes('kinga') || (client.name || client.displayName || "").toLowerCase().includes('kinga') || (client.name || client.displayName || "").toLowerCase().includes('testowa') ? (
                        <img src="/kinga_avatar.jpg" alt={client.displayName || client.name} className="w-full h-full object-cover" />
                      ) : (client.email || "").toLowerCase().includes('monika') || (client.name || client.displayName || "").toLowerCase().includes('monika') ? (
                        <img src="/monika_avatar.jpg?v=1" alt={client.displayName || client.name} className="w-full h-full object-cover" />
                      ) : (client.email || "").toLowerCase().includes('cuker') || (client.email || "").toLowerCase().includes('mateusz') || client.photoURL || client.avatarUrl ? (
                        <img src={(client.photoURL || client.avatarUrl || "/mateusz_avatar.png").replace("/mateusz_avatar.png", "/mateusz_avatar.png?v=2")} alt={client.displayName || client.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-slate-900 flex items-center justify-center text-xl text-slate-300">
                          <span className="material-symbols-outlined">{isFemale ? 'female' : 'male'}</span>
                        </div>
                      )}
                    </div>

                    {/* Name & Contact Stack (No clipping, clean line-breaks) */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-black text-white tracking-tight truncate m-0">
                          {client.name || client.displayName || "Athlete"}
                        </h3>
                        {/* Pulse Indicator */}
                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shrink-0" title="Active athlete" />
                      </div>
                      <p className="text-xs text-slate-300 font-bold font-mono break-all whitespace-normal mt-0.5 m-0">
                        {client.email}
                      </p>

                      {/* Badges Container (Middle Row) */}
                      <div className="flex flex-wrap items-center gap-1.5 mt-3">
                        {/* Gender Badge */}
                        {client.gender === 'male' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-lg bg-blue-500/10 border border-blue-500/20 text-[9px] font-extrabold text-blue-400 tracking-wider uppercase select-none">
                            <span className="material-symbols-outlined text-[10px] mr-1">male</span>
                            {lang === 'pl' ? 'Mężczyzna' : 'Male'}
                          </span>
                        )}
                        {client.gender === 'female' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-lg bg-pink-500/10 border border-pink-500/20 text-[9px] font-extrabold text-pink-400 tracking-wider uppercase select-none">
                            <span className="material-symbols-outlined text-[10px] mr-1">female</span>
                            {lang === 'pl' ? 'Kobieta' : 'Female'}
                          </span>
                        )}
                        {!client.gender && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-lg bg-slate-800 border border-slate-700 text-[9px] font-extrabold text-slate-300 tracking-wider uppercase select-none">
                            <span className="material-symbols-outlined text-[10px] mr-1">person</span>
                            {lang === 'pl' ? 'Zawodnik' : 'Athlete'}
                          </span>
                        )}

                        {/* Active Filter Badge */}
                        {isSelected && (
                          <span 
                            style={{ backgroundColor: `${branding.color}15`, borderColor: `${branding.color}40`, color: branding.color }}
                            className="inline-flex items-center px-2 py-0.5 rounded-lg border text-[9px] font-extrabold tracking-wider uppercase select-none shadow-sm"
                          >
                            <span className="material-symbols-outlined text-[10px] mr-1">filter_alt</span>
                            {lang === 'pl' ? 'Filtr Aktywny' : 'Active Filter'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* BOTTOM LAYER: CARD ACTIONS (Perfectly Aligned Footer Row) */}
                  <div className="border-t border-slate-800/80 pt-4 mt-4 flex items-center justify-between gap-3 flex-wrap">
                    
                    {/* Quick status description */}
                    <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">
                      {lang === 'pl' ? 'Aktywny podopieczny' : 'Active Athlete'}
                    </span>

                    {/* Actions group aligned to the right */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Direct Live Monitor Link */}
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          if (setSelectedTrainee) setSelectedTrainee(client);
                          if (setActiveTab) setActiveTab('live');
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-400 border border-emerald-500/40 hover:border-emerald-400 text-[11px] font-extrabold h-9 px-3.5 rounded-xl transition duration-150 flex items-center gap-1.5 cursor-pointer shadow-sm active:scale-95"
                        title={lang === 'pl' ? "Przejdź bezpośrednio do Monitora Na Żywo dla tego zawodnika" : "Go directly to Live Tracker for this athlete"}
                      >
                        <span className="material-symbols-outlined text-[14px] text-emerald-400 animate-pulse">sensors</span>
                        <span>{lang === 'pl' ? "Monitor na Żywo" : "Live Monitor"}</span>
                      </button>

                      {/* Disconnect Trainee */}
                      {handleDisconnectTrainee && (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDisconnectTrainee(client);
                          }}
                          className="bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/40 hover:border-amber-400 text-[11px] font-extrabold h-9 px-3.5 rounded-xl transition duration-150 flex items-center gap-1.5 cursor-pointer shadow-sm active:scale-95"
                          title={lang === 'pl' ? "Odłącz tego zawodnika od swojego konta trenerskiego" : "Disconnect this trainee from your coaching roster"}
                        >
                          <span className="material-symbols-outlined text-[14px] text-amber-400">link_off</span>
                          <span>{lang === 'pl' ? "Odłącz" : "Disconnect"}</span>
                        </button>
                      )}

                      {/* Archive Trainee */}
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleArchiveTrainee(client);
                        }}
                        className="bg-slate-950 hover:bg-red-500/10 text-slate-400 hover:text-red-400 border border-slate-800 hover:border-red-500/20 text-[11px] font-extrabold h-9 px-3.5 rounded-xl transition duration-150 flex items-center gap-1.5 cursor-pointer shadow-inner active:scale-95"
                        title={t('clientRoster.archiveBtn', lang === 'pl' ? "Archiwizuj" : "Archive")}
                      >
                        <span className="material-symbols-outlined text-[13px]">archive</span>
                        <span>{lang === 'pl' ? "Archiwizuj" : "Archive"}</span>
                      </button>
                    </div>
                  </div>

                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* KPI Stats Cockpit Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-8 pt-4 border-t border-slate-800">

        {/* Card 1: Liczba Podopiecznych (Clients) */}
        <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-4 flex items-center gap-3.5 shadow-md">
          <div className="w-11 h-11 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center shrink-0">
            <span className="material-symbols-outlined text-indigo-400 text-lg">group</span>
          </div>
          <div className="min-w-0 flex-1">
            <span className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider block mb-1 truncate" title={lang === 'pl' ? 'Liczba Podopiecznych' : 'Total Athletes'}>
              {lang === 'pl' ? 'Liczba Podopiecznych' : 'Total Athletes'}
            </span>
            <h3 className="text-xl font-black text-white leading-none m-0">
              {totalTraineesCount}
            </h3>
          </div>
        </div>

        {/* Card 2: Dedykowane Plany */}
        <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-4 flex items-center gap-3.5 shadow-md">
          <div className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
            <span className="material-symbols-outlined text-emerald-400 text-lg">fitness_center</span>
          </div>
          <div className="min-w-0 flex-1">
            <span className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider block mb-1 truncate" title={lang === 'pl' ? 'Dedykowane Plany' : 'Dedicated Plans'}>
              {lang === 'pl' ? 'Dedykowane Plany' : 'Dedicated Plans'}
            </span>
            <h3 className="text-xl font-black text-white leading-none m-0">
              {dedicatedPlansCount}
            </h3>
          </div>
        </div>

        {/* Card 3: Globalne Plany */}
        <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-4 flex items-center gap-3.5 shadow-md">
          <div className="w-11 h-11 rounded-xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center shrink-0">
            <span className="material-symbols-outlined text-sky-400 text-lg">public</span>
          </div>
          <div className="min-w-0 flex-1">
            <span className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider block mb-1 truncate" title={lang === 'pl' ? 'Globalne Plany' : 'Global Plans'}>
              {lang === 'pl' ? 'Globalne Plany' : 'Global Plans'}
            </span>
            <h3 className="text-xl font-black text-white leading-none m-0">
              {globalPlansCount}
            </h3>
          </div>
        </div>

        {/* Card 4: Dedykowane Szablony */}
        <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-4 flex items-center gap-3.5 shadow-md">
          <div className="w-11 h-11 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
            <span className="material-symbols-outlined text-amber-400 text-lg">assignment</span>
          </div>
          <div className="min-w-0 flex-1">
            <span className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider block mb-1 truncate" title={lang === 'pl' ? 'Dedykowane Szablony' : 'Dedicated Routines'}>
              {lang === 'pl' ? 'Dedykowane Szablony' : 'Dedicated Routines'}
            </span>
            <h3 className="text-xl font-black text-white leading-none m-0">
              {dedicatedRoutinesCount}
            </h3>
          </div>
        </div>

        {/* Card 5: Globalne Szablony */}
        <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-4 flex items-center gap-3.5 shadow-md">
          <div className="w-11 h-11 rounded-xl bg-fuchsia-500/10 border border-fuchsia-500/20 flex items-center justify-center shrink-0">
            <span className="material-symbols-outlined text-fuchsia-400 text-lg">layers</span>
          </div>
          <div className="min-w-0 flex-1">
            <span className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider block mb-1 truncate" title={lang === 'pl' ? 'Globalne Szablony' : 'Global Routines'}>
              {lang === 'pl' ? 'Globalne Szablony' : 'Global Routines'}
            </span>
            <h3 className="text-xl font-black text-white leading-none m-0">
              {globalRoutinesCount}
            </h3>
          </div>
        </div>

      </div>

      <div className="space-y-4 pt-4 border-t border-slate-800">
        <h3 className="text-xs font-black uppercase tracking-wider text-sky-400 m-0 flex items-center gap-2">
          <span className="material-symbols-outlined text-sm">folder_special</span>
          <span>{lang === 'pl' ? `Aktywne Plany Wielodniowe (${filteredPlans?.length || 0})` : `Active Multi-Day Training Plans (${filteredPlans?.length || 0})`}</span>
          {selectedRosterTrainee && <span className="text-xs font-bold text-slate-400">({lang === 'pl' ? 'Filtr:' : 'Filtered for:'} {selectedRosterTrainee.name || selectedRosterTrainee.displayName})</span>}
        </h3>
        
        {(!filteredPlans || filteredPlans.length === 0) ? (
          <div className="p-12 text-center text-slate-500 bg-slate-900/50 rounded-2xl border border-dashed border-slate-700 font-semibold text-sm">
            {lang === 'pl' ? "Brak aktywnych planów wielodniowych w Firestore pasujących do filtra." : "No active multi-day plans stored in Firestore matching your selected athlete filter."}
          </div>
        ) : (
          filteredPlans.map(plan => {
            const isEditing = editingPlanId === plan.id;

            return (
              <div key={plan.id} className={`bg-slate-800/90 border p-6 rounded-2xl shadow-xl space-y-4 transition-all ${isEditing ? 'border-sky-400 ring-2 ring-sky-400/20' : 'border-sky-500/40'}`}>
                {isEditing ? (
                  <InteractivePlanBuilder
                    initialTitle={editPlanTitle || plan.title || ''}
                    initialNotes={editPlanNotes || plan.notes || ''}
                    initialDurationWeeks={plan.durationWeeks || 12}
                    initialIncludeDeload={plan.includeDeload ?? true}
                    initialAssignmentMode={editPlanAssignedTo === 'ALL' ? 'ALL_TRAINEES' : (editPlanAssignedTo ? 'SPECIFIC_TRAINEE' : 'TEMPLATE_ONLY')}
                    initialAssignedTrainee={uniqueActiveTrainees.find(t => (t.email || t.id || "").toLowerCase() === (editPlanAssignedTo || "").toLowerCase()) || null}
                    initialDays={editPlanRoutines && editPlanRoutines.length > 0 ? editPlanRoutines : (plan.routines || [])}
                    availableRoutines={createdRoutines}
                    activeTrainees={uniqueActiveTrainees}
                    isGlobalOnly={false}
                    lang={lang}
                    onSave={async (planData) => {
                      const targetClientObj = planData.assignedTrainee;
                      const targetId = planData.assignmentMode === 'ALL_TRAINEES' ? 'ALL' : (targetClientObj ? (targetClientObj.id || targetClientObj.email) : 'TEMPLATE');
                      const isAllTrainees = targetId === 'ALL';
                      const cleanAssign = isAllTrainees ? 'ALL' : (targetClientObj?.email || targetId).toLowerCase().trim();
                      const assignedToNameVal = isAllTrainees ? (lang === 'pl' ? "Wszyscy moi zawodnicy" : "All My Trainees") : (targetClientObj?.name || targetClientObj?.displayName || targetClientObj?.email || "Template Only");

                      const planRef = doc(db, "plans", plan.id);
                      await updateDoc(planRef, {
                        title: planData.title,
                        notes: planData.notes,
                        durationWeeks: planData.durationWeeks,
                        includeDeload: planData.includeDeload,
                        assignedTo: cleanAssign === 'all' ? 'ALL' : cleanAssign,
                        assignedClientId: cleanAssign === 'all' ? 'ALL' : cleanAssign,
                        assignedToName: assignedToNameVal,
                        routines: planData.days,
                        trainerId: (trainerEmail || trainerId || "wieslawp@gmail.com").toLowerCase().trim()
                      });

                      alert(lang === 'pl' ? "🎉 Zmiany w planie zostały pomyślnie zapisane!" : "🎉 Plan modifications saved successfully!");
                      setEditingPlanId(null);
                    }}
                    onCancel={() => setEditingPlanId(null)}
                  />
                ) : (
                  <>
                    <div className="flex justify-between items-start flex-wrap gap-4 border-b border-slate-700 pb-3">
                      <div>
                        <h4 className="text-xl font-black text-white m-0">{plan.title}</h4>
                        <span className="text-xs text-sky-300 font-bold mt-1 inline-flex items-center gap-1">
                          <span className="material-symbols-outlined text-xs">person</span>
                          <span>{lang === 'pl' ? `Przypisano do: ${plan.assignedToName || plan.assignedTo}` : `Assigned to: ${plan.assignedToName || plan.assignedTo}`}</span>
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleStartEditPlan(plan)}
                          className="bg-sky-600/20 hover:bg-sky-600 border border-sky-500/50 text-sky-300 hover:text-white px-3.5 py-2 rounded-xl text-xs font-black cursor-pointer transition shadow-sm flex items-center gap-1.5"
                          title={lang === 'pl' ? "Edytuj nazwe, notatki i harmonogram planu" : "Edit plan name, notes, and schedule"}
                        >
                          <span className="material-symbols-outlined text-xs">edit</span>
                          <span>{lang === 'pl' ? 'Edytuj' : 'Edit'}</span>
                        </button>
                        <button
                          onClick={() => handleDeletePlan(plan.id, plan.title)}
                          className="bg-rose-600/20 hover:bg-rose-600 border border-rose-500/50 text-rose-300 hover:text-white px-3.5 py-2 rounded-xl text-xs font-black cursor-pointer transition shadow-sm flex items-center gap-1.5"
                        >
                          <span className="material-symbols-outlined text-xs">delete</span>
                          <span>{t('common.remove')}</span>
                        </button>
                      </div>
                    </div>

                    {plan.notes && (
                      <div className="bg-slate-950/80 border border-slate-800 p-3.5 rounded-xl">
                        <strong className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1 flex items-center gap-1"><span className="material-symbols-outlined text-xs text-indigo-400">edit_note</span><span>{lang === 'pl' ? "Notatka Trenera:" : "Coach's Notes:"}</span></strong>
                        <p className="text-xs text-slate-200 italic m-0 whitespace-pre-line">"{plan.notes}"</p>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                      {plan.routines && plan.routines.map((d, dIdx) => {
                        const isSelected = selectedRoutineFilter && (
                          (typeof selectedRoutineFilter === 'object' 
                            ? (selectedRoutineFilter.routineId === d.routineId || selectedRoutineFilter.routineTitle === d.routineTitle)
                            : (selectedRoutineFilter === d.routineId || selectedRoutineFilter === d.routineTitle))
                        );

                        return (
                          <button
                            key={dIdx}
                            type="button"
                            onClick={() => {
                              setSelectedRoutineFilter(prev => {
                                const isCurrentlySelected = prev && (
                                  (typeof prev === 'object' 
                                    ? (prev.routineId === d.routineId || prev.routineTitle === d.routineTitle)
                                    : (prev === d.routineId || prev === d.routineTitle))
                                );
                                return isCurrentlySelected ? null : { routineId: d.routineId, routineTitle: d.routineTitle };
                              });
                            }}
                            className={`p-3.5 rounded-xl border text-left transition-all flex items-center justify-between gap-3 cursor-pointer shadow-md select-none ${
                              isSelected
                                ? 'bg-gradient-to-br from-emerald-600/30 to-teal-500/10 border-2 border-emerald-400 text-white font-extrabold shadow-emerald-500/20 scale-[1.02]'
                                : 'bg-slate-900/90 border-slate-750 hover:border-emerald-400/80 text-slate-200 hover:text-white'
                            }`}
                          >
                            <div className="min-w-0 flex-1">
                              <span className={`text-xs font-black block mb-0.5 ${isSelected ? 'text-emerald-300' : 'text-sky-400'}`}>
                                {d.dayLabel}
                              </span>
                              <strong className="text-sm text-slate-100 font-extrabold block truncate">
                                {d.routineTitle}
                              </strong>
                            </div>
                            {isSelected ? (
                              <span className="w-3 h-3 rounded-full bg-emerald-400 animate-pulse shadow-md shadow-emerald-400/50 shrink-0" title="Szablon wybrany (filtrowanie aktywne)" />
                            ) : (
                              <span className="material-symbols-outlined text-sm text-slate-500 hover:text-emerald-400 shrink-0">filter_alt</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            );
          })
        )}
      </div>

      <div className="space-y-4 pt-4 border-t border-slate-800">
        <div className="flex flex-wrap gap-4 justify-between items-center">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="text-xs font-black uppercase tracking-wider text-slate-400 m-0">
              {lang === 'pl' ? `Aktywne Przypisania Szablonów (${filteredRoutines.length})` : `Active Routine Assignments (${filteredRoutines.length})`}
              {selectedRosterTrainee && <span className="text-xs font-bold text-emerald-400 ml-2">({lang === 'pl' ? 'Zawiera dedykowane programy dla:' : 'Includes targeted routines for:'} {selectedRosterTrainee.name || selectedRosterTrainee.displayName})</span>}
            </h3>

            {selectedRoutineFilter && (
              <div className="flex items-center gap-2 bg-emerald-500/20 text-emerald-300 border border-emerald-500/50 px-3 py-1 rounded-xl text-xs font-black animate-fade-in shadow-sm">
                <span className="material-symbols-outlined text-xs text-emerald-400">filter_alt</span>
                <span>{lang === 'pl' ? `Wybrany szablon: ${selectedRoutineFilter.routineTitle || selectedRoutineFilter.routineId}` : `Selected routine: ${selectedRoutineFilter.routineTitle || selectedRoutineFilter.routineId}`}</span>
                <button
                  type="button"
                  onClick={() => setSelectedRoutineFilter(null)}
                  className="ml-1 text-emerald-400 hover:text-white font-bold cursor-pointer bg-transparent border-0 flex items-center p-0"
                  title={lang === 'pl' ? "Wyczyść filtr szablonu" : "Clear routine filter"}
                >
                  <span className="material-symbols-outlined text-sm">cancel</span>
                </button>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-3 flex-wrap w-full sm:w-auto">
            <input 
              type="text" 
              placeholder={t('common.searchPlaceholder')} 
              value={searchQuery} 
              onChange={e => setSearchQuery(e.target.value)} 
              className="flex-1 min-w-[200px] bg-slate-900 border border-slate-700 rounded-xl p-2.5 text-xs text-white placeholder-slate-500 outline-none focus:border-emerald-400 font-semibold shadow-inner"
            />
            
            {/* Tag Filter Dropdown (#) */}
            <select 
              value={selectedTagFilter} 
              onChange={e => setSelectedTagFilter(e.target.value)}
              className="bg-slate-900 border border-purple-500/60 text-purple-300 rounded-xl p-2.5 text-xs outline-none font-extrabold cursor-pointer shadow-inner"
            >
              <option value="ALL">🏷️ {lang === 'pl' ? "Wszystkie Tagi (#)" : "All Tags (#)"}</option>
              <option value="FBW">#FBW</option>
              <option value="Split">#Split</option>
              <option value="Góra / Dół">#Góra / Dół</option>
              <option value="Przód / Tył">#Przód / Tył</option>
              <option value="Push / Pull / Legs">#Push / Pull / Legs</option>
              <option value="Kondycja / Cardio">#Kondycja / Cardio</option>
              <option value="Rehabilitacja">#Rehabilitacja</option>
              {(createdRoutines || [])
                .map(r => r.tag)
                .filter(Boolean)
                .filter((tg, idx, self) => self.indexOf(tg) === idx && !['FBW','Split','Góra / Dół','Przód / Tył','Push / Pull / Legs','Kondycja / Cardio','Rehabilitacja'].includes(tg))
                .map(tg => (
                  <option key={tg} value={tg}>#{tg}</option>
                ))
              }
            </select>

            <select 
              value={filterType} 
              onChange={e => setFilterType(e.target.value)}
              className="bg-slate-900 border border-slate-700 rounded-xl p-2.5 text-xs text-white outline-none font-extrabold cursor-pointer shadow-inner"
            >
              <option value="ALL">🌐 {lang === 'pl' ? "Wszystkie Programy" : "All Programs"}</option>
              <option value="ALL_TRAINEES">👥 {lang === 'pl' ? "Wszyscy moi zawodnicy" : "All My Trainees"}</option>
              {uniqueActiveTrainees.map(t => (
                <option key={t.id} value={t.id}>👤 {t.name || t.displayName}</option>
              ))}
            </select>
          </div>
        </div>
        
        {filteredRoutines.length === 0 ? (
          <div className="p-16 text-center text-slate-500 bg-slate-900/60 rounded-2xl border border-dashed border-slate-700 font-semibold">
            {lang === 'pl' ? "Nie znaleziono programów pasujących do kryteriów lub baza Firestore jest pusta." : "No routines matching your filter or Firestore database is currently empty."}
          </div>
        ) : (
          filteredRoutines.map(rt => {
            const isGlobal = checkIsGlobal(rt);
            const traineeObj = isGlobal ? null : uniqueActiveTrainees.find(c => matchTrainee(rt.assignedClientId || rt.assignedTo, c));
            const isEditing = editingRoutineId === rt.id;

            return (
              <div key={rt.id} className={`bg-slate-800/90 border p-6 rounded-2xl shadow-xl transition-all space-y-5 ${isEditing ? 'border-emerald-400 ring-2 ring-emerald-400/20' : 'border-slate-700 hover:border-emerald-500/50'}`}>
                {isEditing ? (
                  <div className="space-y-5 animate-fadeIn">
                    <div className="flex justify-between items-center border-b border-slate-700 pb-3">
                      <h4 className="text-sm font-black text-emerald-400 uppercase tracking-wider m-0 flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-sm">edit</span>
                        <span>{lang === 'pl' ? 'Edycja Programu i Kolejności Ćwiczeń' : 'Editing Routine & Exercise Order'}</span>
                      </h4>
                      <button onClick={() => setEditingRoutineId(null)} className="text-slate-400 hover:text-white text-xs font-bold bg-transparent border-0 cursor-pointer flex items-center gap-1"><span className="material-symbols-outlined text-xs">close</span><span>{t('common.cancel', 'Cancel')}</span></button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-slate-300 mb-1.5">{lang === 'pl' ? 'Nazwa Programu (np. Dzień 1 - Nogi / Plecy / Barki)' : 'Routine Title'}</label>
                        <input 
                          type="text" 
                          value={editingRoutineTitle} 
                          onChange={e => setEditingRoutineTitle(e.target.value)} 
                          className="w-full bg-slate-950 border border-slate-700 text-white font-black text-base rounded-xl px-4 py-2.5 outline-none focus:border-emerald-400"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-black text-purple-400 mb-1.5 flex items-center gap-1">
                          <span className="material-symbols-outlined text-xs text-purple-400">label</span>
                          <span>{lang === 'pl' ? 'Tag Szablonu Treningowego (#):' : 'Routine Template Tag (#):'}</span>
                        </label>
                        <div className="flex items-center gap-2">
                          <select
                            value={editingRoutineTag}
                            onChange={(e) => setEditingRoutineTag(e.target.value)}
                            className="flex-1 bg-slate-950 border border-slate-700 text-white font-extrabold text-xs sm:text-sm rounded-xl px-3 py-2.5 outline-none focus:border-emerald-400 cursor-pointer"
                          >
                            <option value="FBW">#FBW (Full Body Workout)</option>
                            <option value="Split">#Split (Podział na grupy)</option>
                            <option value="Góra / Dół">#Góra / Dół (Upper / Lower)</option>
                            <option value="Przód / Tył">#Przód / Tył (Push / Pull)</option>
                            <option value="Push / Pull / Legs">#Push / Pull / Legs</option>
                            <option value="Kondycja / Cardio">#Kondycja / Cardio</option>
                            <option value="Rehabilitacja">#Rehabilitacja</option>
                            <option value="CUSTOM">{lang === 'pl' ? "+ Inny / Własny Tag..." : "+ Custom Tag..."}</option>
                          </select>
                          {editingRoutineTag === 'CUSTOM' && (
                            <input
                              type="text"
                              value={editingRoutineCustomTag}
                              onChange={(e) => setEditingRoutineCustomTag(e.target.value)}
                              placeholder="np. Hypertrophy"
                              className="w-1/2 bg-slate-950 border border-slate-700 text-white font-bold text-xs rounded-xl px-3 py-2.5 outline-none focus:border-emerald-400"
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-black uppercase text-slate-400 mb-2.5 flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-sm text-slate-400">format_list_numbered</span>
                        <span>{lang === 'pl' ? `Kolejność Ćwiczeń (${editingRoutineExercises.length}) — Użyj przycisków W Górę / W Dół:` : `Exercise Order (${editingRoutineExercises.length}) — Use Up / Down arrows to reorder:`}</span>
                      </label>
                      <div className="space-y-2 max-h-[460px] overflow-y-auto pr-1">
                        {editingRoutineExercises.map((ex, exIdx) => (
                          <div key={exIdx} className="bg-slate-950 border border-slate-800 p-3.5 rounded-xl flex items-center justify-between gap-4 shadow-sm flex-wrap">
                            <div className="flex items-center gap-3 min-w-[220px] flex-1">
                              <span className="w-7 h-7 rounded-lg bg-emerald-500/15 text-emerald-400 font-extrabold flex items-center justify-center text-sm shrink-0 border border-emerald-500/30">
                                {exIdx + 1}
                              </span>
                              <div className="min-w-0 flex-1 space-y-1.5">
                                <strong className="text-white font-bold text-sm block truncate">{ex.name} <span className="text-xs text-slate-500 font-normal">({ex.bodyPart || ex.targetMuscle || 'General'})</span></strong>
                                
                                {/* Dynamic Series & Reps Editable Controls */}
                                <div className="flex items-center gap-2 flex-wrap">
                                  {/* Series / Sets input */}
                                  <div className="flex items-center gap-1.5 bg-slate-900 border border-slate-800 px-2.5 py-1 rounded-lg">
                                    <span className="text-[10px] font-extrabold text-amber-400 uppercase tracking-wider">
                                      {lang === 'pl' ? 'Serie:' : 'Sets:'}
                                    </span>
                                    <input 
                                      type="number"
                                      min="1"
                                      max="20"
                                      value={Array.isArray(ex.sets) ? ex.sets.length : (ex.setsCount ?? ex.sets ?? 3)}
                                      onChange={(e) => {
                                        const val = parseInt(e.target.value) || 1;
                                        const updated = [...editingRoutineExercises];
                                        updated[exIdx] = {
                                          ...updated[exIdx],
                                          setsCount: val,
                                          sets: val
                                        };
                                        setEditingRoutineExercises(updated);
                                      }}
                                      className="w-12 bg-slate-950 border border-slate-700 text-amber-300 font-black text-xs text-center rounded px-1 py-0.5 outline-none focus:border-amber-400"
                                    />
                                  </div>

                                  {/* Repeats / Reps input */}
                                  <div className="flex items-center gap-1.5 bg-slate-900 border border-slate-800 px-2.5 py-1 rounded-lg">
                                    <span className="text-[10px] font-extrabold text-indigo-400 uppercase tracking-wider">
                                      {lang === 'pl' ? 'Powtórzeń:' : 'Reps:'}
                                    </span>
                                    <input 
                                      type="text"
                                      value={typeof ex.reps === 'object' ? (Array.isArray(ex.reps) ? (ex.reps[0]?.reps || "12") : "12") : (ex.reps ?? "12")}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        const updated = [...editingRoutineExercises];
                                        updated[exIdx] = {
                                          ...updated[exIdx],
                                          reps: val
                                        };
                                        setEditingRoutineExercises(updated);
                                      }}
                                      className="w-16 bg-slate-950 border border-slate-700 text-indigo-300 font-black text-xs text-center rounded px-1.5 py-0.5 outline-none focus:border-indigo-400"
                                      placeholder="12"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center gap-1.5 shrink-0">
                              <button
                                type="button"
                                disabled={exIdx === 0}
                                onClick={() => handleMoveExerciseUp(exIdx)}
                                className="bg-slate-800 hover:bg-emerald-600 disabled:opacity-30 disabled:hover:bg-slate-800 text-white p-2 rounded-lg text-xs font-black transition shadow cursor-pointer disabled:cursor-not-allowed flex items-center gap-1"
                                title="Move exercise up in routine order"
                              >
                                <span className="material-symbols-outlined text-xs">arrow_upward</span>
                                <span>{lang === 'pl' ? 'W górę' : 'Up'}</span>
                              </button>
                              <button
                                type="button"
                                disabled={exIdx === editingRoutineExercises.length - 1}
                                onClick={() => handleMoveExerciseDown(exIdx)}
                                className="bg-slate-800 hover:bg-emerald-600 disabled:opacity-30 disabled:hover:bg-slate-800 text-white p-2 rounded-lg text-xs font-black transition shadow cursor-pointer disabled:cursor-not-allowed flex items-center gap-1"
                                title="Move exercise down in routine order"
                              >
                                <span className="material-symbols-outlined text-xs">arrow_downward</span>
                                <span>{lang === 'pl' ? 'W dół' : 'Down'}</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleRemoveExerciseFromRoutineEdit(exIdx)}
                                className="bg-slate-900 hover:bg-rose-600 text-slate-400 hover:text-white p-2 rounded-lg text-xs font-black transition shadow ml-2 cursor-pointer flex items-center justify-center"
                                title="Remove exercise from routine"
                              >
                                <span className="material-symbols-outlined text-sm">delete</span>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* ADD EXERCISE TO ROUTINE SELECTOR SECTION */}
                      {(() => {
                        const filteredRoutineExercisesList = (exercises || []).filter(exItem => {
                          // 1. Source Filter (Global Standard vs Trainer Custom)
                          const owner = (exItem.trainerId || "").toLowerCase().trim();
                          const isGlobal = exItem.isGlobal === true || owner === 'global' || owner === 'all' || !owner;
                          if (editingRoutineSourceFilter === 'GLOBAL' && !isGlobal) return false;
                          if (editingRoutineSourceFilter === 'TRAINER' && isGlobal) return false;

                          // 2. Body Part Filter
                          if (editingRoutineBodyPartFilter !== 'ALL') {
                            const filterLower = editingRoutineBodyPartFilter.toLowerCase();
                            const bodyLower = (exItem.bodyPart || exItem.targetMuscle || "").toLowerCase();
                            if (!bodyLower.includes(filterLower) && !filterLower.includes(bodyLower)) return false;
                          }

                          // 3. Search Query Filter
                          if (editingRoutineSearchQuery.trim()) {
                            const q = editingRoutineSearchQuery.toLowerCase().trim();
                            const nameStr = ((exItem.name || "") + " " + (exItem.namePL || "") + " " + (exItem.nameEN || "")).toLowerCase();
                            const subStr = (exItem.subPart || "").toLowerCase();
                            const hintsStr = ((exItem.hints || "") + " " + (exItem.hintsPL || "") + " " + (exItem.hintsEN || "")).toLowerCase();
                            const bodyStr = (exItem.bodyPart || "").toLowerCase();
                            if (!nameStr.includes(q) && !subStr.includes(q) && !hintsStr.includes(q) && !bodyStr.includes(q)) return false;
                          }

                          return true;
                        });

                        return (
                          <div className="bg-slate-950/90 border border-emerald-500/30 p-4 rounded-xl space-y-3 mt-4 shadow-inner">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <span className="text-xs font-black text-emerald-400 uppercase tracking-wider flex items-center gap-1.5">
                                <span className="material-symbols-outlined text-sm">add_circle</span>
                                <span>{lang === 'pl' ? "Dodaj Nowe Ćwiczenie do Programu" : "Add New Exercise to Routine"}</span>
                              </span>
                              <span className="text-[10px] font-black text-slate-400 bg-slate-900 border border-slate-800 px-2 py-0.5 rounded-md">
                                {lang === 'pl' ? `Dostępne: ${filteredRoutineExercisesList.length}` : `${filteredRoutineExercisesList.length} available`}
                              </span>
                            </div>

                            {/* Filters Bar: Search + Source Filter + Muscle Filter */}
                            <div className="grid grid-cols-1 sm:grid-cols-12 gap-2.5 items-center">
                              {/* Search Bar Input */}
                              <div className="sm:col-span-5 relative">
                                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">search</span>
                                <input
                                  type="text"
                                  value={editingRoutineSearchQuery}
                                  onChange={(e) => setEditingRoutineSearchQuery(e.target.value)}
                                  placeholder={lang === 'pl' ? "Szukaj ćwiczenia (np. wyciskanie)..." : "Search exercise (e.g. bench)..."}
                                  className="w-full bg-slate-900 border border-slate-800 focus:border-emerald-400 text-white font-bold text-xs rounded-xl pl-9 pr-8 py-2 outline-none shadow-inner"
                                />
                                {editingRoutineSearchQuery && (
                                  <button
                                    type="button"
                                    onClick={() => setEditingRoutineSearchQuery('')}
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white text-xs cursor-pointer font-bold"
                                  >
                                    ✕
                                  </button>
                                )}
                              </div>

                              {/* Source Filter (Global vs Trainer) */}
                              <div className="sm:col-span-4">
                                <select
                                  value={editingRoutineSourceFilter}
                                  onChange={(e) => setEditingRoutineSourceFilter(e.target.value)}
                                  className="w-full bg-slate-900 text-sky-300 border border-emerald-500/50 focus:border-emerald-400 rounded-xl px-2.5 py-2 text-xs font-black cursor-pointer outline-none shadow-inner"
                                >
                                  <option value="ALL">{lang === 'pl' ? "🌐 Wszystkie (Global + Trener)" : "🌐 All (Global + Coach)"}</option>
                                  <option value="GLOBAL">{lang === 'pl' ? "🌐 Biblioteka Globalna" : "🌐 Global Library"}</option>
                                  <option value="TRAINER">{lang === 'pl' ? "👤 Moje (Trener)" : "👤 Trainer Custom"}</option>
                                </select>
                              </div>

                              {/* Body Part Filter */}
                              <div className="sm:col-span-3">
                                <select
                                  value={editingRoutineBodyPartFilter}
                                  onChange={(e) => setEditingRoutineBodyPartFilter(e.target.value)}
                                  className="w-full bg-slate-900 border border-emerald-500/50 text-amber-300 font-extrabold text-xs rounded-xl px-2.5 py-2 outline-none focus:border-emerald-400 cursor-pointer shadow-inner"
                                >
                                  <option value="ALL">{lang === 'pl' ? "Wszystkie Partie" : "All Muscles"}</option>
                                  <option value="Legs">{lang === 'pl' ? "Nogi (Legs)" : "Legs"}</option>
                                  <option value="Chest">{lang === 'pl' ? "Klatka (Chest)" : "Chest"}</option>
                                  <option value="Back">{lang === 'pl' ? "Plecy (Back)" : "Back"}</option>
                                  <option value="Shoulders">{lang === 'pl' ? "Barki (Shoulders)" : "Shoulders"}</option>
                                  <option value="Biceps">Biceps</option>
                                  <option value="Triceps">Triceps</option>
                                  <option value="Core">Core</option>
                                </select>
                              </div>
                            </div>

                            {/* Dynamically Filtered Exercise Selector + Add Button */}
                            <div className="grid grid-cols-1 sm:grid-cols-12 gap-2.5 items-center pt-1">
                              <div className="sm:col-span-9">
                                <select
                                  id={`add_ex_select_${rt.id}`}
                                  defaultValue=""
                                  className="w-full bg-slate-900 border border-slate-750 text-white font-bold text-xs rounded-xl p-2.5 outline-none focus:border-emerald-400 cursor-pointer shadow-inner truncate"
                                >
                                  <option value="" disabled>{lang === 'pl' ? "Wybierz ćwiczenie z listy..." : "Choose exercise from list..."}</option>
                                  {filteredRoutineExercisesList.map((exItem, eIndex) => {
                                    const owner = (exItem.trainerId || "").toLowerCase().trim();
                                    const isGlobal = exItem.isGlobal === true || owner === 'global' || owner === 'all' || !owner;
                                    const tag = isGlobal ? "🌐 [Global Standard]" : (lang === 'pl' ? "👤 [Trener Custom]" : "👤 [Coach Custom]");
                                    const primaryName = lang === 'pl' ? (exItem.namePL || exItem.name) : (exItem.nameEN || exItem.name);
                                    const subInfo = exItem.subPart ? ` • ${exItem.subPart}` : '';
                                    const bodyInfo = exItem.bodyPart || "General";
                                    return (
                                      <option key={eIndex} value={JSON.stringify(exItem)}>
                                        {tag} {primaryName} ({bodyInfo}{subInfo})
                                      </option>
                                    );
                                  })}
                                </select>
                              </div>

                              <div className="sm:col-span-3">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const selectEl = document.getElementById(`add_ex_select_${rt.id}`);
                                    if (!selectEl || !selectEl.value) return;
                                    try {
                                      const parsedEx = JSON.parse(selectEl.value);
                                      const newExObj = {
                                        id: parsedEx.id || "add_" + Date.now(),
                                        name: parsedEx.name,
                                        bodyPart: parsedEx.bodyPart || parsedEx.targetMuscle || "General",
                                        targetMuscle: parsedEx.targetMuscle || parsedEx.bodyPart?.toLowerCase() || "general",
                                        setsCount: 3,
                                        sets: 3,
                                        reps: 12,
                                        hints: parsedEx.hints || parsedEx.notes || parsedEx.remarks || ""
                                      };
                                      setEditingRoutineExercises(prev => [...prev, newExObj]);
                                      selectEl.value = "";
                                    } catch (err) {}
                                  }}
                                  className="w-full px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-black text-xs rounded-xl shadow-md cursor-pointer shrink-0 transition active:scale-95 flex items-center justify-center gap-1.5"
                                >
                                  <span className="material-symbols-outlined text-sm">add_circle</span>
                                  <span>{lang === 'pl' ? "Dodaj" : "Add"}</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    <div className="flex justify-end gap-3 pt-3 border-t border-slate-750">
                      <button 
                        onClick={() => {
                          const finalTag = editingRoutineTag === 'CUSTOM' ? (editingRoutineCustomTag.trim() || 'Custom') : editingRoutineTag;
                          handleUpdateRoutine(rt.id, editingRoutineTitle.trim() || rt.title, editingRoutineExercises, finalTag);
                          setEditingRoutineId(null);
                        }}
                        className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-slate-950 font-black text-xs sm:text-sm px-6 py-3 rounded-xl transition shadow-xl shadow-emerald-500/25 cursor-pointer flex items-center gap-1.5 active:scale-95"
                      >
                        <span className="material-symbols-outlined text-sm">save</span>
                        <span>{lang === 'pl' ? 'Zapisz Zmiany' : 'Save Changes'}</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-start flex-wrap gap-4 border-b border-slate-700/80 pb-4">
                      <div className="space-y-3 flex-1 min-w-[260px]">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h4 className="text-lg md:text-xl font-extrabold text-white m-0 tracking-wide">{rt.title}</h4>
                          <span className="bg-purple-500/20 text-purple-300 border border-purple-500/40 px-3 py-1 rounded-lg text-xs font-black flex items-center gap-1 shadow-sm">
                            <span className="material-symbols-outlined text-xs text-purple-400">label</span>
                            <span>#{rt.tag || "FBW"}</span>
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2.5 flex-wrap">
                          <span className="text-xs font-semibold text-slate-400 bg-slate-900/90 px-3 py-1.5 rounded-lg border border-slate-700 flex items-center gap-1.5 shadow-sm">
                            <span className="material-symbols-outlined text-xs text-sky-400">calendar_month</span>
                            <span>{lang === 'pl' ? "Utworzono:" : "Created:"} <strong className="text-slate-200 font-bold">{rt.creationDate || "2026-07-28"}</strong></span>
                          </span>

                          {isGlobal ? (
                            <span className="bg-sky-500/20 text-sky-300 border border-sky-500/40 px-3.5 py-1.5 rounded-lg text-xs font-extrabold flex items-center gap-1.5 shadow-sm">
                              <span className="material-symbols-outlined text-xs">public</span>
                              <span>{lang === 'pl' ? "Przypisanie Globalne: Dostępne dla Wszystkich" : "Global Assignment: Available to All Athletes"}</span>
                            </span>
                          ) : (
                            <span className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/50 px-3.5 py-1.5 rounded-lg text-xs font-extrabold flex items-center gap-1.5 shadow-sm shadow-emerald-500/10">
                              <span className="material-symbols-outlined text-xs">person</span>
                              <span>{lang === 'pl' ? `Dedykowane dla: ${traineeObj?.name || rt.assignedClientId}` : `Exclusively Assigned to: ${traineeObj?.name || rt.assignedClientId}`}</span>
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2.5 flex-wrap shrink-0">
                        <button
                          onClick={() => {
                            setEditingRoutineId(rt.id);
                            setEditingRoutineTitle(rt.title || "");
                            setEditingRoutineExercises([...(rt.exercises || [])]);
                            const standardTags = ['FBW','Split','Góra / Dół','Przód / Tył','Push / Pull / Legs','Kondycja / Cardio','Rehabilitacja'];
                            const currentTag = rt.tag || 'FBW';
                            const hasStandard = standardTags.includes(currentTag);
                            setEditingRoutineTag(hasStandard ? currentTag : 'CUSTOM');
                            setEditingRoutineCustomTag(hasStandard ? '' : currentTag);
                          }}
                          className="bg-emerald-600/20 hover:bg-emerald-600 border border-emerald-500/50 text-emerald-300 hover:text-white px-3.5 py-2 rounded-xl text-xs font-black cursor-pointer transition shadow-sm flex items-center gap-1.5"
                          title="Edit routine"
                        >
                          <span className="material-symbols-outlined text-xs">edit</span>
                          <span>{lang === 'pl' ? 'Edytuj' : 'Edit'}</span>
                        </button>

                        <div className="flex items-center gap-1.5 bg-slate-900 border border-slate-700 rounded-xl px-3 py-1.5 shadow-sm">
                          <span className="inline-flex items-center gap-1 text-xs text-slate-400 font-extrabold">
                            <span className="material-symbols-outlined text-xs text-indigo-400">swap_horiz</span>
                            <span>{lang === 'pl' ? "Zmień:" : "Reassign:"}</span>
                          </span>
                          <select
                            value={rt.assignedClientId}
                            onChange={(e) => handleReassignRoutine(rt.id, e.target.value)}
                            className="bg-slate-800 border border-slate-600 rounded-lg text-xs text-emerald-300 font-extrabold px-2 py-1 outline-none cursor-pointer hover:bg-slate-700"
                          >
                            <option value="ALL">👥 {lang === 'pl' ? "Wszyscy moi zawodnicy" : "All My Trainees"}</option>
                            {uniqueActiveTrainees.map(c => (
                              <option key={c.id} value={c.id}>👤 {c.name || c.displayName}</option>
                            ))}
                          </select>
                        </div>

                        <button 
                          onClick={() => handleDeleteRoutine(rt)}
                          className="bg-rose-600/20 hover:bg-rose-600 border border-rose-500/50 text-rose-300 hover:text-white px-3.5 py-2 rounded-xl text-xs font-extrabold transition-all flex items-center gap-1.5 cursor-pointer shadow-sm"
                        >
                          <span className="material-symbols-outlined text-xs">delete</span>
                          <span>{t('common.remove')}</span>
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3.5 pt-1">
                      {rt.exercises.map((ex, exIndex) => (
                        <div key={exIndex} className="bg-slate-900/95 border border-slate-700/80 p-4 rounded-xl flex flex-col justify-between shadow-inner">
                          <div>
                            <div className="flex items-start gap-2 mb-2 min-w-0">
                              <span className="font-extrabold text-sm text-indigo-400 shrink-0 leading-tight">
                                {exIndex + 1}.
                              </span>
                              <h4 className="font-bold text-sm text-slate-100 leading-tight break-words m-0">
                                {ex.name}
                              </h4>
                            </div>
                            <div className="mt-2 flex items-center gap-2 flex-wrap">
                              <span className="inline-flex items-center gap-1 text-xs text-sky-300 font-extrabold bg-sky-500/10 border border-sky-500/30 px-2.5 py-1 rounded-lg">
                                <span className="material-symbols-outlined text-xs text-amber-400">bolt</span>
                                <span>{Array.isArray(ex.sets) ? ex.sets.length : (typeof ex.sets === 'object' ? 3 : (ex.sets ?? ex.setsCount ?? 3))} {t('createAssign.workingSetsLabel', 'Sets')} × </span>
                                <span className="material-symbols-outlined text-xs text-indigo-400">repeat</span>
                                <span>{Array.isArray(ex.reps) ? (ex.reps[0]?.reps || 8) : (typeof ex.reps === 'object' ? 8 : (ex.reps ?? 8))} {t('createAssign.targetRepsLabel', 'Reps')}</span>
                              </span>
                              {getMuscleBadge(ex.bodyPart || ex.targetMuscle, t, lang)}
                            </div>
                          </div>
                          {(ex.hints || ex.notes) && (
                            <span className="mt-3 text-xs text-slate-300 italic flex items-start gap-1.5 bg-slate-800/90 p-2.5 rounded-lg border border-slate-700 font-normal">
                              <span className="material-symbols-outlined text-sm text-amber-400 shrink-0 mt-0.5">lightbulb</span>
                              <span>"{ex.hints || ex.notes}"</span>
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })
        )}
      </div>
    

</div>
  );
};

/* ==========================================
   MAIN COMPONENT: UNIFIED NAVBAR & SHELL
   ========================================== */
export default function AdminPortal({ trainerId, trainerEmail, userProfile, branding = { logo: "GymGhost", color: "#6366f1" }, onSignOut }) {
  const { t, lang, setLang } = useTranslation();
  
  const trainerDisplayName = userProfile?.displayName || userProfile?.name || ((trainerEmail || "").toLowerCase().includes('wieslaw') ? 'Trener Wiesław Piątek' : ((trainerEmail || "").toLowerCase().includes('marek') ? 'Trener Marek Testowy' : `Coach ${trainerEmail?.split('@')[0] || 'Trainer'}`));
  const isWieslaw = (trainerEmail || "").toLowerCase().includes('wieslaw') || (trainerId || "").toLowerCase().includes('wieslaw') || trainerDisplayName.toLowerCase().includes('wiesław') || trainerDisplayName.toLowerCase().includes('wieslaw');
  const isMarek = (trainerEmail || "").toLowerCase().includes('marek') || (trainerId || "").toLowerCase().includes('marek') || trainerDisplayName.toLowerCase().includes('marek') || trainerDisplayName.toLowerCase().includes('testowy');

  const { exercises } = useExerciseLibrary(trainerId, trainerEmail);
  const [passwordModalUser, setPasswordModalUser] = useState(null);
  const [newPasswordInput, setNewPasswordInput] = useState("");
  const [selectedTrainee, setSelectedTrainee] = useState(null);
  const [traineeSearchQuery, setTraineeSearchQuery] = useState("");
  const [routineTitle, setRoutineTitle] = useState('');
  const [routineDate, setRoutineDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [assignedClientId, setAssignedClientId] = useState('ALL');
  const [routineTag, setRoutineTag] = useState('FBW');
  const [customRoutineTag, setCustomRoutineTag] = useState('');
  const [routineExercises, setRoutineExercises] = useState([]);
  const [customExerciseName, setCustomExerciseName] = useState('');
  const [customExerciseNamePL, setCustomExerciseNamePL] = useState('');
  const [customExerciseNameEN, setCustomExerciseNameEN] = useState('');
  const [customExerciseHintsPL, setCustomExerciseHintsPL] = useState('');
  const [customExerciseHintsEN, setCustomExerciseHintsEN] = useState('');
  const [customExerciseImageFile, setCustomExerciseImageFile] = useState(null);
  const [customExerciseImageUrlInput, setCustomExerciseImageUrlInput] = useState('');
  const [customExerciseYoutubeUrl, setCustomExerciseYoutubeUrl] = useState('');
  const [isUploadingCoachImage, setIsUploadingCoachImage] = useState(false);
  const [customExerciseBodyPart, setCustomExerciseBodyPart] = useState('chest');
  const [customExerciseEquipment, setCustomExerciseEquipment] = useState('body weight');
  const [customExerciseSubPart, setCustomExerciseSubPart] = useState('');
  const [customExerciseTarget, setCustomExerciseTarget] = useState('');
  const [customExerciseMuscleGroup, setCustomExerciseMuscleGroup] = useState('');
  const [customExerciseSecondaryMuscles, setCustomExerciseSecondaryMuscles] = useState('');
  const [customExerciseInstructionsPL, setCustomExerciseInstructionsPL] = useState('');
  const [customExerciseInstructionsEN, setCustomExerciseInstructionsEN] = useState('');
  const [customExerciseMediaId, setCustomExerciseMediaId] = useState('');
  const [customExerciseAttribution, setCustomExerciseAttribution] = useState('Gym visual');
  
  const [editingBodyPart, setEditingBodyPart] = useState('chest');
  const [editingEquipment, setEditingEquipment] = useState('body weight');
  const [editingSubPart, setEditingSubPart] = useState('');
  const [editingTarget, setEditingTarget] = useState('');
  const [editingMuscleGroup, setEditingMuscleGroup] = useState('');
  const [editingSecondaryMuscles, setEditingSecondaryMuscles] = useState('');
  const [editingInstructionsPL, setEditingInstructionsPL] = useState('');
  const [editingInstructionsEN, setEditingInstructionsEN] = useState('');
  const [editingMediaId, setEditingMediaId] = useState('');
  const [editingAttribution, setEditingAttribution] = useState('Gym visual');
  const [editingImageUrlInput, setEditingImageUrlInput] = useState('');
  const [editingYoutubeUrl, setEditingYoutubeUrl] = useState('');
  
  const [createdRoutines, setCreatedRoutines] = useState([]);
  const [createdPlans, setCreatedPlans] = useState([]);
  const [activeTrainees, setActiveTrainees] = useState([]);
  const [archivedTrainees, setArchivedTrainees] = useState([]);
  
  const [activeTab, setActiveTab] = useState('roster'); // 'roster' | 'assign' | 'catalog' | 'live' | 'settings'

  const [selectedExercises, setSelectedExercises] = useState([]);
  const [isArchiveListExpanded, setIsArchiveListExpanded] = useState(false);

  const [coachAvatarUrlInput, setCoachAvatarUrlInput] = useState('');

  const [coachLogoUrlInput, setCoachLogoUrlInput] = useState('');
  const [selectedBrandColor, setSelectedBrandColor] = useState(userProfile?.brandColor || branding?.color || '#6366f1');
  const [enlargedImage, setEnlargedImage] = useState(null);

  useEffect(() => {
    if (userProfile?.brandColor || branding?.color) {
      setSelectedBrandColor(userProfile?.brandColor || branding?.color || '#6366f1');
    }
  }, [userProfile, branding]);

  const saveCoachAvatar = async (newUrl) => {
    try {
      const cleanUid = userProfile?.uid || trainerId;
      const cleanEmail = (trainerEmail || userProfile?.email || "").toLowerCase().trim();
      const updateObj = { avatarUrl: newUrl || null, coachAvatarUrl: newUrl || null };

      if (cleanUid) {
        await setDoc(doc(db, "users", cleanUid), updateObj, { merge: true }).catch(() => {});
      }
      if (cleanEmail) {
        await setDoc(doc(db, "users", cleanEmail), updateObj, { merge: true }).catch(() => {});
      }
      if (userProfile) {
        userProfile.avatarUrl = newUrl || null;
        userProfile.coachAvatarUrl = newUrl || null;
      }
      alert(lang === 'pl' ? "Zdjęcie profilowe trenera zostało zaktualizowane!" : "Coach avatar updated successfully!");
    } catch (err) {
      alert("Error updating coach avatar: " + err.message);
    }
  };

  const handleCoachAvatarFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      alert(lang === 'pl' ? "Plik jest za duży. Maksymalny rozmiar to 3 MB." : "File too large. Max size is 3 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = async (re) => {
      await saveCoachAvatar(re.target.result);
    };
    reader.readAsDataURL(file);
  };

  const saveCoachLogo = async (newLogoUrl) => {
    try {
      const cleanUid = userProfile?.uid || trainerId;
      const cleanEmail = (trainerEmail || userProfile?.email || "").toLowerCase().trim();
      const updateObj = { 
        brandLogo: newLogoUrl || null,
        brandCustom: !!newLogoUrl || !!userProfile?.brandColor
      };

      if (cleanUid) {
        await setDoc(doc(db, "users", cleanUid), updateObj, { merge: true }).catch(() => {});
      }
      if (cleanEmail) {
        await setDoc(doc(db, "users", cleanEmail), updateObj, { merge: true }).catch(() => {});
      }
      if (userProfile) {
        userProfile.brandLogo = newLogoUrl || null;
        userProfile.brandCustom = !!newLogoUrl || !!userProfile?.brandColor;
      }
      alert(lang === 'pl' ? "Logo marki zostało zaktualizowane!" : "Brand logo updated successfully!");
    } catch (err) {
      alert("Error updating brand logo: " + err.message);
    }
  };

  const handleCoachLogoFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      alert(lang === 'pl' ? "Plik jest za duży. Maksymalny rozmiar to 3 MB." : "File too large. Max size is 3 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = async (re) => {
      await saveCoachLogo(re.target.result);
    };
    reader.readAsDataURL(file);
  };

  const saveCoachBrandColor = async (colorHex) => {
    try {
      const cleanUid = userProfile?.uid || trainerId;
      const cleanEmail = (trainerEmail || userProfile?.email || "").toLowerCase().trim();
      const updateObj = { 
        brandColor: colorHex,
        brandCustom: true
      };

      if (cleanUid) {
        await setDoc(doc(db, "users", cleanUid), updateObj, { merge: true }).catch(() => {});
      }
      if (cleanEmail) {
        await setDoc(doc(db, "users", cleanEmail), updateObj, { merge: true }).catch(() => {});
      }
      if (userProfile) {
        userProfile.brandColor = colorHex;
        userProfile.brandCustom = true;
      }
      alert(lang === 'pl' ? `Kolor marki zaktualizowany na ${colorHex}!` : `Brand color updated to ${colorHex}!`);
    } catch (err) {
      alert("Error updating brand color: " + err.message);
    }
  };

  const uniqueActiveTrainees = activeTrainees.filter((trainee, index, self) => 
    self.findIndex(t => (t.email || t.id)?.toLowerCase().trim() === (trainee.email || trainee.id)?.toLowerCase().trim()) === index
  );

  const [selectedRosterTrainee, setSelectedRosterTrainee] = useState(null);

  const [isCreatingPlan, setIsCreatingPlan] = useState(false);
  const [planTitle, setPlanTitle] = useState("");
  const [planAssignedTrainee, setPlanAssignedTrainee] = useState(null);
  const [planNotes, setPlanNotes] = useState("");
  const [planDays, setPlanDays] = useState([
    { dayNumber: 1, dayLabel: lang === 'pl' ? "Dzień 1 - Dół Ciała" : "Day 1 - Lower Body", routineId: "", routineTitle: "" },
    { dayNumber: 2, dayLabel: lang === 'pl' ? "Dzień 2 - Góra Ciała" : "Day 2 - Upper Body", routineId: "", routineTitle: "" }
  ]);

  const [editingExerciseId, setEditingExerciseId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [editingNamePL, setEditingNamePL] = useState('');
  const [editingNameEN, setEditingNameEN] = useState('');
  const [editingHintsPL, setEditingHintsPL] = useState('');
  const [editingHintsEN, setEditingHintsEN] = useState('');
  const [editingImageFile, setEditingImageFile] = useState(null);
  const [isUploadingEditImage, setIsUploadingEditImage] = useState(false);

  const [coachOldPasswordInput, setCoachOldPasswordInput] = useState("");
  const [coachNewPasswordInput, setCoachNewPasswordInput] = useState("");
  const [coachConfirmPasswordInput, setCoachConfirmPasswordInput] = useState("");
  const [showCoachPasswords, setShowCoachPasswords] = useState(false);
  const [isUpdatingCoachPassword, setIsUpdatingCoachPassword] = useState(false);
  const [isSendingCoachReset, setIsSendingCoachReset] = useState(false);
  const [coachPasswordNotice, setCoachPasswordNotice] = useState(null);

  const handleCoachDirectPasswordChange = async (e) => {
    e.preventDefault();
    const cleanOld = (coachOldPasswordInput || "").trim();
    const cleanNew = (coachNewPasswordInput || "").trim();
    const cleanConfirm = (coachConfirmPasswordInput || "").trim();

    if (!cleanNew || cleanNew.length < 6) {
      setCoachPasswordNotice({
        type: 'error',
        text: lang === 'pl' ? "Nowe hasło musi mieć co najmniej 6 znaków." : "New password must be at least 6 characters long."
      });
      return;
    }

    if (cleanNew !== cleanConfirm) {
      setCoachPasswordNotice({
        type: 'error',
        text: lang === 'pl' ? "Nowe hasło i powtórzone hasło nie są identyczne." : "New password and confirmation password do not match."
      });
      return;
    }

    const targetEmail = (userProfile?.email || trainerEmail || user?.email || "").toLowerCase().trim();
    setIsUpdatingCoachPassword(true);
    setCoachPasswordNotice(null);

    try {
      if (auth.currentUser && cleanOld && auth.currentUser.email) {
        try {
          const credential = EmailAuthProvider.credential(auth.currentUser.email, cleanOld);
          await reauthenticateWithCredential(auth.currentUser, credential);
        } catch (reauthErr) {
          console.warn("Coach reauth note:", reauthErr.message);
        }
      }

      if (auth.currentUser) {
        await updatePassword(auth.currentUser, cleanNew).catch(err => console.warn("Coach Auth password note:", err.message));
      }

      const updateData = { password: cleanNew, updatedPassword: cleanNew, updatedAt: Date.now() };
      if (user?.uid) {
        await setDoc(doc(db, "users", user.uid), updateData, { merge: true }).catch(() => {});
      }
      if (targetEmail) {
        await setDoc(doc(db, "users", targetEmail), updateData, { merge: true }).catch(() => {});
      }

      setCoachOldPasswordInput("");
      setCoachNewPasswordInput("");
      setCoachConfirmPasswordInput("");
      setCoachPasswordNotice({
        type: 'success',
        text: lang === 'pl' 
          ? "✅ Hasło zostało pomyślnie zmienione! Nowe hasło jest aktywne." 
          : "✅ Password updated successfully! Your new password is active."
      });
    } catch (err) {
      setCoachPasswordNotice({
        type: 'error',
        text: "Error updating password: " + err.message
      });
    } finally {
      setIsUpdatingCoachPassword(false);
    }
  };

  const handleCoachSendPasswordReset = async () => {
    const targetEmail = userProfile?.email || trainerEmail || user?.email;
    if (!targetEmail) {
      alert(lang === 'pl' ? "Brak adresu e-mail." : "No email address found.");
      return;
    }
    setIsSendingCoachReset(true);
    setCoachPasswordNotice(null);
    try {
      await sendPasswordResetEmail(auth, targetEmail).catch(() => {});
      await sendPasswordResetNotification({ userEmail: targetEmail }).catch(() => {});
      setCoachPasswordNotice({
        type: 'success',
        text: lang === 'pl' ? `Wysłano e-mail z informacją o zmianie hasła z contact@gymghost.net na adres: ${targetEmail}` : `Password reset notice sent from contact@gymghost.net to: ${targetEmail}`
      });
    } catch (err) {
      setCoachPasswordNotice({
        type: 'error',
        text: "Error sending password reset: " + err.message
      });
    } finally {
      setIsSendingCoachReset(false);
    }
  };

  useEffect(() => {
    let unsubscribe;
    try {
      const q = query(collection(db, 'users'));
      unsubscribe = onSnapshot(q, (snapshot) => {
        const dbUsers = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        const cleanId = (trainerId || "").toLowerCase().trim();
        const cleanEmail = (trainerEmail || "").toLowerCase().trim();
        
        // Flexibly match assigned trainees whether trainerId was saved as Email address, UID, or username prefix
        const dbClients = dbUsers.filter(u => {
          if (u.role === 'trainer' || u.role === 'admin') return false;
          const email = (u.email || "").toLowerCase().trim();
          const name = (u.displayName || u.name || "").toLowerCase().trim();
          const id = (u.id || "").toLowerCase().trim();
          const isLegacyMock = email.includes('demo') || email === 'client-demo@google.com' || id === 'client_123456' || name === 'alex athlete' || id.includes('demo');
          if (isLegacyMock) {
            deleteDoc(doc(db, "users", u.id)).catch(() => {});
            return false;
          }
          const assignedTo = (u.trainerId || "").toLowerCase().trim();
          return assignedTo === cleanId || assignedTo === cleanEmail || (cleanEmail && assignedTo.includes(cleanEmail.split('@')[0]));
        });
        const active = dbClients.filter(c => c.status !== 'archived');
        const archived = dbClients.filter(c => c.status === 'archived');
        setActiveTrainees(active);
        setArchivedTrainees(archived);
        if (!selectedTrainee && active.length > 0) setSelectedTrainee(active[0]);
        if (!planAssignedTrainee && active.length > 0) setPlanAssignedTrainee(active[0]);
      }, (err) => {
        console.warn("Firestore users listener warning:", err.message);
      });
    } catch (e) {
      console.warn("Firestore init warning:", e.message);
    }
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [trainerId, trainerEmail]);

  useEffect(() => {
    const cleanCoachId = (userProfile?.id || trainerId || "").toLowerCase().trim();
    const isWieslawCoach = cleanCoachId.includes("wieslaw");
    const isMarekCoach = cleanCoachId.includes("marek");

    let unsubscribe;
    try {
      const q = query(collection(db, "plans"));
      unsubscribe = onSnapshot(q, (snapshot) => {
        const loadedPlans = snapshot.docs
          .map(doc => ({ ...doc.data(), docId: doc.id, id: doc.id }))
          .filter(p => {
            if (!p.title) return false;
            const pTrainer = (p.trainerId || "").toLowerCase().trim();
            if (isWieslawCoach) return pTrainer.includes("wieslaw") || pTrainer === "trainer_987654" || !pTrainer;
            if (isMarekCoach) return pTrainer.includes("marek") || pTrainer === "marekt@gmail.com";
            return pTrainer === cleanCoachId || (cleanCoachId && pTrainer.includes(cleanCoachId.split('@')[0]));
          });

        const sortedPlans = loadedPlans.sort((a, b) => {
          if (a.createdAt && b.createdAt) {
            return new Date(b.createdAt) - new Date(a.createdAt);
          }
          return (a.title || "").localeCompare(b.title || "");
        });
        setCreatedPlans(sortedPlans);
      }, (error) => {
        console.warn("Plans query notice:", error.message);
      });
    } catch (err) {}

    return () => { if (unsubscribe) unsubscribe(); };
  }, [userProfile, trainerId]);

  useEffect(() => {
    const cleanCoachId = (userProfile?.id || trainerId || "").toLowerCase().trim();
    const isWieslawCoach = cleanCoachId.includes("wieslaw");
    const isMarekCoach = cleanCoachId.includes("marek");

    let unsubscribe;
    try {
      const q = query(collection(db, "routines"));
      unsubscribe = onSnapshot(q, (snapshot) => {
        const loadedRoutines = snapshot.docs
          .map(docSnap => ({ ...docSnap.data(), docId: docSnap.id, id: docSnap.id }))
          .filter(r => {
            if (!r.title || !r.title.trim()) return false;
            const isGlobalRt = r.isGlobal === true || r.assignedClientId === 'GLOBAL' || r.assignedTo === 'GLOBAL' || r.trainerId === 'global';
            if (isGlobalRt) return true;

            const rTrainer = (r.trainerId || r.trainerEmail || "").toLowerCase().trim();
            if (isWieslawCoach) {
              return rTrainer.includes("wieslaw") || 
                     rTrainer === "trainer_987654" || 
                     r.assignedClientId === "ALL" || 
                     !rTrainer || 
                     (r.title && r.title.includes("2.26")) || 
                     (r.id && r.id.includes("wieslaw")) || 
                     (r.docId && r.docId.includes("wieslaw"));
            }
            if (isMarekCoach) return rTrainer.includes("marek") || rTrainer === "marekt@gmail.com";
            return rTrainer === cleanCoachId || (cleanCoachId && rTrainer.includes(cleanCoachId.split('@')[0]));
          });

        const sortedRoutines = loadedRoutines.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
        setCreatedRoutines(sortedRoutines);
      }, (error) => {
        console.warn("Routines query notice:", error.message);
      });
    } catch (err) {}

    return () => { if (unsubscribe) unsubscribe(); };
  }, [userProfile, trainerId]);

  const handleSavePlan = (e) => {
    e.preventDefault();
    if (!planTitle || !planAssignedTrainee || planDays.some(d => !d.routineId)) {
      alert(lang === 'pl' ? "Proszę uzupełnić tytuł planu, wybrać odbiorcę i przypisać trening do każdego dnia." : "Please provide a plan title, select recipient, and select a routine template for every day.");
      return;
    }

    const planId = "plan_" + crypto.randomUUID();
    const cleanCoachId = (trainerEmail || trainerId || "wieslawp@gmail.com").toLowerCase().trim();
    const targetId = planAssignedTrainee.id || planAssignedTrainee;
    const isGlobalPlan = targetId === 'GLOBAL';
    const isAllTrainees = targetId === 'ALL';

    const payload = {
      id: planId,
      trainerId: isGlobalPlan ? "global" : cleanCoachId,
      trainerEmail: cleanCoachId,
      title: planTitle,
      assignedTo: isGlobalPlan ? "GLOBAL" : (isAllTrainees ? "ALL" : (planAssignedTrainee.email || targetId).toLowerCase().trim()),
      assignedClientId: isGlobalPlan ? "GLOBAL" : (isAllTrainees ? "ALL" : (planAssignedTrainee.id || targetId).toLowerCase().trim()),
      assignedToName: isGlobalPlan ? (lang === 'pl' ? "Biblioteka Globalna" : "Global Library Standard") : (isAllTrainees ? (lang === 'pl' ? "Wszyscy moi zawodnicy" : "All My Trainees") : (planAssignedTrainee.name || planAssignedTrainee.displayName || planAssignedTrainee.email)),
      isGlobal: isGlobalPlan,
      notes: planNotes,
      createdAt: new Date().toISOString(),
      routines: planDays
    };

    setDoc(doc(db, "plans", planId), payload).catch(err => console.warn("Offline note:", err.message));
    setCreatedPlans(prev => [payload, ...prev]);
    window.dispatchEvent(new CustomEvent('plan_created', { detail: payload }));

    // Send Email Notification from contact@gymghost.net to Trainee
    if (!isGlobalPlan && payload.assignedTo && payload.assignedTo !== 'ALL' && payload.assignedTo !== 'GLOBAL' && payload.assignedTo.includes('@')) {
      sendNewPlanAssignedNotification({
        traineeEmail: payload.assignedTo,
        planTitle: payload.title,
        trainerName: userProfile?.displayName || userProfile?.name || trainerEmail || "Twój Trener"
      });
    }

    setPlanTitle("");
    setPlanNotes("");
    setPlanDays([{ dayNumber: 1, dayLabel: lang === 'pl' ? "Dzień 1" : "Day 1", routineId: "", routineTitle: "" }]);
    alert(lang === 'pl' ? `🚀 Cykl Treningowy "${payload.title}" pomyślnie wdrożony dla: ${payload.assignedToName}!` : `🚀 Training Plan "${payload.title}" successfully deployed for: ${payload.assignedToName}!`);
  };

  const handleDeletePlan = (planOrId, title) => {
    const idToDelete = typeof planOrId === 'object' ? (planOrId.docId || planOrId.id) : planOrId;
    const internalId = typeof planOrId === 'object' ? planOrId.id : planOrId;
    const planTitle = title || (typeof planOrId === 'object' ? planOrId.title : "");

    if (window.confirm(lang === 'pl' ? `Czy na pewno usunąć cykl "${planTitle}"?` : `Are you sure you want to remove plan "${planTitle}"?`)) {
      deleteDoc(doc(db, "plans", idToDelete)).catch(err => console.warn("Offline note:", err.message));
      if (internalId && internalId !== idToDelete) {
        deleteDoc(doc(db, "plans", internalId)).catch(() => {});
      }
      setCreatedPlans(prev => prev.filter(p => p.id !== idToDelete && p.docId !== idToDelete && p.id !== internalId));
      window.dispatchEvent(new CustomEvent('plan_deleted', { detail: idToDelete }));
      alert(lang === 'pl' ? `🗑️ Plan "${planTitle}" został usunięty.` : `🗑️ Plan "${planTitle}" has been removed.`);
    }
  };

  const handleChangeTraineePassword = (traineeObj) => {
    setPasswordModalUser(traineeObj);
    setNewPasswordInput("");
  };

  const handleExecuteChangePassword = async () => {
    if (!passwordModalUser) return;
    const pwCheck = validatePassword(newPasswordInput);
    if (!pwCheck.isValid) {
      alert(lang === "pl" ? pwCheck.messagePL : pwCheck.messageEN);
      return;
    }

    const targetEmail = (passwordModalUser.email || passwordModalUser.id || "").toLowerCase().trim();
    const name = passwordModalUser.displayName || passwordModalUser.name || targetEmail;
    const cleanPass = newPasswordInput.trim();

    updateDoc(doc(db, 'users', passwordModalUser.id || targetEmail), { 
      tempPassword: cleanPass, 
      updatedPassword: cleanPass, 
      lastPasswordReset: new Date().toISOString() 
    }).catch(err => console.warn("Offline password sync:", err.message));

    setActiveTrainees(prev => prev.map(c => ((c.id || c.email)?.toLowerCase().trim() === targetEmail ? { ...c, tempPassword: cleanPass, updatedPassword: cleanPass } : c)));
    window.dispatchEvent(new CustomEvent('password_updated', { detail: { email: targetEmail, newPassword: cleanPass } }));
    
    alert(lang === 'pl' 
      ? `🎉 Hasło dla podopiecznego "${name}" (${targetEmail}) zostało zmienione na: [${cleanPass}].`
      : `🎉 Success! Password for "${name}" (${targetEmail}) has been updated to: [${cleanPass}].`);
    setPasswordModalUser(null);
    setNewPasswordInput("");
  };

  const handleArchiveTrainee = (traineeObj) => {
    if (window.confirm(lang === 'pl' ? `Czy na pewno chcesz zarchiwizować profil "${traineeObj.name}"? Historia treningowa pozostanie nienaruszona.` : `Are you sure you want to archive "${traineeObj.name}"?\n\nThis soft-delete removes them from active selector rosters while safely preserving all their historical workout diaries and metrics.`)) {
      updateDoc(doc(db, 'users', traineeObj.id), { status: 'archived' })
        .catch(err => console.warn("Offline archive note:", err.message));
      
      setActiveTrainees(prev => prev.filter(c => c.id !== traineeObj.id));
      setArchivedTrainees(prev => [...prev, { ...traineeObj, status: 'archived' }]);
      if (selectedTrainee?.id === traineeObj.id) {
        const remaining = activeTrainees.filter(c => c.id !== traineeObj.id);
        setSelectedTrainee(remaining.length > 0 ? remaining[0] : null);
      }
      if (selectedRosterTrainee?.id === traineeObj.id || selectedRosterTrainee?.email === traineeObj.email) {
        setSelectedRosterTrainee(null);
      }
      alert(lang === 'pl' ? `📦 Zawodnik "${traineeObj.name}" został przeniesiony do Archiwum.` : `📦 Profile Soft-Archived: "${traineeObj.name}" is now located in your preserved Archived Roster vault.`);
    }
  };

  const handleDisconnectTrainee = async (traineeObj) => {
    const displayName = traineeObj.name || traineeObj.email || "Zawodnika";
    if (window.confirm(lang === 'pl' 
      ? `Czy na pewno chcesz odłączyć zawodnika "${displayName}" od swojego konta trenerskiego? Podopieczny utraci dostęp do Twojego programu treningowego.`
      : `Are you sure you want to disconnect trainee "${displayName}" from your coaching roster? The athlete will lose access to your training program.`
    )) {
      try {
        const updateData = {
          trainerId: null,
          trainerEmail: null,
          trainerName: null,
          coachCode: null,
          assignedTrainerId: null
        };

        if (traineeObj.id) {
          await setDoc(doc(db, "users", traineeObj.id), updateData, { merge: true }).catch(() => {});
        }
        if (traineeObj.email) {
          await setDoc(doc(db, "users", traineeObj.email), updateData, { merge: true }).catch(() => {});
        }

        // Update local state by removing trainee from active roster
        setActiveTrainees(prev => prev.filter(c => (c.id !== traineeObj.id && c.email !== traineeObj.email)));
        if (selectedTrainee?.id === traineeObj.id || selectedTrainee?.email === traineeObj.email) {
          const remaining = activeTrainees.filter(c => c.id !== traineeObj.id && c.email !== traineeObj.email);
          setSelectedTrainee(remaining.length > 0 ? remaining[0] : null);
        }
        if (selectedRosterTrainee?.id === traineeObj.id || selectedRosterTrainee?.email === traineeObj.email) {
          setSelectedRosterTrainee(null);
        }

        alert(lang === 'pl' 
          ? `🔌 Odłączono zawodnika "${displayName}".` 
          : `🔌 Disconnected trainee "${displayName}" from your roster.`
        );
      } catch (err) {
        alert("Error disconnecting trainee: " + err.message);
      }
    }
  };

  const handleRestoreTrainee = (traineeObj) => {
    updateDoc(doc(db, 'users', traineeObj.id), { status: 'active' })
      .catch(err => console.warn("Offline restore note:", err.message));
    
    setArchivedTrainees(prev => prev.filter(c => c.id !== traineeObj.id));
    const restoredObj = { ...traineeObj, status: 'active' };
    setActiveTrainees(prev => [...prev, restoredObj]);
    if (!selectedTrainee) setSelectedTrainee(restoredObj);
    alert(lang === 'pl' ? `🔄 Zawodnik "${traineeObj.name}" został przywrócony na aktywną listę!` : `🔄 Restored to Roster: "${traineeObj.name}" is once again available for active program assignment and live supervision!`);
  };

  const handleAddExerciseToRoutine = (ex) => {
    const isGlobal = ex.isGlobal || (ex.trainerId || "").toLowerCase() === 'global' || (ex.trainerId || "").toLowerCase() === 'all';
    const primaryHint = lang === 'pl'
      ? (ex.hintsPL || ex.hints_pl || ex.hints || ex.hintsEN || ex.hints_en || ex.notes || ex.description || '')
      : (ex.hintsEN || ex.hints_en || ex.hints || ex.hintsPL || ex.hints_pl || ex.notes || ex.description || '');

    setRoutineExercises(prev => [...prev, {
      id: ex.id,
      name: ex.name,
      namePL: ex.namePL || ex.name_pl || ex.name,
      nameEN: ex.nameEN || ex.name_en || ex.name,
      isGlobal: isGlobal,
      bodyPart: ex.bodyPart || ex.targetMuscle || 'General',
      targetMuscle: ex.bodyPart || ex.targetMuscle || 'General',
      sets: 3,
      setsCount: 3,
      reps: 8,
      hints: primaryHint,
      hintsPL: ex.hintsPL || ex.hints_pl || ex.hints || '',
      hintsEN: ex.hintsEN || ex.hints_en || ex.hints || '',
      notes: primaryHint
    }]);
  };

  const handleCreateExercise = async (formData) => {
    const namePL = (formData?.namePL || customExerciseNamePL || "").trim();
    const nameEN = (formData?.nameEN || customExerciseNameEN || "").trim();
    if (!namePL && !nameEN) {
      alert(lang === 'pl' ? "Proszę wpisać nazwę ćwiczenia przynajmniej w jednym języku." : "Please enter an exercise name in at least one language.");
      return;
    }

    setIsUploadingCoachImage(true);
    try {
      let uploadedUrl = (formData?.imageUrlInput || customExerciseImageUrlInput || "").trim();
      let imagePath = "";
      const cleanCoachId = (trainerEmail || trainerId || "trainer_wieslaw").toLowerCase().trim().replace(/[^a-z0-9]/g, '_');

      const targetFile = formData?.imageFile || customExerciseImageFile;

      if (targetFile) {
        const fileName = `${Date.now()}_${targetFile.name}`;
        imagePath = `exercises/coaches/${cleanCoachId}/${fileName}`;
        
        // 5-second race timeout with base64 fallback so it NEVER freezes
        const convertToBase64 = (fileObj) => new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result || "");
          reader.onerror = () => resolve("");
          reader.readAsDataURL(fileObj);
        });

        try {
          const storageRef = ref(storage, imagePath);
          const gcsPromise = uploadBytes(storageRef, targetFile).then(() => getDownloadURL(storageRef));
          const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error("Storage timeout")), 5000));
          uploadedUrl = await Promise.race([gcsPromise, timeoutPromise]);
        } catch (storageErr) {
          console.warn("GCS upload bypassed/timed out, using inline image fallback:", storageErr.message);
          uploadedUrl = await convertToBase64(targetFile);
        }
      }

      const primaryName = namePL || nameEN;
      const primaryHints = formData?.instructionsPL || formData?.instructionsEN || customExerciseHintsPL.trim() || customExerciseHintsEN.trim();
      const exId = "ex_" + Date.now();

      const newEx = {
        id: exId,
        docId: exId,
        exerciseId: exId,
        name: primaryName,
        namePL: namePL || primaryName,
        nameEN: nameEN || primaryName,
        name_translations: {
          pl: namePL || primaryName,
          en: nameEN || primaryName
        },
        category: formData?.bodyPart || customExerciseBodyPart,
        body_part: formData?.bodyPart || customExerciseBodyPart,
        bodyPart: formData?.bodyPart || customExerciseBodyPart,
        equipment: (formData?.equipment || customExerciseEquipment || "body weight").toLowerCase(),
        target: (formData?.target || customExerciseTarget || "").trim(),
        subPart: (formData?.subPart || customExerciseSubPart || "").trim() || (formData?.target || customExerciseTarget || "").trim(),
        muscle_group: (formData?.muscle_group || customExerciseMuscleGroup || "").trim() || (formData?.bodyPart || customExerciseBodyPart),
        secondary_muscles: Array.isArray(formData?.secondary_muscles) ? formData.secondary_muscles : (customExerciseSecondaryMuscles ? customExerciseSecondaryMuscles.split(',').map(s => s.trim()).filter(Boolean) : []),
        instructions: {
          pl: formData?.instructionsPL || customExerciseInstructionsPL.trim() || customExerciseHintsPL.trim(),
          en: formData?.instructionsEN || customExerciseInstructionsEN.trim() || customExerciseHintsEN.trim()
        },
        instruction_steps: {
          pl: (formData?.instructionsPL || customExerciseInstructionsPL.trim() || customExerciseHintsPL.trim()).split('\n').filter(Boolean),
          en: (formData?.instructionsEN || customExerciseInstructionsEN.trim() || customExerciseHintsEN.trim()).split('\n').filter(Boolean)
        },
        hints: primaryHints,
        notes: primaryHints,
        hintsPL: formData?.instructionsPL || customExerciseHintsPL.trim() || primaryHints,
        hintsEN: formData?.instructionsEN || customExerciseHintsEN.trim() || primaryHints,
        imageUrl: uploadedUrl,
        image: uploadedUrl,
        gif_url: uploadedUrl,
        imagePath: imagePath,
        media_id: exId,
        attribution: "Gym visual",
        youtubeUrl: (formData?.youtubeUrl || customExerciseYoutubeUrl || "").trim(),
        videoUrl: (formData?.youtubeUrl || customExerciseYoutubeUrl || "").trim(),
        isGlobal: false,
        trainerId: (trainerEmail || trainerId || "trainer_wieslaw").toLowerCase().trim(),
        createdAt: Date.now()
      };

      await setDoc(doc(db, 'exercises', exId), newEx);
      window.dispatchEvent(new CustomEvent('custom_exercise_created', { detail: newEx }));
      window.dispatchEvent(new CustomEvent('exercise_created', { detail: newEx }));
      alert(lang === 'pl' ? `✅ Dodano nowe ćwiczenie "${primaryName}" do Twojego katalogu!` : `✅ Exercise "${primaryName}" added to your catalog!`);
      
      setCustomExerciseNamePL('');
      setCustomExerciseNameEN('');
      setCustomExerciseEquipment('body weight');
      setCustomExerciseSubPart('');
      setCustomExerciseTarget('');
      setCustomExerciseMuscleGroup('');
      setCustomExerciseSecondaryMuscles('');
      setCustomExerciseInstructionsPL('');
      setCustomExerciseInstructionsEN('');
      setCustomExerciseHintsPL('');
      setCustomExerciseHintsEN('');
      setCustomExerciseMediaId('');
      setCustomExerciseAttribution('Gym visual');
      setCustomExerciseImageFile(null);
      setCustomExerciseImageUrlInput('');
      setCustomExerciseYoutubeUrl('');
    } catch (err) {
      console.error("Coach exercise creation error:", err);
      alert("Error creating exercise: " + err.message);
    } finally {
      setIsUploadingCoachImage(false);
    }
  };

  const handleDeleteExercise = (id, name, silent = false) => {
    if (silent || window.confirm(lang === 'pl' ? `Czy na pewno usunąć ćwiczenie "${name}" ze swojego katalogu?` : `Are you sure you want to completely remove "${name}" from your coaching library?`)) {
      deleteDoc(doc(db, 'exercises', id)).catch(err => console.warn("Offline note:", err.message));
      window.dispatchEvent(new CustomEvent('custom_exercise_deleted', { detail: id }));
      window.dispatchEvent(new CustomEvent('exercise_deleted', { detail: id }));
      if (!silent) {
        alert(lang === 'pl' ? `🗑️ Ćwiczenie "${name}" zostało usunięte.` : `🗑️ Exercise "${name}" has been removed from your catalog.`);
      }
    }
  };

  const handleUpdateExercise = async (id, currentEx = {}, formData = null) => {
    const namePL = (formData?.namePL || editingNamePL || "").trim();
    const nameEN = (formData?.nameEN || editingNameEN || "").trim();
    const primaryName = namePL || nameEN || (formData?.name || editingName || "").trim();

    if (!primaryName) {
      alert(lang === 'pl' ? "Nazwa ćwiczenia nie może być pusta." : "Exercise name cannot be empty.");
      return;
    }

    setIsUploadingEditImage(true);
    try {
      let uploadedUrl = (formData?.imageUrlInput || editingImageUrlInput || "").trim() || currentEx.imageUrl || "";
      let imagePath = currentEx.imagePath || "";

      const fileToUpload = formData?.imageFile || editingImageFile;

      if (fileToUpload) {
        const cleanCoachId = (trainerEmail || trainerId || "trainer_wieslaw").toLowerCase().trim().replace(/[^a-z0-9]/g, '_');
        const fileName = `${Date.now()}_${fileToUpload.name}`;
        imagePath = `exercises/coaches/${cleanCoachId}/${fileName}`;
        
        const convertToBase64 = (fileObj) => new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result || "");
          reader.onerror = () => resolve("");
          reader.readAsDataURL(fileObj);
        });

        try {
          const storageRef = ref(storage, imagePath);
          const gcsPromise = uploadBytes(storageRef, fileToUpload).then(() => getDownloadURL(storageRef));
          const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error("Storage timeout")), 5000));
          uploadedUrl = await Promise.race([gcsPromise, timeoutPromise]);
        } catch (storageErr) {
          console.warn("GCS upload bypassed/timed out, using inline image fallback:", storageErr.message);
          uploadedUrl = await convertToBase64(fileToUpload);
        }
      }

      const primaryHints = formData?.instructionsPL || formData?.instructionsEN || editingHintsPL.trim() || editingHintsEN.trim() || currentEx.hints || "";
      const isAlreadyGlobal = currentEx.isGlobal === true || (currentEx.trainerId || "").toLowerCase() === 'global' || (currentEx.trainerId || "").toLowerCase() === 'all';

      const updatedPayload = {
        id: id,
        docId: id,
        exerciseId: id,
        name: primaryName,
        namePL: namePL || primaryName,
        nameEN: nameEN || primaryName,
        name_translations: {
          pl: namePL || primaryName,
          en: nameEN || primaryName
        },
        category: formData?.bodyPart || editingBodyPart,
        body_part: formData?.bodyPart || editingBodyPart,
        bodyPart: formData?.bodyPart || editingBodyPart,
        equipment: (formData?.equipment || editingEquipment || "body weight").toLowerCase(),
        target: (formData?.target || editingTarget || "").trim(),
        subPart: (formData?.subPart || editingSubPart || "").trim() || (formData?.target || editingTarget || "").trim(),
        muscle_group: (formData?.muscle_group || editingMuscleGroup || "").trim() || (formData?.bodyPart || editingBodyPart),
        secondary_muscles: formData?.secondary_muscles ? formData.secondary_muscles : (editingSecondaryMuscles ? (Array.isArray(editingSecondaryMuscles) ? editingSecondaryMuscles : editingSecondaryMuscles.split(',').map(s => s.trim()).filter(Boolean)) : []),
        instructions: {
          pl: formData?.instructionsPL || editingInstructionsPL.trim() || editingHintsPL.trim(),
          en: formData?.instructionsEN || editingInstructionsEN.trim() || editingHintsEN.trim()
        },
        instruction_steps: {
          pl: (formData?.instructionsPL || editingInstructionsPL.trim() || editingHintsPL.trim()).split('\n').filter(Boolean),
          en: (formData?.instructionsEN || editingInstructionsEN.trim() || editingHintsEN.trim()).split('\n').filter(Boolean)
        },
        hints: primaryHints,
        hintsPL: formData?.instructionsPL || editingHintsPL.trim() || primaryHints,
        hintsEN: formData?.instructionsEN || editingHintsEN.trim() || primaryHints,
        notes: primaryHints,
        imageUrl: uploadedUrl,
        image: uploadedUrl,
        gif_url: uploadedUrl,
        imagePath: imagePath,
        media_id: id,
        attribution: "Gym visual",
        youtubeUrl: (formData?.youtubeUrl || editingYoutubeUrl || "").trim(),
        videoUrl: (formData?.youtubeUrl || editingYoutubeUrl || "").trim(),
        isGlobal: isAlreadyGlobal,
        trainerId: isAlreadyGlobal ? (currentEx.trainerId || "global") : (trainerEmail || trainerId || "trainer_wieslaw").toLowerCase().trim(),
        updatedAt: Date.now()
      };

      await setDoc(doc(db, 'exercises', id), updatedPayload, { merge: true });
      window.dispatchEvent(new CustomEvent('custom_exercise_updated', { detail: updatedPayload }));
      window.dispatchEvent(new CustomEvent('exercise_updated', { detail: updatedPayload }));
      setEditingExerciseId(null);
      setEditingImageFile(null);
      setEditingImageUrlInput("");
      setEditingYoutubeUrl("");
      alert(lang === 'pl' ? `✏️ Zaktualizowano ćwiczenie "${primaryName}"!` : `✏️ Updated exercise "${primaryName}"!`);
    } catch (err) {
      console.error("Exercise update error:", err);
      alert("Update error: " + err.message);
    } finally {
      setIsUploadingEditImage(false);
    }
  };

  const handleSaveRoutine = () => {
    if (!routineTitle || routineExercises.length === 0) {
      alert(lang === 'pl' ? "Proszę podać tytuł treningu i wybrać chociaż jedno ćwiczenie." : "Please enter a routine title and choose at least one exercise from the catalog.");
      return;
    }

    const formattedExercises = routineExercises.map(ex => ({
      id: ex.id,
      name: ex.name,
      bodyPart: ex.bodyPart || ex.targetMuscle || 'Legs',
      targetMuscle: ex.targetMuscle || ex.bodyPart || 'Legs',
      sets: parseInt(ex.sets || ex.setsCount) || 3,
      setsCount: parseInt(ex.sets || ex.setsCount) || 3,
      reps: parseInt(ex.reps) || 8,
      hints: ex.hints || ex.notes || "",
      notes: ex.notes || ex.hints || ""
    }));

    const finalTag = routineTag === 'CUSTOM' ? (customRoutineTag.trim() || 'Custom') : routineTag;
    const cleanCoachId = (trainerEmail || trainerId || "wieslawp@gmail.com").toLowerCase().trim();
    const isGlobalRoutine = assignedClientId === 'GLOBAL';

    const newRoutine = {
      id: "routine_" + Date.now(),
      trainerId: isGlobalRoutine ? "global" : cleanCoachId,
      trainerEmail: cleanCoachId,
      assignedClientId: assignedClientId || 'ALL',
      assignedTo: assignedClientId || 'ALL',
      creationDate: routineDate || new Date().toISOString().split('T')[0],
      title: routineTitle,
      tag: finalTag,
      isGlobal: isGlobalRoutine,
      exercises: formattedExercises
    };
    addDoc(collection(db, 'routines'), newRoutine).catch(err => console.warn("Offline note:", err.message));
    
    setCreatedRoutines(prev => [newRoutine, ...prev]);
    window.dispatchEvent(new CustomEvent('routine_created', { detail: newRoutine }));
    
    const targetName = assignedClientId === 'ALL' 
      ? (lang === 'pl' ? "Wszyscy moi zawodnicy" : "All My Trainees") 
      : (assignedClientId === 'GLOBAL' ? (lang === 'pl' ? "Biblioteka Globalna" : "Global Library") : (activeTrainees.find(c => c.id === assignedClientId)?.name || "selected trainee"));

    alert(lang === 'pl' ? `🎯 Szablon treningu "${routineTitle}" [#${finalTag}] został zapisany!\n\nPrzypisano do: ${targetName}` : `🎯 Routine template "${routineTitle}" [#${finalTag}] saved successfully!\n\nTarget recipient: ${targetName}`);
    
    setRoutineTitle('');
    setRoutineExercises([]);
    setAssignedClientId('ALL');
    setSelectedExercises([]);
  };

  const handleDeleteRoutine = (routineOrId) => {
    const idToDelete = typeof routineOrId === 'object' ? (routineOrId.docId || routineOrId.id) : routineOrId;
    const internalId = typeof routineOrId === 'object' ? routineOrId.id : routineOrId;
    
    if (window.confirm(lang === 'pl' ? "Czy na pewno chcesz usunąć ten program z katalogu?" : "Are you sure you want to remove this routine?")) {
      deleteDoc(doc(db, "routines", idToDelete)).catch(err => console.warn("Offline note:", err.message));
      if (internalId && internalId !== idToDelete) {
        deleteDoc(doc(db, "routines", internalId)).catch(() => {});
      }

      // Automatically purge deleted routine pointer from any multi-day plans in Firestore!
      if (createdPlans && createdPlans.length > 0) {
        createdPlans.forEach(plan => {
          if (plan.routines && plan.routines.some(r => r.routineId === idToDelete || r.id === idToDelete || r.routineId === internalId || r.id === internalId)) {
            const updatedPlanRoutines = plan.routines.filter(r => r.routineId !== idToDelete && r.id !== idToDelete && r.routineId !== internalId && r.id !== internalId);
            updateDoc(doc(db, 'plans', plan.id), { routines: updatedPlanRoutines }).catch(() => {});
            setCreatedPlans(prev => prev.map(p => p.id === plan.id ? { ...p, routines: updatedPlanRoutines } : p));
          }
        });
      }

      setCreatedRoutines(prev => prev.filter(r => r.id !== idToDelete && r.docId !== idToDelete && r.id !== internalId));
      window.dispatchEvent(new CustomEvent('routine_deleted', { detail: idToDelete }));
    }
  };

  const handleUpdatePlan = (id, newTitle, newNotes, updatedRoutines, assignedTo, assignedToName) => {
    const cleanCoachId = (trainerEmail || trainerId || "wieslawp@gmail.com").toLowerCase().trim();
    
    const payload = {};
    if (newTitle !== undefined) payload.title = newTitle;
    if (newNotes !== undefined) payload.notes = newNotes;
    if (updatedRoutines !== undefined) payload.routines = updatedRoutines;
    
    if (assignedTo !== undefined) {
      const cleanAssign = assignedTo.toString().trim().toUpperCase();
      if (cleanAssign === 'GLOBAL') {
        payload.assignedTo = 'GLOBAL';
        payload.assignedClientId = 'GLOBAL';
        payload.isGlobal = true;
        payload.trainerId = 'global';
      } else if (cleanAssign === 'ALL' || cleanAssign === 'ALL_TRAINEES') {
        payload.assignedTo = 'ALL';
        payload.assignedClientId = 'ALL';
        payload.isGlobal = false;
        payload.trainerId = cleanCoachId;
      } else {
        payload.assignedTo = assignedTo.toLowerCase().trim();
        payload.assignedClientId = assignedTo.toLowerCase().trim();
        payload.isGlobal = false;
        payload.trainerId = cleanCoachId;
      }
    }
    if (assignedToName !== undefined) payload.assignedToName = assignedToName || "";

    updateDoc(doc(db, 'plans', id), payload).catch(err => console.warn("Offline note:", err.message));
    setCreatedPlans(prev => prev.map(p => p.id === id ? { ...p, ...payload } : p));
    window.dispatchEvent(new CustomEvent('plan_updated', { detail: { id, ...payload } }));
    alert(lang === 'pl' ? "Zmiany w planie oraz przypisanie podopiecznego zostały zapisane!" : "Plan modifications and trainee assignment saved successfully!");
  };

  const handleUpdateRoutine = (id, newTitle, newExercises, newTag) => {
    const payload = {};
    if (newTitle !== undefined) payload.title = newTitle;
    if (newExercises !== undefined) payload.exercises = newExercises;
    if (newTag !== undefined) payload.tag = newTag;

    updateDoc(doc(db, 'routines', id), payload).catch(err => console.warn("Offline note:", err.message));
    setCreatedRoutines(prev => prev.map(r => r.id === id ? { ...r, ...payload } : r));
    
    // Also automatically sync routine titles into any existing training plans referencing this routine!
    if (newTitle !== undefined && createdPlans) {
      createdPlans.forEach(plan => {
        if (plan.routines && plan.routines.some(r => r.routineId === id)) {
          const updatedDays = plan.routines.map(r => r.routineId === id ? { ...r, routineTitle: newTitle } : r);
          updateDoc(doc(db, 'plans', plan.id), { routines: updatedDays }).catch(() => {});
          setCreatedPlans(prev => prev.map(p => p.id === plan.id ? { ...p, routines: updatedDays } : p));
        }
      });
    }

    window.dispatchEvent(new CustomEvent('routine_updated', { detail: { id, ...payload } }));
    alert(lang === 'pl' ? `✅ Zapisano zmiany w programie! Nowa kolejność ćwiczeń i nazwa zaktualizowana automatycznie u Twoich zawodników.` : `✅ Routine updated! New exercise ordering and title instantly synced to athlete diaries.`);
  };

  const handleReassignRoutine = (id, newClientId) => {
    const cleanCoachId = (trainerEmail || trainerId || "wieslawp@gmail.com").toLowerCase().trim();
    const isGlobal = newClientId === 'GLOBAL';
    const payload = {
      assignedClientId: newClientId,
      assignedTo: newClientId,
      isGlobal: isGlobal,
      trainerId: isGlobal ? "global" : cleanCoachId
    };

    setCreatedRoutines(prev => prev.map(r => r.id === id ? { ...r, ...payload } : r));
    setDoc(doc(db, 'routines', id), payload, { merge: true }).catch(err => console.warn("Offline note:", err.message));
    
    const targetName = newClientId === 'ALL' 
      ? (lang === 'pl' ? "Wszyscy moi zawodnicy" : "All My Trainees") 
      : (newClientId === 'GLOBAL' ? (lang === 'pl' ? "Biblioteka Globalna" : "Global Library") : (activeTrainees.find(c => c.id === newClientId)?.name || "selected athlete"));

    alert(lang === 'pl' ? `🔀 Program pomyślnie przypisany do: ${targetName}!` : `🔀 Program successfully reassigned to: ${targetName}!`);
  };

  return (
    <div className="flex flex-col w-full bg-[#0a101d] text-slate-100 min-h-screen relative">
      <header className="w-full bg-slate-950/95 backdrop-blur-md border-b border-slate-800/80 sticky top-0 z-50 shadow-xl">
        <div className="w-full max-w-[1700px] mx-auto px-4 lg:px-8 py-3 flex flex-wrap lg:flex-nowrap items-center justify-between gap-4">
          
          {/* LEFT SECTION: LOGO + COACH PROFILE */}
          <div className="flex items-center gap-4 min-w-0 shrink-0">
            {/* Logo */}
            {branding.logo && (branding.logo.startsWith('http://') || branding.logo.startsWith('https://') || branding.logo.startsWith('data:image/') || branding.logo.startsWith('/') || /\.(png|jpg|jpeg|svg|webp|gif)($|\?)/i.test(branding.logo)) ? (
              <div className="h-10 flex items-center shrink-0">
                <img 
                  src={branding.logo} 
                  alt="Team Logo" 
                  className="max-h-10 w-auto object-contain rounded-xl border border-slate-800 p-1 bg-slate-950/40 shadow-sm"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            ) : (
              <div className="flex items-center gap-2.5 shrink-0">
                <div 
                  style={{ backgroundColor: `${branding.color}15`, borderColor: `${branding.color}35`, color: branding.color }} 
                  className="w-9 h-9 rounded-xl border flex items-center justify-center text-base font-black shrink-0 shadow-sm"
                >
                  {branding.logo?.slice(0, 2).toUpperCase() || "GG"}
                </div>
                <h1 className="text-base font-black text-white leading-none m-0 tracking-tight hidden sm:block">
                  {branding.logo || "GymGhost"}
                </h1>
              </div>
            )}

            {/* Divider */}
            <div className="h-7 w-[1px] bg-slate-800/90 shrink-0" />

            {/* Coach/Trainer Profile Card */}
            <div className="flex items-center gap-3 min-w-0">
              <div 
                style={{ borderColor: branding.color, boxShadow: `0 0 10px ${branding.color}35` }}
                className="w-10 h-10 rounded-full border-2 overflow-hidden shrink-0 transition hover:scale-105"
              >
                {userProfile?.avatarUrl || isWieslaw || isMarek ? (
                  <img 
                    src={userProfile?.avatarUrl || (isMarek ? "/marek_avatar.jpg" : "/wieslaw_avatar.jpg")} 
                    alt={trainerDisplayName} 
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <div className="w-full h-full bg-slate-900 flex items-center justify-center text-base">
                    <span className="material-symbols-outlined text-slate-300">fitness_center</span>
                  </div>
                )}
              </div>
              
              <div className="flex flex-col justify-center min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs font-black text-slate-100 truncate max-w-[140px] sm:max-w-[200px]">
                    {trainerDisplayName}
                  </span>
                  <span 
                    className={`w-2 h-2 rounded-full shrink-0 ${navigator.onLine ? 'bg-emerald-400 animate-pulse shadow-[0_0_6px_#10b981]' : 'bg-red-500 shadow-[0_0_6px_#ef4444]'}`} 
                    title={navigator.onLine ? (lang === 'pl' ? "Synchronizacja aktywna (Online)" : "Real-time sync active (Online)") : (lang === 'pl' ? "Brak połączenia (Offline)" : "No connection (Offline)")} 
                  />
                </div>

                <div className="flex items-center gap-1.5 whitespace-nowrap text-[10px] mt-0.5">
                  <span className="text-slate-400 font-extrabold uppercase tracking-wider">
                    {t('common.roleTrainer', 'TRENER')}
                  </span>
                  <span className="text-slate-700 font-bold">•</span>
                  <button
                    type="button"
                    onClick={() => {
                      const codeToCopy = userProfile?.coachCode || (trainerEmail?.includes('wieslaw') ? 'GHOST-WIESLAW' : 'GHOST-JAN');
                      navigator.clipboard.writeText(codeToCopy);
                      alert(lang === 'pl' ? `Skopiowano Twój kod trenera dla podopiecznych: ${codeToCopy}` : `Copied your coach code: ${codeToCopy}`);
                    }}
                    className="bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 px-2 py-0.5 rounded-md text-[10px] font-mono font-bold cursor-pointer transition flex items-center gap-1 shadow-2xs active:scale-95"
                    title={lang === 'pl' ? "Kliknij, aby skopiować Twój kod dla zawodników" : "Click to copy code for athletes"}
                  >
                    <span>KOD: {userProfile?.coachCode || (trainerEmail?.includes('wieslaw') ? 'GHOST-WIESLAW' : 'GHOST-JAN')}</span>
                    <span className="material-symbols-outlined text-[10px]">content_copy</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT SECTION: NAVIGATION TABS + HELP & LOGOUT */}
          <div className="flex items-center gap-3 shrink-0 ml-auto lg:ml-0 overflow-x-auto max-w-full py-0.5 scrollbar-none">
            {/* Navigation Tabs Container */}
            <nav className="flex items-center gap-1 bg-slate-900/90 border border-slate-800/90 rounded-xl p-1 shadow-inner shrink-0">
              {[
                { id: 'roster', label: t('admin.nav.clients', t('nav.clientRoster', 'Lista Podopiecznych')), icon: 'group' },
                { id: 'assign', label: t('nav.createAssign', 'Twórz i Przypisuj'), icon: 'fitness_center' },
                { id: 'catalog', label: lang === 'pl' ? 'Katalog Ćwiczeń' : 'Exercise Catalog', icon: 'reorder' },
                { id: 'live', label: t('nav.liveTracker', 'Monitor Na Żywo'), icon: 'bolt' },
                { id: 'settings', label: lang === 'pl' ? 'Ustawienia' : 'Settings', icon: 'settings' }
              ].map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    style={isActive ? { 
                      backgroundColor: branding.color,
                      boxShadow: `0 3px 12px ${branding.color}35`
                    } : {}}
                    className={`px-3 py-1.5 rounded-lg text-xs font-black flex items-center gap-1.5 transition-all duration-150 cursor-pointer select-none whitespace-nowrap ${
                      isActive 
                        ? 'text-slate-950' 
                        : 'text-slate-400 hover:text-slate-100 hover:bg-slate-800/60 bg-transparent border-0'
                    }`}
                  >
                    <span className="material-symbols-outlined text-sm leading-none">{tab.icon}</span>
                    <span className="inline">{tab.label}</span>
                  </button>
                );
              })}
            </nav>

            {/* Action Buttons: Help & Logout */}
            <div className="flex items-center gap-2 shrink-0">
              <a
                href="mailto:contact@gymghost.net?subject=GymGhost%20-%20Wsparcie%20dla%20Trenera"
                className="bg-slate-900/90 hover:bg-emerald-500/10 text-slate-300 hover:text-emerald-400 border border-slate-800 hover:border-emerald-500/30 text-xs font-extrabold h-9 px-3 rounded-xl transition duration-150 shrink-0 flex items-center justify-center gap-1.5 cursor-pointer shadow-sm active:scale-95 no-underline"
                title={lang === 'pl' ? 'Pomoc i Wsparcie (contact@gymghost.net)' : 'Help & Support (contact@gymghost.net)'}
              >
                <span className="material-symbols-outlined text-sm text-emerald-400">help</span>
                <span className="hidden sm:inline">{lang === 'pl' ? 'Pomoc' : 'Help'}</span>
              </a>

              <button 
                onClick={onSignOut}
                className="bg-slate-900/90 hover:bg-red-500/10 text-slate-300 hover:text-red-400 border border-slate-800 hover:border-red-500/20 text-xs font-extrabold h-9 px-3 rounded-xl transition duration-150 shrink-0 flex items-center justify-center gap-1.5 cursor-pointer shadow-sm active:scale-95"
                title={t('common.signOut', 'Wyloguj się z platformy')}
              >
                <span className="material-symbols-outlined text-sm text-red-400">logout</span> 
                <span className="hidden sm:inline">{t('common.signOut', 'Wyloguj się')}</span>
              </button>
            </div>
          </div>

        </div>
      </header>

      <main className="w-full px-4 sm:px-6 md:px-8 py-6 max-w-7xl mx-auto flex-1 pb-20">
        {activeTab === 'live' && (
          <RenderLiveTracker 
            clients={uniqueActiveTrainees} 
            selectedTrainee={selectedTrainee} 
            setSelectedTrainee={setSelectedTrainee}
            traineeSearchQuery={traineeSearchQuery}
            setTraineeSearchQuery={setTraineeSearchQuery}
          />
        )}

        {activeTab === 'assign' && (
          <RenderCreateAndAssign
            trainerId={trainerId}
            trainerEmail={trainerEmail}
            routineTitle={routineTitle} setRoutineTitle={setRoutineTitle}
            routineDate={routineDate} setRoutineDate={setRoutineDate}
            assignedClientId={assignedClientId} setAssignedClientId={setAssignedClientId}
            routineTag={routineTag} setRoutineTag={setRoutineTag}
            customRoutineTag={customRoutineTag} setCustomRoutineTag={setCustomRoutineTag}
            clients={uniqueActiveTrainees} exercises={exercises}
            routineExercises={routineExercises} setRoutineExercises={setRoutineExercises}
            setSelectedExercises={setSelectedExercises}
            handleAddExerciseToRoutine={handleAddExerciseToRoutine}
            handleSaveRoutine={handleSaveRoutine}
            createdRoutines={createdRoutines}
            isCreatingPlan={isCreatingPlan} setIsCreatingPlan={setIsCreatingPlan}
            planTitle={planTitle} setPlanTitle={setPlanTitle}
            planAssignedTrainee={planAssignedTrainee} setPlanAssignedTrainee={setPlanAssignedTrainee}
            planNotes={planNotes} setPlanNotes={setPlanNotes}
            planDays={planDays} setPlanDays={setPlanDays}
            handleSavePlan={handleSavePlan}
          />
        )}

        {activeTab === 'catalog' && (
          <RenderMasterCatalog
            exercises={exercises}
            customExerciseNamePL={customExerciseNamePL} setCustomExerciseNamePL={setCustomExerciseNamePL}
            customExerciseNameEN={customExerciseNameEN} setCustomExerciseNameEN={setCustomExerciseNameEN}
            customExerciseHintsPL={customExerciseHintsPL} setCustomExerciseHintsPL={setCustomExerciseHintsPL}
            customExerciseHintsEN={customExerciseHintsEN} setCustomExerciseHintsEN={setCustomExerciseHintsEN}
            customExerciseImageFile={customExerciseImageFile} setCustomExerciseImageFile={setCustomExerciseImageFile}
            customExerciseImageUrlInput={customExerciseImageUrlInput} setCustomExerciseImageUrlInput={setCustomExerciseImageUrlInput}
            customExerciseYoutubeUrl={customExerciseYoutubeUrl} setCustomExerciseYoutubeUrl={setCustomExerciseYoutubeUrl}
            isUploadingCoachImage={isUploadingCoachImage}
            customExerciseBodyPart={customExerciseBodyPart} setCustomExerciseBodyPart={setCustomExerciseBodyPart}
            customExerciseEquipment={customExerciseEquipment} setCustomExerciseEquipment={setCustomExerciseEquipment}
            customExerciseSubPart={customExerciseSubPart} setCustomExerciseSubPart={setCustomExerciseSubPart}
            customExerciseTarget={customExerciseTarget} setCustomExerciseTarget={setCustomExerciseTarget}
            customExerciseMuscleGroup={customExerciseMuscleGroup} setCustomExerciseMuscleGroup={setCustomExerciseMuscleGroup}
            customExerciseSecondaryMuscles={customExerciseSecondaryMuscles} setCustomExerciseSecondaryMuscles={setCustomExerciseSecondaryMuscles}
            customExerciseInstructionsPL={customExerciseInstructionsPL} setCustomExerciseInstructionsPL={setCustomExerciseInstructionsPL}
            customExerciseInstructionsEN={customExerciseInstructionsEN} setCustomExerciseInstructionsEN={setCustomExerciseInstructionsEN}
            customExerciseMediaId={customExerciseMediaId} setCustomExerciseMediaId={setCustomExerciseMediaId}
            customExerciseAttribution={customExerciseAttribution} setCustomExerciseAttribution={setCustomExerciseAttribution}
            handleCreateExercise={handleCreateExercise}
            editingExerciseId={editingExerciseId} setEditingExerciseId={setEditingExerciseId}
            editingName={editingName} setEditingName={setEditingName}
            editingNamePL={editingNamePL} setEditingNamePL={setEditingNamePL}
            editingNameEN={editingNameEN} setEditingNameEN={setEditingNameEN}
            editingHintsPL={editingHintsPL} setEditingHintsPL={setEditingHintsPL}
            editingHintsEN={editingHintsEN} setEditingHintsEN={setEditingHintsEN}
            editingBodyPart={editingBodyPart} setEditingBodyPart={setEditingBodyPart}
            editingEquipment={editingEquipment} setEditingEquipment={setEditingEquipment}
            editingSubPart={editingSubPart} setEditingSubPart={setEditingSubPart}
            editingTarget={editingTarget} setEditingTarget={setEditingTarget}
            editingMuscleGroup={editingMuscleGroup} setEditingMuscleGroup={setEditingMuscleGroup}
            editingSecondaryMuscles={editingSecondaryMuscles} setEditingSecondaryMuscles={setEditingSecondaryMuscles}
            editingInstructionsPL={editingInstructionsPL} setEditingInstructionsPL={setEditingInstructionsPL}
            editingInstructionsEN={editingInstructionsEN} setEditingInstructionsEN={setEditingInstructionsEN}
            editingMediaId={editingMediaId} setEditingMediaId={setEditingMediaId}
            editingAttribution={editingAttribution} setEditingAttribution={setEditingAttribution}
            editingImageFile={editingImageFile} setEditingImageFile={setEditingImageFile}
            editingImageUrlInput={editingImageUrlInput} setEditingImageUrlInput={setEditingImageUrlInput}
            editingYoutubeUrl={editingYoutubeUrl} setEditingYoutubeUrl={setEditingYoutubeUrl}
            isUploadingEditImage={isUploadingEditImage}
            handleUpdateExercise={handleUpdateExercise}
            handleDeleteExercise={handleDeleteExercise}
            handleAddExerciseToRoutine={handleAddExerciseToRoutine}
            routineExercises={routineExercises} setRoutineExercises={setRoutineExercises}
            selectedExercises={selectedExercises} setSelectedExercises={setSelectedExercises}
            setActiveTab={setActiveTab}
            setEnlargedImage={setEnlargedImage}
          />
        )}

        {activeTab === 'roster' && (
          <RenderClientRoster
            clients={uniqueActiveTrainees}
            archivedTrainees={archivedTrainees}
            selectedRosterTrainee={selectedRosterTrainee}
            setSelectedRosterTrainee={setSelectedRosterTrainee}
            isArchiveListExpanded={isArchiveListExpanded} setIsArchiveListExpanded={setIsArchiveListExpanded}
            handleArchiveTrainee={handleArchiveTrainee}
            handleDisconnectTrainee={handleDisconnectTrainee}
            handleChangeTraineePassword={handleChangeTraineePassword}
            handleRestoreTrainee={handleRestoreTrainee}
            createdRoutines={createdRoutines}
            handleDeleteRoutine={handleDeleteRoutine}
            handleReassignRoutine={handleReassignRoutine}
            handleUpdateRoutine={handleUpdateRoutine}
            createdPlans={createdPlans}
            handleDeletePlan={handleDeletePlan}
            handleUpdatePlan={handleUpdatePlan}
            exercises={exercises}
            branding={branding}
            trainerId={trainerId}
            trainerEmail={trainerEmail}
            isMarek={isMarek}
            setActiveTab={setActiveTab}
            setSelectedTrainee={setSelectedTrainee}
          />
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6 animate-fade-in max-w-4xl">
            <div className="border-b border-slate-800 pb-4">
              <div className="flex items-center gap-3 mb-2">
                <span className="material-symbols-outlined text-3xl text-indigo-400">settings</span>
                <h2 className="text-2xl md:text-3xl font-black text-white m-0 tracking-tight leading-none">
                  {lang === 'pl' ? "Ustawienia Konta Trenera" : "Coach Account & Language Settings"}
                </h2>
              </div>
              <p className="text-slate-400 text-sm md:text-base mt-1.5 m-0 font-medium">
                {lang === 'pl' ? "Zarządzaj domyślnym językiem interfejsu oraz synchronizacją i importem planów z pliku PDF." : "Configure localized interface language preference and import training routines from PDF."}
              </p>
            </div>

            <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 md:p-8 shadow-xl space-y-8">
              {/* Interactive PDF Plan Importer */}
              <PdfPlanImporter trainerId={trainerId} existingExercises={exercises || []} onImportSuccess={() => { /* real-time firestore onSnapshot handles UI refreshes */ }} />

                            {/* SECTION: COACH 100% FREE LICENSE */}
              <div className="bg-slate-950/80 border border-slate-800 p-5 md:p-6 rounded-2xl space-y-4 shadow-inner">
                <div className="flex items-center justify-between border-b border-slate-800/80 pb-3 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-emerald-400 text-xl">verified</span>
                    <strong className="text-sm font-black text-white uppercase tracking-wide">
                      {lang === 'pl' ? 'Status Konta Trenerskiego' : 'Coach Account Status'}
                    </strong>
                  </div>
                  <span className="px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider border bg-emerald-500/20 text-emerald-300 border-emerald-500/40 shadow-sm">
                    {lang === 'pl' ? '🎉 Licencja 100% Za Darmo' : '🎉 100% Free Coach License'}
                  </span>
                </div>

                <div className="bg-slate-900/90 border border-slate-800 p-4 rounded-xl space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-emerald-400 text-lg">all_inclusive</span>
                    <h4 className="text-base font-black text-white m-0">
                      {lang === 'pl' ? 'Nielimitowany Dostęp Trenerski (0 PLN)' : 'Unlimited Coach Access ($0 / 0 PLN)'}
                    </h4>
                  </div>
                  <p className="text-xs text-slate-300 font-medium m-0 leading-relaxed">
                    {lang === 'pl'
                      ? 'Twój profil trenera w GymGhost jest w 100% darmowy bez żadnych opłat, subskrypcji i limitów podopiecznych. Zarządzaj swoimi zawodnikami, twórz programy oraz analizuj wyniki całkowicie bezpłatnie.'
                      : 'Your GymGhost Coach account is 100% free forever with zero subscriptions or fees. Manage unlimited trainees, create programs, and analyze performance with zero cost.'}
                  </p>
                </div>
              </div>

              {/* SECTION 1: COACH AVATAR / PROFILE PHOTO */}
              <div className="bg-slate-950/80 border border-slate-800 p-5 md:p-6 rounded-2xl space-y-4 shadow-inner">
                <div className="flex items-center gap-2 border-b border-slate-800/80 pb-3">
                  <span className="material-symbols-outlined text-indigo-400 text-xl">account_circle</span>
                  <strong className="text-sm font-black text-white uppercase tracking-wide">
                    {lang === 'pl' ? 'Zdjęcie Profilowe Trenera / Avatar' : 'Coach Profile Photo & Avatar'}
                  </strong>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-5">
                  <div 
                    style={{ borderColor: selectedBrandColor, boxShadow: `0 0 16px ${selectedBrandColor}40` }}
                    className="w-20 h-20 rounded-full border-2 bg-slate-900 overflow-hidden shrink-0 shadow-lg flex items-center justify-center"
                  >
                    {userProfile?.avatarUrl || userProfile?.coachAvatarUrl ? (
                      <img src={userProfile?.avatarUrl || userProfile?.coachAvatarUrl} alt="Coach Avatar Preview" className="w-full h-full object-cover" />
                    ) : (
                      <div 
                        style={{ backgroundColor: selectedBrandColor }}
                        className="w-full h-full flex items-center justify-center text-xl font-black text-slate-950 uppercase tracking-wider select-none"
                      >
                        {(() => {
                          const nameStr = trainerDisplayName || userProfile?.displayName || userProfile?.name || trainerEmail || "";
                          const parts = nameStr.trim().split(/\s+/);
                          if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
                          if (parts[0] && parts[0].length >= 2) return parts[0].substring(0, 2).toUpperCase();
                          return "TR";
                        })()}
                      </div>
                    )}
                  </div>

                  <div className="space-y-3 flex-1 w-full text-left">
                    <p className="text-xs text-slate-400 m-0 font-medium">
                      {lang === 'pl' 
                        ? "Wgraj własne zdjęcie profilowe trenera (avatar) z urządzenia lub wklej bezpośredni link URL:" 
                        : "Upload custom coach profile photo from your device or paste a direct image URL link:"}
                    </p>

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
                      <label className="bg-indigo-600 hover:bg-indigo-500 text-white font-extrabold text-xs px-4 py-2.5 rounded-xl transition cursor-pointer flex items-center justify-center gap-1.5 shadow-md active:scale-95 shrink-0">
                        <span className="material-symbols-outlined text-sm">upload</span>
                        <span>{lang === 'pl' ? 'Wgraj Avatara z Urządzenia' : 'Upload Avatar'}</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleCoachAvatarFileUpload}
                          className="hidden"
                        />
                      </label>

                      {(userProfile?.avatarUrl || userProfile?.coachAvatarUrl) && (
                        <button
                          type="button"
                          onClick={() => saveCoachAvatar(null)}
                          className="bg-slate-900 hover:bg-rose-950/40 text-slate-400 hover:text-rose-400 border border-slate-800 hover:border-rose-500/40 px-3 py-2.5 rounded-xl text-xs font-bold transition cursor-pointer flex items-center justify-center gap-1 active:scale-95"
                        >
                          <span className="material-symbols-outlined text-sm text-rose-400">delete</span>
                          <span>{lang === 'pl' ? 'Użyj Inicjałów' : 'Reset to Initials'}</span>
                        </button>
                      )}
                    </div>

                    <div className="flex items-center gap-2 pt-1">
                      <input
                        type="text"
                        value={coachAvatarUrlInput}
                        onChange={(e) => setCoachAvatarUrlInput(e.target.value)}
                        placeholder={lang === 'pl' ? "LUB wklej link URL do zdjęcia profilowego..." : "OR paste profile photo URL link..."}
                        className="flex-1 bg-slate-900 border border-slate-800 focus:border-indigo-500/60 text-slate-100 font-bold text-xs rounded-xl px-3.5 py-2.5 focus:outline-none shadow-inner"
                      />
                      <button
                        type="button"
                        disabled={!coachAvatarUrlInput.trim()}
                        onClick={async () => {
                          if (coachAvatarUrlInput.trim()) {
                            await saveCoachAvatar(coachAvatarUrlInput.trim());
                            setCoachAvatarUrlInput('');
                          }
                        }}
                        className="bg-slate-800 hover:bg-indigo-600 disabled:opacity-40 text-slate-200 hover:text-white font-extrabold text-xs px-4 py-2.5 rounded-xl transition cursor-pointer shrink-0"
                      >
                        {lang === 'pl' ? 'Zapisz URL' : 'Save URL'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* SECTION 2: CUSTOM BRANDING LOGO */}
              <div className="bg-slate-950/80 border border-slate-800 p-5 md:p-6 rounded-2xl space-y-4 shadow-inner">
                <div className="flex items-center gap-2 border-b border-slate-800/80 pb-3">
                  <span className="material-symbols-outlined text-indigo-400 text-xl">image</span>
                  <strong className="text-sm font-black text-white uppercase tracking-wide">
                    {lang === 'pl' ? 'Własne Logo Marki / Klubu' : 'Custom Brand & Club Logo'}
                  </strong>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-5">
                  <div className="h-20 w-36 rounded-2xl border border-slate-800 bg-slate-900 p-2 overflow-hidden shrink-0 shadow-lg flex items-center justify-center">
                    {userProfile?.brandLogo || branding?.logo ? (
                      (userProfile?.brandLogo || branding?.logo).startsWith('http') || (userProfile?.brandLogo || branding?.logo).startsWith('data:') || (userProfile?.brandLogo || branding?.logo).startsWith('/') ? (
                        <img src={userProfile?.brandLogo || branding?.logo} alt="Brand Logo Preview" className="max-h-full max-w-full object-contain" />
                      ) : (
                        <span className="text-sm font-black text-white">{userProfile?.brandLogo || branding?.logo}</span>
                      )
                    ) : (
                      <span className="text-xs font-bold text-slate-500">Brak logo</span>
                    )}
                  </div>

                  <div className="space-y-3 flex-1 w-full text-left">
                    <p className="text-xs text-slate-400 m-0 font-medium">
                      {lang === 'pl' 
                        ? "Wgraj oficjalne logo swojego klubu lub marki osobistej z urządzenia (PNG, SVG, JPG):" 
                        : "Upload your official team or personal coaching brand logo (PNG, SVG, JPG):"}
                    </p>

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
                      <label className="bg-indigo-600 hover:bg-indigo-500 text-white font-extrabold text-xs px-4 py-2.5 rounded-xl transition cursor-pointer flex items-center justify-center gap-1.5 shadow-md active:scale-95 shrink-0">
                        <span className="material-symbols-outlined text-sm">upload_file</span>
                        <span>{lang === 'pl' ? 'Wgraj Logo z Urządzenia' : 'Upload Logo'}</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleCoachLogoFileUpload}
                          className="hidden"
                        />
                      </label>

                      {(userProfile?.brandLogo || branding?.logo) && (
                        <button
                          type="button"
                          onClick={() => saveCoachLogo(null)}
                          className="bg-slate-900 hover:bg-rose-950/40 text-slate-400 hover:text-rose-400 border border-slate-800 hover:border-rose-500/40 px-3 py-2.5 rounded-xl text-xs font-bold transition cursor-pointer flex items-center justify-center gap-1 active:scale-95"
                        >
                          <span className="material-symbols-outlined text-sm text-rose-400">delete</span>
                          <span>{lang === 'pl' ? 'Przywróć Domyślne Logo' : 'Reset Logo'}</span>
                        </button>
                      )}
                    </div>

                    <div className="flex items-center gap-2 pt-1">
                      <input
                        type="text"
                        value={coachLogoUrlInput}
                        onChange={(e) => setCoachLogoUrlInput(e.target.value)}
                        placeholder={lang === 'pl' ? "LUB wklej link URL do obrazu logo..." : "OR paste logo image URL link..."}
                        className="flex-1 bg-slate-900 border border-slate-800 focus:border-indigo-500/60 text-slate-100 font-bold text-xs rounded-xl px-3.5 py-2.5 focus:outline-none shadow-inner"
                      />
                      <button
                        type="button"
                        disabled={!coachLogoUrlInput.trim()}
                        onClick={async () => {
                          if (coachLogoUrlInput.trim()) {
                            await saveCoachLogo(coachLogoUrlInput.trim());
                            setCoachLogoUrlInput('');
                          }
                        }}
                        className="bg-slate-800 hover:bg-indigo-600 disabled:opacity-40 text-slate-200 hover:text-white font-extrabold text-xs px-4 py-2.5 rounded-xl transition cursor-pointer shrink-0"
                      >
                        {lang === 'pl' ? 'Zapisz Logo URL' : 'Save Logo URL'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* SECTION 3: PRIMARY BRAND ACCENT COLOR */}
              <div className="bg-slate-950/80 border border-slate-800 p-5 md:p-6 rounded-2xl space-y-4 shadow-inner">
                <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-indigo-400 text-xl">palette</span>
                    <strong className="text-sm font-black text-white uppercase tracking-wide">
                      {lang === 'pl' ? 'Główny Kolor Marki Trenera' : 'Primary Brand Accent Color'}
                    </strong>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <div 
                      style={{ backgroundColor: selectedBrandColor }}
                      className="w-5 h-5 rounded-full border border-white/20 shadow-md"
                    />
                    <span className="font-mono font-black text-xs text-white">{selectedBrandColor}</span>
                  </div>
                </div>

                <p className="text-xs text-slate-400 m-0 font-medium">
                  {lang === 'pl' 
                    ? "Wybierz główny kolor akcentujący swój profil trenera oraz nagłówki w panelu zawodników:" 
                    : "Select your primary accent color applied across your coach portal and athlete dashboard UI:"}
                </p>

                {/* Preset Color Swatches */}
                <div className="flex flex-wrap items-center gap-2.5">
                  {[
                    { color: '#6366f1', label: 'Indigo' },
                    { color: '#eab308', label: 'Gold' },
                    { color: '#10b981', label: 'Emerald' },
                    { color: '#06b6d4', label: 'Cyan' },
                    { color: '#ec4899', label: 'Rose' },
                    { color: '#8b5cf6', label: 'Violet' },
                    { color: '#f97316', label: 'Orange' },
                    { color: '#ef4444', label: 'Red' }
                  ].map(swatch => (
                    <button
                      key={swatch.color}
                      type="button"
                      onClick={() => {
                        setSelectedBrandColor(swatch.color);
                        saveCoachBrandColor(swatch.color);
                      }}
                      style={{ backgroundColor: swatch.color }}
                      className={`h-9 px-3 rounded-xl text-xs font-black text-slate-950 transition cursor-pointer flex items-center gap-1.5 shadow-md active:scale-95 ${
                        selectedBrandColor.toLowerCase() === swatch.color.toLowerCase() ? 'ring-2 ring-white ring-offset-2 ring-offset-slate-950 scale-105' : 'opacity-85 hover:opacity-100'
                      }`}
                    >
                      {selectedBrandColor.toLowerCase() === swatch.color.toLowerCase() && (
                        <span className="material-symbols-outlined text-sm font-black">check</span>
                      )}
                      <span>{swatch.label}</span>
                    </button>
                  ))}
                </div>

                {/* Custom Color Input Picker */}
                <div className="flex items-center gap-3 pt-2">
                  <input
                    type="color"
                    value={selectedBrandColor}
                    onChange={(e) => setSelectedBrandColor(e.target.value)}
                    className="w-10 h-10 rounded-xl bg-transparent border-0 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={selectedBrandColor}
                    onChange={(e) => setSelectedBrandColor(e.target.value)}
                    placeholder="#HEX..."
                    className="w-32 bg-slate-900 border border-slate-800 text-slate-100 font-mono font-black text-xs rounded-xl px-3.5 py-2.5 outline-none focus:border-indigo-500 uppercase"
                  />
                  <button
                    type="button"
                    onClick={() => saveCoachBrandColor(selectedBrandColor)}
                    className="bg-indigo-600 hover:bg-indigo-500 text-white font-extrabold text-xs px-4 py-2.5 rounded-xl transition cursor-pointer shadow-md active:scale-95"
                  >
                    {lang === 'pl' ? 'Zastosuj Kolor' : 'Apply Color'}
                  </button>
                </div>
              </div>

              {/* SECTION 4: PREFERRED INTERFACE LANGUAGE */}
              <div className="bg-slate-950/80 border border-slate-800 p-5 rounded-2xl space-y-3 shadow-inner">
                <label className="block text-xs font-black text-slate-300 tracking-wider uppercase">
                  {lang === 'pl' ? "Preferowany Język Interfejsu (ENG / PL)" : "Preferred Portal Interface Language"}
                </label>
                <p className="text-xs text-slate-500 m-0 font-medium">
                  {lang === 'pl' ? "Wybierz język, w którym chcesz zarządzać planami, katalogiem i analityką:" : "Select your default operating language for routine builders, catalogs, and client rosters:"}
                </p>
                <div className="flex gap-3 pt-1">
                  <button 
                    onClick={() => setLang('en')} 
                    className={`px-6 py-3 text-xs sm:text-sm font-black rounded-xl transition cursor-pointer flex items-center gap-2.5 shadow-lg active:scale-95 ${
                      lang === 'en' ? 'bg-indigo-600 text-white shadow-indigo-500/30' : 'bg-slate-900 text-slate-400 hover:text-white border border-slate-800'
                    }`}
                  >
                    <span>🇺🇸</span> English (ENG)
                  </button>
                  <button 
                    onClick={() => setLang('pl')} 
                    className={`px-6 py-3 text-xs sm:text-sm font-black rounded-xl transition cursor-pointer flex items-center gap-2.5 shadow-lg active:scale-95 ${
                      lang === 'pl' ? 'bg-indigo-600 text-white shadow-indigo-500/30' : 'bg-slate-900 text-slate-400 hover:text-white border border-slate-800'
                    }`}
                  >
                    <span>🇵🇱</span> Polski (PL)
                  </button>
                </div>
              </div>

              {/* SECTION 5: SECURITY & ACCOUNT PASSWORD */}
              <div className="bg-slate-950/80 border border-slate-800 p-5 md:p-6 rounded-2xl space-y-4 shadow-inner">
                <div className="flex items-center gap-2 border-b border-slate-800 pb-3">
                  <span className="material-symbols-outlined text-emerald-400 text-xl">shield_lock</span>
                  <strong className="text-sm font-black text-white uppercase tracking-wide">
                    {lang === 'pl' ? 'Bezpieczeństwo i Zmiana Hasła' : 'Security & Password Change'}
                  </strong>
                </div>

                {/* Direct In-App Password Update Form */}
                <form onSubmit={handleCoachDirectPasswordChange} className="bg-slate-900/90 border border-slate-800 p-4 sm:p-5 rounded-xl space-y-4">
                  <div className="flex items-center justify-between border-b border-slate-800/60 pb-2.5">
                    <span className="text-xs font-black text-white flex items-center gap-1.5">
                      <span className="material-symbols-outlined text-base text-emerald-400">key</span>
                      <span>{lang === 'pl' ? "Ustaw Nowe Hasło" : "Set New Password"}</span>
                    </span>
                    <span className="text-[10px] text-slate-400 font-medium">
                      {lang === 'pl' ? "Wprowadź aktualne i nowe hasło (min. 6 znaków)" : "Enter current and new password (min. 6 chars)"}
                    </span>
                  </div>

                  <div className="space-y-3.5">
                    {/* 1. Current / Old Password */}
                    <div>
                      <label className="block text-[11px] font-bold text-slate-300 mb-1">
                        {lang === 'pl' ? "Stare Hasło (Dotychczasowe):" : "Current Password:"}
                      </label>
                      <div className="relative">
                        <input
                          type={showCoachPasswords ? "text" : "password"}
                          value={coachOldPasswordInput}
                          onChange={(e) => setCoachOldPasswordInput(e.target.value)}
                          placeholder={lang === 'pl' ? "Wpisz dotychczasowe hasło..." : "Type current password..."}
                          className="w-full bg-slate-950 border border-slate-700 focus:border-emerald-500 text-white font-mono text-xs rounded-xl px-3.5 py-2.5 pr-12 focus:outline-none shadow-inner"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCoachPasswords(!showCoachPasswords)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-emerald-400 text-xs transition cursor-pointer flex items-center gap-1 bg-slate-850 hover:bg-slate-800 px-2 py-1 rounded-lg border border-slate-700/60 shadow-sm"
                          title={showCoachPasswords ? "Hide Password" : "Show Password"}
                        >
                          <span className="material-symbols-outlined text-sm">
                            {showCoachPasswords ? 'visibility_off' : 'visibility'}
                          </span>
                        </button>
                      </div>
                    </div>

                    {/* 2. New Password */}
                    <div>
                      <label className="block text-[11px] font-bold text-slate-300 mb-1">
                        {lang === 'pl' ? "Nowe Hasło:" : "New Password:"}
                      </label>
                      <div className="relative">
                        <input
                          type={showCoachPasswords ? "text" : "password"}
                          value={coachNewPasswordInput}
                          onChange={(e) => setCoachNewPasswordInput(e.target.value)}
                          placeholder={lang === 'pl' ? "Wpisz nowe hasło (min. 6 znaków)..." : "Type new password..."}
                          className="w-full bg-slate-950 border border-slate-700 focus:border-emerald-500 text-white font-mono text-xs rounded-xl px-3.5 py-2.5 pr-12 focus:outline-none shadow-inner"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCoachPasswords(!showCoachPasswords)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-emerald-400 text-xs transition cursor-pointer flex items-center gap-1 bg-slate-850 hover:bg-slate-800 px-2 py-1 rounded-lg border border-slate-700/60 shadow-sm"
                          title={showCoachPasswords ? "Hide Password" : "Show Password"}
                        >
                          <span className="material-symbols-outlined text-sm">
                            {showCoachPasswords ? 'visibility_off' : 'visibility'}
                          </span>
                        </button>
                      </div>
                    </div>

                    {/* 3. Confirm New Password */}
                    <div>
                      <label className="block text-[11px] font-bold text-slate-300 mb-1">
                        {lang === 'pl' ? "Powtórz Nowe Hasło:" : "Confirm New Password:"}
                      </label>
                      <div className="relative">
                        <input
                          type={showCoachPasswords ? "text" : "password"}
                          value={coachConfirmPasswordInput}
                          onChange={(e) => setCoachConfirmPasswordInput(e.target.value)}
                          placeholder={lang === 'pl' ? "Powtórz nowe hasło..." : "Re-type new password..."}
                          className="w-full bg-slate-950 border border-slate-700 focus:border-emerald-500 text-white font-mono text-xs rounded-xl px-3.5 py-2.5 pr-12 focus:outline-none shadow-inner"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCoachPasswords(!showCoachPasswords)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-emerald-400 text-xs transition cursor-pointer flex items-center gap-1 bg-slate-850 hover:bg-slate-800 px-2 py-1 rounded-lg border border-slate-700/60 shadow-sm"
                          title={showCoachPasswords ? "Hide Password" : "Show Password"}
                        >
                          <span className="material-symbols-outlined text-sm">
                            {showCoachPasswords ? 'visibility_off' : 'visibility'}
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isUpdatingCoachPassword || !coachNewPasswordInput.trim() || !coachConfirmPasswordInput.trim()}
                    className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-slate-950 font-black text-xs py-3 rounded-xl transition cursor-pointer flex items-center justify-center gap-1.5 shadow-md active:scale-95 mt-1"
                  >
                    <span className="material-symbols-outlined text-sm">lock_reset</span>
                    <span>{isUpdatingCoachPassword ? (lang === 'pl' ? 'Zapisywanie...' : 'Saving...') : (lang === 'pl' ? 'Zapisz Nowe Hasło' : 'Save New Password')}</span>
                  </button>
                </form>

                {/* Send Password Reset Link */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-2 border-t border-slate-900">
                  <div>
                    <h4 className="text-xs font-black text-white m-0">
                      {lang === 'pl' ? "Wysyłka Linku Resetu na E-mail" : "Send Password Reset Email"}
                    </h4>
                    <p className="text-[11px] text-slate-400 m-0 mt-0.5 font-medium">
                      {lang === 'pl' ? "Wyślij bezpłatny link z przyciskiem zmiany hasła z contact@gymghost.net." : "Send a password reset email link from contact@gymghost.net."}
                    </p>
                  </div>

                  <button
                    type="button"
                    disabled={isSendingCoachReset}
                    onClick={handleCoachSendPasswordReset}
                    className="bg-slate-850 hover:bg-slate-800 text-slate-200 hover:text-white border border-slate-750 font-extrabold text-xs px-4 py-2.5 rounded-xl transition cursor-pointer flex items-center justify-center gap-1.5 shrink-0"
                  >
                    <span className="material-symbols-outlined text-sm text-indigo-400">mail</span>
                    <span>{isSendingCoachReset ? (lang === 'pl' ? 'Wysyłanie...' : 'Sending...') : (lang === 'pl' ? 'Wyślij Link Resetu' : 'Send Reset Link')}</span>
                  </button>
                </div>

                {coachPasswordNotice && (
                  <div className={`p-3 rounded-xl text-xs font-bold flex items-center gap-2 border ${
                    coachPasswordNotice.type === 'success' ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' : 'bg-rose-500/15 text-rose-300 border-rose-500/30'
                  }`}>
                    <span className="material-symbols-outlined text-sm">
                      {coachPasswordNotice.type === 'success' ? 'check_circle' : 'error'}
                    </span>
                    <span>{coachPasswordNotice.text}</span>
                  </div>
                )}
              </div>

              <div className="bg-slate-950/80 border border-indigo-500/30 p-5 rounded-2xl space-y-2">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
                  <strong className="text-sm font-black text-white uppercase tracking-wide">{lang === 'pl' ? 'Synchronizacja z Bazą Danych Aktywna' : 'Real-Time Tenant Synchronization Active'}</strong>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed m-0 font-medium">
                  {lang === 'pl' ? 'Twoje szablony treningowe i cykle wielodniowe są w trybie stałego nadzoru. Dedykowani zawodnicy widzą każdą zmianę objętości siłowej bez konieczności odświeżania aplikacji.' : 'All multi-day cycles and individual workout routines created under your trainer profile are continuously indexed in real time across active athlete mobile client interfaces.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Change Password Dialog Modal Overlay */}
        {passwordModalUser && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
            <div className="bg-slate-900 border border-slate-750 rounded-3xl p-6 md:p-8 max-w-md w-full shadow-[0_10px_40px_rgba(0,0,0,0.8)] space-y-5">
              <div className="flex items-center justify-between border-b border-slate-800 pb-3.5">
                <div className="flex items-center gap-2.5">
                  <span className="material-symbols-outlined text-2xl text-indigo-400">key</span>
                  <h3 className="text-lg md:text-xl font-black text-white m-0">
                    {lang === 'pl' ? "Zmień Hasło Podopiecznego" : "Change Trainee Password"}
                  </h3>
                </div>
                <button 
                  onClick={() => setPasswordModalUser(null)}
                  className="text-slate-400 hover:text-white transition bg-transparent border-0 cursor-pointer p-1 flex items-center justify-center"
                >
                  <span className="material-symbols-outlined text-lg">close</span>
                </button>
              </div>

              <div className="space-y-2 text-xs text-slate-300">
                <p className="m-0 font-medium">
                  {lang === 'pl' 
                    ? `Wprowadź nowe hasło logowania dla podopiecznego: ` 
                    : `Enter a new login password for athlete: `}
                  <strong className="text-white font-extrabold block text-sm mt-1">{passwordModalUser.displayName || passwordModalUser.name} ({passwordModalUser.email})</strong>
                </p>
              </div>

              <div className="space-y-3">
                <input
                  type="password"
                  value={newPasswordInput}
                  onChange={(e) => setNewPasswordInput(e.target.value)}
                  placeholder={lang === 'pl' ? "Nowe hasło..." : "New password..."}
                  className="w-full bg-slate-950 border border-slate-700 text-white font-extrabold text-sm rounded-xl px-4 py-3 outline-none focus:border-indigo-400 shadow-inner"
                />

                {/* Real-time Password Strength UI Indicator */}
                <div className="mt-1.5 px-1 space-y-1">
                  <p className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider">
                    Wymagania hasła (Password Requirements):
                  </p>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded ${newPasswordInput.length >= 8 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-slate-950 text-slate-600'}`}>
                      Min. 8 Chars
                    </span>
                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded ${/[A-Z]/.test(newPasswordInput) ? 'bg-emerald-500/10 text-emerald-400' : 'bg-slate-950 text-slate-600'}`}>
                      A-Z
                    </span>
                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded ${/[a-z]/.test(newPasswordInput) ? 'bg-emerald-500/10 text-emerald-400' : 'bg-slate-950 text-slate-600'}`}>
                      a-z
                    </span>
                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded ${/[0-9]/.test(newPasswordInput) ? 'bg-emerald-500/10 text-emerald-400' : 'bg-slate-950 text-slate-600'}`}>
                      0-9
                    </span>
                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded ${/[@$!%*?&._-]/.test(newPasswordInput) ? 'bg-emerald-500/10 text-emerald-400' : 'bg-slate-950 text-slate-600'}`}>
                      Special Char
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2 border-t border-slate-800/80">
                <button
                  type="button"
                  onClick={() => setPasswordModalUser(null)}
                  className="bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold px-5 py-2.5 rounded-xl text-xs transition cursor-pointer"
                >
                  {t('common.cancel', 'Anuluj')}
                </button>
                <button
                  type="button"
                  onClick={handleExecuteChangePassword}
                  className="bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-400 hover:to-indigo-500 text-white font-black px-6 py-2.5 rounded-xl text-xs transition shadow-lg shadow-indigo-500/20 cursor-pointer active:scale-95 flex items-center gap-1.5"
                >
                  <span className="material-symbols-outlined text-sm">save</span>
                  <span>{t('common.save', 'Zapisz')}</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* MEDIA LIGHTBOX MODAL AT ROOT LEVEL */}
      <MediaLightboxModal 
        mediaData={enlargedImage} 
        onClose={() => setEnlargedImage(null)} 
        lang={lang} 
      />
    

</div>
  );
}
